/**
 * DOM renderer for the ops protocol (docs/ops-protocol.md).
 *
 * The renderer owns a `Map<ElementId, HTMLElement>` and a mirror of the
 * element tree as `LayoutNode`s; id 0 is the root container supplied by the
 * host page. Every op is applied to the DOM immediately; at `commit` the
 * layout engine (src/layout) computes a frame for every element and
 * `layoutDom.ts` positions the elements absolutely from it (a `sb:precommit`
 * CustomEvent is dispatched on the root once the pass's ops are applied and
 * before that layout, and a `sb:commit` after it, so the browser history,
 * tests and tooling can observe pass boundaries). Animated passes are handed
 * to the `Animator`.
 *
 * Most kinds are a single DOM element whose children are the element's
 * children. Compound kinds (`navstack`, `navscreen`, `section`, `toggle`,
 * `navlink`, `tabview`) own some chrome of their own (a navigation bar, a
 * switch, a chevron, a tab bar...) and expose a *slot* element that receives
 * the protocol children, so `insert` indices stay exact.
 *
 * Phase 4: after every layout pass the renderer compares each `geometry`
 * element's frame size with the size it last reported and sends one
 * `geometry` event per changed element (batched after the ops being applied),
 * so Swift's re-render converges as soon as the size is stable.
 *
 * Phase 6 (docs/phase6/chrome.md): `menu` opens the same popover a `menu`
 * picker does, built from its item children when it opens; `toolbar` and
 * `searchfield` are navscreen children the engine places in the bar;
 * `tapgesture` sends a `tap` for a click anywhere on it that no inner
 * control claimed.
 *
 * Phase 7: `navsplit` holds two `navstack` columns with a hairline between
 * them (drawn by the sidebar column in the engine's 1px `separator` gap);
 * a `navlink` with `selected: true` carries `data-sb-selected` (a rounded fill
 * background; the engine drops its chevron inside a split view's sidebar);
 * the candidates a `viewthatfits` did not choose get the `hidden` attribute.
 */

import {
  Animator,
  barToolbarOf,
  isBarToolbar,
  EXIT_CLONE_CLASS,
  emptyPass,
  setTransition,
  transitionOf,
  type PassRecord,
  type UpdatedRecord,
 type StyleSnapshot } from './animation';
import {
  CanvasTextMeasurer,
  LayoutPass,
  defaultFont,
  resolveFont,
  textHeight,
  wrapText,
  type LayoutEnvironment,
  type LayoutNode,
  type LayoutResult,
  type Rect,
} from './layout';
import { LayoutReporter } from './layoutEvents';
import { createGaugeRing, isCircularGauge, updateGaugeRing } from './gaugeRing';
import { bitmapStore, showBitmap } from './bitmaps';
import { clearRichText, runTemplates } from './richText';
import { markDeleteBackward, noteReportedText, textArrived, wantsDeleteBackward } from './editorSelection';
import { applyPlainEditorSelection, focusPlainEditor, isPlainEditor, listenToPlainEditors } from './plainEditor';
import { applyRichEditor, createRichEditor, focusRichEditor, isRichEditor, listenToRichEditors, richEditorText } from './richEditor';
import { applyLayout, setTabHidden, type BoxChange } from './layoutDom';
import { BarMorph } from './barMorph';
import { ghostClone } from './ghost';
import { ExternalDrops } from './dragDrop';
import { InteractionController, type InteractionHost } from './interaction';
import { TapCounter } from './gestureComposition';
import { PressEffects } from './press';
import { symbolOf, textOf, unwrapStyledNode } from './nodeQueries';
import {
  INTERACTION_KINDS,
  ROOT_ID,
  isAssetImage,
  isBitmapImage,
  isUrlImage,
  type Animation,
  type ButtonProps,
  type ChartItemProps,
  type ChartProps,
  type ColorProps,
  type ColorScheme,
  type DecoratedProps,
  type DynamicTypeSize,
  type ElementId,
  type ElementKind,
  type ElementProps,
  type ElementPropsByKind,
  type FontWeight,
  type GaugeProps,
  type GeometryEvent,
  type ScrollGeometryEvent,
  type GestureProps,
  type GridProps,
  type GridRowProps,
  type HStackProps,
  type LayoutEvent,
  type LazyVGridProps,
  type LazyHGridProps,
  type ImageProps,
  type InspectorProps,
  type ListProps,
  type ListRowProps,
  type DropDestinationProps,
  type NavLinkProps,
  type TapGestureProps,
  type NavScreenProps,
  type Op,
  type PickerProps,
  type RendererEvent,
  type ScrollViewProps,
  type SearchFieldProps,
  type SectionProps,
  type DisclosureGroupProps,
  type ShapeProps,
  type SheetProps,
  type SpacerProps,
  type Style,
  type StyledProps,
  type SwipeActionsProps,
  type TabProps,
  type TabViewProps,
  type HostProps,
  type RegisteredProps,
  type TextFieldProps,
  type TextProps,
  type TextRun,
  type ShapeStyle,
  type ToggleProps,
  type ToolbarProps,
  type ViewThatFitsProps,
  type VStackProps,
  type ZStackProps,
  // Phase 8: alerts, controls, effects
  type AlertProps,
  type DatePickerProps,
  type SliderProps,
  type StepperProps,
  type TextEditorProps,
  type RootProps,
  type ScrollToCommand,
  type VideoProps,
  type CanvasProps,
  type Color,
  type LazySpacerProps,
  type Platform,
} from './protocol';
import {
  combineDateSeconds,
  dateInputValue,
  formatPickerDate,
  formatPickerTime,
  sameDay,
  sliderFraction,
  snapSliderValue,
  timeInputValue,
} from './layout';
import { filledSymbolName, renderSymbol } from './symbols';
import { applyPathShape, hasPathFrames, layOutAffinePath, playPathFrames } from './pathShape';
import { VideoEvents, applyVideo, stopVideos } from './video';
import { loadableURL } from './hostRequests';
import { PinchController } from './pinch';
import { TouchesController } from './touches';
import { applyCanvas, createCanvasElement } from './canvasDrawing';
import { scrollToTarget } from './scrollTo';
import { POSITION_SETTLE_MS, POSITION_SNAP_WAITS, anchorPoint, positionTarget, restsOnSnapPoint, type PositionCandidate } from './scrollPosition';
import { TouchPaging } from './touchPaging';
import { isScrollVisible } from './scrollVisibility';
import { applyDefaultScrollAnchor, keepScrollAnchors } from './scrollAnchor';
import { EMPTY_CATALOG, assetColorVar, type AssetCatalog } from './assets';
import { HIERARCHY_OPACITY, colorToCSS, effectsCSS, shadowCSS, styleToCSS, type PaintCSS } from './styles';
import { hostContent, onHostContentChange } from './hostContent';
import { type RegisteredViewInstance, ViewRegistry } from './viewRegistry';
import { HoverTracker, applyPointerAndEffects, safeAreaOverlap } from './pointerEffects';
import { colorMultiplyFilter } from './colorMultiply';
import { largeTitleOf, largeTitleTextOf } from './largeTitle';
import { applyEdgeEffect } from './scrollEdge';
import { discardScrollOffsets, readScrollOffsets, trackScroller } from './scrollSync';
import { drawScrollTransitions, setScrollTransition } from './scrollTransition';
import { ScrollGeometryReporter, readScrollGeometry } from './scrollGeometry';
import { drawPinnedViews, setPinnedViews } from './pinnedViews';
import { DEFAULT_TYPE_SIZE, TEXT_STYLE_WEIGHT, resolveTextStyle, textStyleVar } from './typography';

export type EventCallback = (event: RendererEvent) => void;

export class RendererError extends Error {
  constructor(message: string, readonly op?: Op) {
    super(op ? `${message} (op: ${JSON.stringify(op)})` : message);
    this.name = 'RendererError';
  }
}

const KIND_ATTR = 'sbKind';
/**
 * The kinds that draw what they show themselves: a shadow of a box around one falls on it as a whole (castShadows,
 * styles.css), where a container hands it on to the views inside.
 */
const DRAWN_KINDS: ReadonlySet<string> = new Set([
  'text', 'image', 'divider', 'color', 'shape', 'textfield', 'toggle', 'picker', 'gauge', 'searchfield', 'registered', 'chart',
  'stepper', 'slider', 'datepicker', 'texteditor', 'video', 'canvas',
]);
const ID_ATTR = 'sbId';
/** `animationToken` of a `styled` box, kept across `update` to detect changes. */
const ANIMATION_TOKEN = 'sbAnimationToken';

/**
 * Kinds whose own changes animate even outside `withAnimation`: their token
 * changes with what they present (an inspector's `presented`).
 */
const IMPLICITLY_ANIMATED: ReadonlySet<ElementKind> = new Set(['inspector']);

/**
 * The animation token an element of `kind` has with `props`: a `styled`
 * box's `animationToken` (`.animation(_:value:)`), an inspector's
 * `presented`. An `update` that changes it animates its subtree.
 */
function animationTokenOf(kind: ElementKind | undefined, props: unknown): string | undefined {
  if (kind === 'inspector') return (props as InspectorProps | null)?.presented === true ? 'shown' : 'hidden';
  const token = (props as { style?: { animationToken?: number | null } } | null)?.style?.animationToken;
  return token == null ? undefined : String(token);
}

/** Duration of the menu's fade out; keep in sync with styles.css (`sb-menu-out`). */
export const MENU_EXIT_MS = 150;

/**
 * Put a closed popover away: it stops being the menu (no `sb-picker-menu`
 * class or `menu` role, inert) and fades out where it was, then leaves the
 * DOM, as the open animation brought it in.
 */
function dismissMenu(menu: HTMLElement): void {
  menu.classList.replace('sb-picker-menu', 'sb-picker-menu-exit');
  menu.removeAttribute('role');
  menu.setAttribute('aria-hidden', 'true');
  menu.inert = true;
  const remove = (): void => menu.remove();
  menu.addEventListener('animationend', remove, { once: true });
  // Without animations (reduced motion, a hidden page, jsdom) it still goes.
  setTimeout(remove, MENU_EXIT_MS + 100);
}

/** Duration of the navigation push/pop slide; keep in sync with styles.css (`--sb-nav-easing`). */
export const NAV_TRANSITION_MS = 400;
/**
 * How long `keepRepainting` paints after a browser history navigation:
 * through Safari's 3 s snapshot-removal watchdog, with room for a slow
 * device. Measured on an iPhone: painting for 1.5 s (past the swipe
 * animation alone) removed the stall but left a single freeze of the touch
 * in progress at the 3 s mark; 5 s left none.
 */
export const REPAINT_KEEPALIVE_MS = 5000;
/** Between two repaints of the keep-alive: a few frames, so the cost is nothing and the first frame after Safari hides the page is near. */
const REPAINT_KEEPALIVE_INTERVAL_MS = 50;
/** Duration of the sheet present/dismiss slide; keep in sync with styles.css. */
export const SHEET_TRANSITION_MS = 400;
// Phase 8: the sheet drag (and every other pointer gesture) lives in src/interaction.ts.
export { SHEET_DISMISS_DRAG } from './interaction';

const ALL_KINDS: ReadonlySet<string> = new Set<ElementKind>([
  'host',
  'registered',
  'text',
  'vstack',
  'hstack',
  'spacer',
  'button',
  'styled',
  'zstack',
  'image',
  'divider',
  'scrollview',
  'list',
  'section',
  'textfield',
  'toggle',
  'navstack',
  'navscreen',
  'navlink',
  'color',
  'shape',
  'sheet',
  'tabview',
  'tab',
  'picker',
  'geometry',
  'layout',
  'grid',
  'gridrow',
  'lazyvgrid',
  'lazyhgrid',
  'gauge',
  'decorated',
  'menu',
  'toolbar',
  'searchfield',
  'tapgesture',
  // Phase 7
  'navsplit',
  'inspector',
  'inspectorpanel',
  'viewthatfits',
  'chart',
  'chartitem',
  // Phase 8: gestures, rows, navigation
  ...INTERACTION_KINDS,
  // Phase 8: alerts, controls
  'alert',
  'stepper',
  'slider',
  'datepicker',
  'texteditor',
  // A `Section`'s view header / footer
  'sectionheader',
  'sectionfooter',
  // `DisclosureGroup`: its label with a chevron, its content below once expanded
  'disclosuregroup',
  // An `AVPlayer` shown by a view (src/video.ts)
  'video',
  'lazyspacer',
  // A `Canvas`'s drawing (src/canvasDrawing.ts)
  'canvas',
]);

/** Duration of the alert / dialog fade (Phase 8); keep in sync with styles.css. */
export const ALERT_TRANSITION_MS = 250;
/** Width of the slider thumb (iOS 26: a 38×24 capsule); keep in sync with the engine and styles.css. */
const SLIDER_THUMB = 38;

/** Width of the menu a `menu` picker (or a Phase 6 `menu`) opens, like an iOS context menu. */
export const PICKER_MENU_WIDTH = 250;
/** Row height inside the picker menu. */
export const PICKER_MENU_ROW = 44;
/** A row with a subtitle under its title (styles.css, `[data-sb-subtitled]`). */
const MENU_SUBTITLED_ROW = 58;
/** Height of a section header caption row in the popover (Phase 6). */
const MENU_CAPTION_HEIGHT = 30;
/** A group separator in the popover: a hairline with 9px above and below (iOS 26). */
const MENU_SEPARATOR_HEIGHT = 19;
/** The popover's padding above its first row and below its last (iOS 26's inset menu); keep in sync with styles.css. */
const MENU_PADDING = 10;
/** Gap between the control and its menu. */
const PICKER_MENU_GAP = 4;
/** Minimum distance between the menu and the screen edge. */
const PICKER_MENU_MARGIN = 8;

/** Kinds whose enter/exit is animated by their own CSS, not by transitions. */
const SELF_ANIMATING: ReadonlySet<string> = new Set(['navscreen', 'sheet', 'alert']);

/** `styled` animations by element (cleared on `update`). */
const ANIMATIONS = new WeakMap<HTMLElement, Animation>();

/** iPhone 15 Pro logical screen, used when the root is not rendered (tests, hidden tabs). */
const DEFAULT_SCREEN = { width: 393, height: 852 };
const DEFAULT_SAFE_AREA = { top: 59, leading: 0, bottom: 34, trailing: 0 };

/** The parts of the layout environment the page controls. */
export interface RendererEnvironment {
  dynamicTypeSize: DynamicTypeSize;
  colorScheme: ColorScheme;
  /** The platform drawn (docs/notes/macos.md): iOS unless the page says macOS. */
  platform: Platform;
}

export class Renderer {
  /** All live elements by id, including the root (id 0). */
  readonly elements = new Map<ElementId, HTMLElement>();
  /** The element tree as the layout engine reads it; id 0 is the root. */
  readonly nodes = new Map<ElementId, LayoutNode>();
  /** Parent id of every attached element. */
  readonly parents = new Map<ElementId, ElementId>();
  private readonly root: HTMLElement;
  private readonly onEvent: EventCallback;
  /** Animates style changes, frame changes and transitions of animated passes. */
  readonly animator: Animator;
  /** Number of `commit` ops applied so far. */
  commits = 0;
  /** Number of layout passes run so far. */
  layouts = 0;
  /** The most recent layout result (null before the first commit). */
  layoutResult: LayoutResult | null = null;
  /** What the current render pass did so far (handed to the Animator at `commit`). */
  private pass: PassRecord = emptyPass();
  /**
   * Whether the current pass can animate (see `passCanAnimate`), decided at
   * its first op; null between passes. A pass that cannot skips the style
   * snapshots and rectangles the Animator would only need to animate, each
   * of which forces a style recalculation.
   */
  private passAnimates: boolean | null = null;
  /** The animation root's rectangle, read once per pass that animates. */
  private passRootRect: DOMRect | null = null;
  /**
   * What the Animator needs from before the pass, read in one go before its
   * first write (`prereadPass`): the style snapshots of the elements its
   * updates change and the rectangles of those its removes take out. Read
   * op by op, between writes, each forced a style recalculation or layout.
   */
  private readonly preSnapshots = new Map<HTMLElement, StyleSnapshot | null>();
  private readonly preRects = new Map<HTMLElement, { rect: DOMRect | null; matched: { id: string; el: HTMLElement; rect: DOMRect }[] }>();
  /**
   * The root's size as the engine last read it; null when it must be read
   * again. Reading it after a pass changed the DOM forces a style and layout
   * recalculation, so it is read once and then only after the root resized
   * (`resizeObserver`, `viewportChanged`). Without a ResizeObserver it is
   * read on every pass.
   */
  private rootSize: { width: number; height: number } | null = null;
  private readonly resizeObserver: ResizeObserver | null = null;
  /** `reportsPosition` scroll views mid-scroll: the timer that calls the scroll settled (src/scrollPosition.ts). */
  private readonly positionTimers = new Map<HTMLElement, number>();
  /** `reportsPosition` scroll views a finger is on: a scroll under it is not settled, however still the finger holds. */
  private readonly heldScrolls = new Set<HTMLElement>();
  /** The scroll target each `reportsPosition` scroll view last reported, so a settle at the same one says nothing. */
  private readonly reportedPositions = new WeakMap<HTMLElement, number>();
  /**
   * `host` elements (src/hostContent.ts): their content elements, observed
   * for size changes, and the measurements of the current layout pass.
   */
  private readonly hostObserver: ResizeObserver | null = null;
  /** `hover` and `continuoushover` events for boxes whose style asks for them (pointerEffects.ts). */
  private readonly hover: HoverTracker;
  /** A frame check of the `reportsFrame` readers is queued for the next animation frame (after a scroll). */
  private frameCheckQueued = false;
  /** `reportsGeometry` scroll views (src/scrollGeometry.ts): their ids, the last report of each, and whether a check waits for the next frame. */
  private readonly scrollReporters = new Set<ElementId>();
  private readonly scrollGeometry = new ScrollGeometryReporter();
  private scrollGeometryQueued = false;
  /** `scrollview`s whose `scrollOffset` command is new this pass: scrolled after its layout (`performOffsetScrolls`), and the serial each last carried out. */
  private pendingOffsets: ElementId[] = [];
  private readonly offsetSerials = new Map<ElementId, number>();
  private readonly hostContents = new Map<ElementId, HTMLElement>();
  /** The `host` elements, adopted or still waiting for their content. */
  private readonly hosts = new Set<ElementId>();
  /** The registered views (src/viewRegistry.ts) built for `registered` elements, by element: the name each was built for. */
  private readonly registeredViews = new Map<ElementId, { name: string; instance: RegisteredViewInstance | undefined }>();
  private readonly hostSizes = new WeakMap<HTMLElement, { width: number; height: number }>();
  private readonly hostMeasures = new Map<string, { width: number; height: number }>();
  private readonly stopHostChanges: () => void;
  private readonly stopRichEditors: () => void;
  private readonly stopPlainEditors: () => void;
  /**
   * The root takes its content's height (`LayoutEnvironment.contentHeight`):
   * a SwiftUI island in a page's own layout. Set before the first commit.
   */
  contentHeight = false;
  /**
   * The Mac's sidebar panel paints its top 44 in the window's color, as
   * macOS draws the title bar behind the window's buttons (the default).
   * `false` keeps the panel's sidebar fill to its top, for a page that has
   * no window buttons there. Web only: a native app is in a real window.
   */
  get sidebarTitleBar(): boolean {
    return this.root.dataset['sbSidebarTitleBar'] !== 'none';
  }
  set sidebarTitleBar(shown: boolean) {
    if (shown) delete this.root.dataset['sbSidebarTitleBar'];
    else this.root.dataset['sbSidebarTitleBar'] = 'none';
  }
  /**
   * Phase 8: owns every pointer gesture (drags, long presses, swipe rows,
   * swipe back, the sheet drag, pull to refresh) and arbitrates between them.
   */
  readonly interaction: InteractionController;
  private readonly pressEffects: PressEffects;
  /** Text, links and files dragged in from outside the page (src/dragDrop.ts). */
  private readonly externalDrops: ExternalDrops;
  /**
   * How a dropped file reaches the app: base64 `data` in the event (a Swift
   * app) or the `File` by reference (a React app in the same page sets
   * `reference`; a dropped folder then comes as its files by path).
   */
  fileDrops: 'bytes' | 'reference' = 'bytes';
  private readonly pinches: PinchController;
  private readonly touches: TouchesController;
  private readonly touchPaging: TouchPaging;
  /** `video` elements' media events, as `video` events (src/video.ts). */
  private readonly videoEvents: VideoEvents;
  private readonly measurer = new CanvasTextMeasurer();
  /**
   * Phase 9: the layout pass lives across commits and is told what changed
   * (`update`, `insert`, `remove`), so a commit lays out only the subtrees
   * it touched. Replaced whenever the environment (screen size, type size,
   * color scheme, fonts) changes, which invalidates everything.
   */
  private engine: LayoutPass | null = null;
  private engineEnvKey = '';
  private environment: RendererEnvironment = { dynamicTypeSize: 'large', colorScheme: 'light', platform: 'ios' };
  /** Phase 5: the app's asset catalog manifest (empty until `setAssetCatalog`). */
  private assets: AssetCatalog = EMPTY_CATALOG;
  /** The `<style>` holding the catalog colors as custom properties (see `setAssetCatalog`). */
  private assetStyle: HTMLStyleElement | null = null;
  /** Safe-area insets; read from the device tokens on first use unless `setSafeArea` set them. */
  private safeArea: LayoutEnvironment['safeArea'] | null = null;
  /** Device mode: the on-screen keyboard's height over the screen's bottom (`setKeyboard`), 0 while it is down. */
  private keyboard = 0;
  /** DOM boxes applied by the last layout, by id (see layoutDom.ts). */
  private boxes = new Map<ElementId, Rect>();
  /** Elements whose inline style was reset since the last layout. */
  private readonly dirty = new Set<ElementId>();
  /** The open popover (of a `menu` picker or a `menu` element), if any. */
  private menu: OpenMenu | null = null;
  /** Lazy menus: a menu the viewer opened whose items Swift is building. */
  private pendingMenu: { anchor: HTMLElement; id: ElementId; kind: 'menu' | 'contextmenu' } | null = null;
  /** Size last reported for every `geometry` element (whole px). */
  private readonly reportedGeometry = new Map<ElementId, { width: number; height: number; key: string }>();
  /** The elements whose size is reported (`geometry`, or `reportsSize` props): what `collectGeometry` visits. */
  private readonly geometryReporters = new Set<ElementId>();
  /** The custom shapes with a path for any size (`affinePath`), drawn at their size after each layout. */
  private readonly affinePaths = new Set<ElementId>();
  /** `geometry` and `layout` events collected by layout passes, sent once the ops are applied. */
  private pendingGeometry: (GeometryEvent | LayoutEvent | ScrollGeometryEvent)[] = [];
  /** Phase 6: dedupes `layout` events per element (src/layoutEvents.ts). */
  private readonly layoutReporter = new LayoutReporter();
  /** Nesting depth of `applyOps` (events are flushed when it returns to 0). */
  private applying = 0;
  /** `styled` elements whose props in this pass carry a new `scrollTo` command (src/scrollTo.ts). */
  private pendingScrolls: ElementId[] = [];
  /** Lazy rows: the `lazyspacer`s on the page, and those already reported near the visible area. */
  private lazySpacers = new Set<ElementId>();
  /** `styled` boxes with `scrollVisibility`, and what each was last told (src/scrollVisibility.ts). */
  private readonly visibilityTargets = new Map<ElementId, number>();
  private readonly visibilityReported = new Map<ElementId, boolean>();
  private visibilityCheckQueued = false;
  private lazyReported = new Set<ElementId>();
  private lazyCheckQueued = false;
  /** The highest `scrollTo` serial seen: a command keeps its serial until the reader re-runs, and runs once. */
  private scrollSerial = 0;

  /**
   * A row that taps its button (`rowAction`) highlights while pressed, as a
   * row that is a button does (`:active`, styles.css): until the pointer is
   * released or the press turns into a scroll.
   */
  private readonly onRowPress = (event: PointerEvent): void => {
    const hit = event.target as HTMLElement | null;
    if (!hit || !event.isPrimary || hit.closest('.sb-button, .sb-navlink')) return;
    const button = rowAction(hit, this.root);
    const row = button ? listRowOf(button, this.root) : null;
    if (!row) return;
    row.dataset['sbRowPressed'] = '';
    const release = (): void => {
      delete row.dataset['sbRowPressed'];
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
      this.root.removeEventListener('scroll', release, true);
    };
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', release, true);
    this.root.addEventListener('scroll', release, true);
  };

  private readonly onClick = (event: MouseEvent): void => {
    // `closest` picks the innermost control, so a button inside a
    // `tapgesture` (or a menu label) handles the click alone. Elsewhere in
    // a list row, the row's one button (`rowAction`).
    const hit = event.target as HTMLElement | null;
    let target = hit?.closest<HTMLElement>(
      '.sb-button, .sb-navlink, .sb-navbar-back, .sb-navbar-more, .sb-navsplit-toggle, .sb-switch, .sb-sheet-scrim, ' +
        '.sb-toggle[data-sb-toggle-style="button"], .sb-toolbar .sb-toggle:not([data-sb-toggle-style]), ' +
        '.sb-tabbar-item, .sb-segment, .sb-picker-option, .sb-picker-menu-item, .sb-picker[data-sb-style="menu"], ' +
        '.sb-menu, .sb-tapgesture, .sb-stepper-button, .sb-alert-scrim, .sb-alert-ok, ' +
        // Phase 8: swipe action buttons, the edit-mode row control and selectable rows (the row sends `tap` itself)
        '.sb-swipe-action, .sb-listrow-control, .sb-listrow[data-sb-selectable], ' +
        // A disclosure group's label, a collapsible section's header: a `tap` on the element toggles it
        '.sb-disclosure-toggle, .sb-section[data-sb-collapsible] > .sb-section-header, .sb-section[data-sb-collapsible] .sb-sectionheader, ' +
        // A link in a rich text: innermost, so it wins over a tappable row around the text
        '.sb-link',
    ) ?? (hit ? rowAction(hit, this.root) : null);
    // A link or button row of a list in edit mode is selected, not followed
    // or pressed: the tap is the row's (its `tag`), as iOS selects the row then.
    if (target?.classList.contains('sb-navlink') || target?.classList.contains('sb-button')) {
      const row = target.closest<HTMLElement>('.sb-listrow[data-sb-selectable]');
      if (row && row.closest('.sb-list')?.hasAttribute('data-sb-editing')) target = row;
    }
    if (!target || !this.root.contains(target)) return;
    if (target.classList.contains('sb-link')) {
      const text = target.closest<HTMLElement>('.sb-text');
      const id = Number(text?.dataset[ID_ATTR]);
      const url = target.dataset['sbLink'];
      if (!text || !Number.isInteger(id) || url === undefined) return;
      event.preventDefault();
      this.onEvent({ type: 'link', id, value: url });
      return;
    }
    const sectionHeader = target.classList.contains('sb-section-header') || target.classList.contains('sb-sectionheader');
    if (target.classList.contains('sb-disclosure-toggle') || sectionHeader) {
      // `DisclosureGroup` and `Section(isExpanded:)`: the tap toggles the
      // element (a section's only in a sidebar-style list, where it collapses).
      const section = sectionHeader;
      const owner = target.closest<HTMLElement>(section ? '.sb-section' : '.sb-disclosuregroup');
      const id = Number(owner?.dataset[ID_ATTR]);
      if (!owner || !Number.isInteger(id)) return;
      if (section && owner.closest<HTMLElement>('.sb-list')?.dataset['sbListStyle'] !== 'sidebar') return;
      event.preventDefault();
      this.onEvent({ type: 'tap', id });
      return;
    }
    if (this.interaction.handleClick(target)) {
      event.preventDefault();
      return;
    }
    if (target.classList.contains('sb-stepper-button')) {
      // Phase 8: a half of the −/+ control; a dimmed half (`canDecrement` /
      // `canIncrement` false) swallows the click.
      const stepper = target.closest<HTMLElement>('.sb-stepper');
      const id = Number(stepper?.dataset[ID_ATTR]);
      const value = Number(target.dataset['sbStep']);
      if (!stepper || !Number.isInteger(id) || !Number.isInteger(value)) return;
      event.preventDefault();
      if (target.getAttribute('aria-disabled') === 'true') return;
      this.onEvent({ type: 'step', id, value });
      return;
    }
    if (target.classList.contains('sb-alert-scrim') || target.classList.contains('sb-alert-ok')) {
      // Phase 8: the renderer-drawn OK, or the scrim of a dialog (an alert
      // with buttons is modal; without any it dismisses too) ask Swift to
      // dismiss with the alert's own id.
      const alert = target.closest<HTMLElement>('.sb-alert');
      const id = Number(alert?.dataset[ID_ATTR]);
      if (!alert || !Number.isInteger(id)) return;
      event.preventDefault();
      if (target.classList.contains('sb-alert-scrim')) {
        const hasButtons = alert.querySelector('.sb-alert-buttons > [data-sb-id]') !== null;
        if (alert.dataset['sbStyle'] !== 'dialog' && hasButtons) return;
      }
      this.onEvent({ type: 'tap', id });
      return;
    }
    if (target.classList.contains('sb-tabbar-item')) {
      // A tab bar item asks Swift to select the tab; `selected` is authoritative.
      const tabview = target.closest<HTMLElement>('.sb-tabview');
      const id = Number(tabview?.dataset[ID_ATTR]);
      const index = Number(target.dataset['sbIndex']);
      if (!tabview || !Number.isInteger(id) || !Number.isInteger(index)) return;
      event.preventDefault();
      this.onEvent({ type: 'select', id, value: index });
      return;
    }
    if (target.classList.contains('sb-segment') || target.classList.contains('sb-picker-option')) {
      const picker = target.closest<HTMLElement>('.sb-picker');
      const id = Number(picker?.dataset[ID_ATTR]);
      const index = Number(target.dataset['sbIndex']);
      if (!picker || !Number.isInteger(id) || !Number.isInteger(index)) return;
      event.preventDefault();
      // Move the pill / checkmark right away; Swift's `update` confirms (or reverts) it.
      setPickerSelection(picker, index);
      this.onEvent({ type: 'select', id, value: index });
      return;
    }
    if (target.classList.contains('sb-picker-menu-item')) {
      // A popover row: a picker option (`select`), a toggle, or a button / navlink (`tap`).
      if (!this.menu) return;
      const action = target.dataset['sbAction'];
      const id = Number(target.dataset['sbTarget']);
      const index = Number(target.dataset['sbIndex']);
      if (!Number.isInteger(id)) return;
      event.preventDefault();
      this.closeMenu();
      if (action === 'tap') this.onEvent({ type: 'tap', id });
      else if (action === 'toggle') this.onEvent({ type: 'toggle', id, value: target.getAttribute('aria-checked') !== 'true' });
      else if (Number.isInteger(index)) this.onEvent({ type: 'select', id, value: index });
      return;
    }
    if (target.classList.contains('sb-picker') || target.classList.contains('sb-menu')) {
      const id = Number(target.dataset[ID_ATTR]);
      if (!Number.isInteger(id)) return;
      event.preventDefault();
      if (this.menu?.anchorId === id) this.closeMenu();
      else if (target.classList.contains('sb-menu')) this.openMenuWhenBuilt(target, id, 'menu');
      else this.openMenu(target, id, this.pickerRows(id));
      return;
    }
    if (target.classList.contains('sb-sheet-scrim')) {
      // A tap outside the card asks Swift to dismiss the sheet.
      const sheet = target.closest<HTMLElement>('.sb-sheet');
      const id = Number(sheet?.dataset[ID_ATTR]);
      if (!sheet || !Number.isInteger(id)) return;
      event.preventDefault();
      this.onEvent({ type: 'tap', id });
      return;
    }
    if (target.classList.contains('sb-navsplit-toggle')) {
      const split = target.closest<HTMLElement>('.sb-navsplit');
      const id = Number(split?.dataset[ID_ATTR]);
      if (!split || !Number.isInteger(id)) return;
      event.preventDefault();
      this.onEvent({ type: 'tap', id });
      return;
    }
    if (target.classList.contains('sb-navbar-more')) {
      // The collapsed `secondary` items, as a menu under the button.
      const screen = target.closest<HTMLElement>('.sb-navscreen');
      const id = Number(screen?.dataset[ID_ATTR]);
      if (!screen || !Number.isInteger(id)) return;
      event.preventDefault();
      if (this.menu?.anchorId === id) this.closeMenu();
      else this.openMenu(target, id, this.collapsedSecondaryRows(id));
      return;
    }
    if (target.classList.contains('sb-navbar-back')) {
      const id = Number(target.dataset['sbTarget']);
      if (!Number.isInteger(id)) return;
      event.preventDefault();
      this.onEvent({ type: 'tap', id });
      return;
    }
    if (target.classList.contains('sb-switch') || target.classList.contains('sb-toggle')) {
      // The switch, or a toggle drawn as a button (the whole of it).
      const toggle = target.closest<HTMLElement>('.sb-toggle');
      const id = Number(toggle?.dataset[ID_ATTR]);
      if (!toggle || !Number.isInteger(id)) return;
      event.preventDefault();
      const value = toggle.dataset['sbOn'] !== 'true';
      // Flip optimistically so the switch animates right away; Swift's
      // `update` confirms (or reverts) the state.
      setSwitchState(toggle, value);
      this.onEvent({ type: 'toggle', id, value });
      return;
    }
    const id = Number(target.dataset[ID_ATTR]);
    if (!Number.isInteger(id)) return;
    event.preventDefault();
    if (target.classList.contains('sb-tapgesture')) {
      // `onTapGesture(count: 2)`: the innermost double tap on the way up
      // counts the clicks; the innermost single tap waits for it.
      const multi = target.closest<HTMLElement>('.sb-tapgesture[data-sb-tap-count]');
      const single = target.closest<HTMLElement>('.sb-tapgesture:not([data-sb-tap-count])');
      this.taps.click(multi, Number(multi?.dataset['sbTapCount'] ?? 1), single, { x: event.clientX, y: event.clientY });
      return;
    }
    this.onEvent({ type: 'tap', id });
  };

  /** A `tapgesture` fires: with `location`, the click's point in the element's own coordinates (points; the device frame may be scaled). */
  private readonly taps = new TapCounter<HTMLElement, { x: number; y: number }>((el, point) => {
    const id = Number(el.dataset[ID_ATTR]);
    if (!Number.isInteger(id)) return;
    if (el.hasAttribute('data-sb-tap-location')) {
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      const x = Math.round(((point.x - rect.left) / (scale || 1)) * 100) / 100;
      const y = Math.round(((point.y - rect.top) / (scale || 1)) * 100) / 100;
      this.onEvent({ type: 'tap', id, x, y });
      return;
    }
    this.onEvent({ type: 'tap', id });
  });

  /**
   * Phase 12: after a commit, the bound text fields (`data-sb-focus`) take or
   * give up focus to match what Swift asked for. The `focus` event the move
   * raises is echoed back so the state and the DOM agree. A field the app
   * focuses is scrolled into view (`revealFocused`), as SwiftUI shows a
   * field its `@FocusState` focuses.
   */
  private syncFocus(): void {
    const active = document.activeElement;
    for (const input of this.root.querySelectorAll<HTMLElement>('.sb-textfield[data-sb-focus], .sb-texteditor[data-sb-focus]')) {
      const wanted = input.dataset['sbFocus'] === 'true';
      // `preventScroll`: a field in a sheet still sliding in sits below the
      // screen, and the browser would scroll the whole screen to reveal it,
      // blanking the page until something scrolls it back.
      if (wanted && active !== input) {
        if (isRichEditor(input)) focusRichEditor(input);
        else if (isPlainEditor(input)) focusPlainEditor(input);
        else input.focus({ preventScroll: true });
        this.revealFocused(input);
      } else if (!wanted && active === input) input.blur();
    }
  }

  /**
   * Scrolls the scroll views and lists around a text input as little as
   * shows it, from the layout's frames (src/scrollTo.ts), never the page:
   * the frames are where the views rest, so a sheet still sliding in is no
   * reason to scroll, and in device mode they already stop above the
   * keyboard, which the focus raised before this ran (`watchKeyboard`
   * reports its known height on `focusin`, and `setKeyboard` lays out).
   */
  private revealFocused(input: HTMLElement): void {
    const result = this.layoutResult;
    const id = Number(input.dataset[ID_ATTR]);
    if (!result || !Number.isInteger(id)) return;
    scrollToTarget(this, result, id, undefined, false);
  }

  /**
   * `.scrollDismissesKeyboard`: scrolling a list or scroll view marked
   * `data-sb-dismisses-keyboard` takes the focus from the text input being
   * edited inside it (a bound field then reports the blur as usual).
   */
  private readonly onScrollDismissKeyboard = (event: Event): void => {
    const scroller = event.target;
    if (!(scroller instanceof HTMLElement) || scroller.dataset['sbDismissesKeyboard'] !== 'true') return;
    const active = document.activeElement;
    if ((active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || isRichEditor(active)) && scroller.contains(active)) (active as HTMLElement).blur();
  };

  /**
   * A press anywhere but another text input leaves the text being edited
   * focused, as on iOS, where only a text input takes the first responder
   * (a Bold button acts on the selection still there; a stepper, a row or
   * the space beside the editor leaves the keyboard up): the mouse down
   * that would move the focus does nothing else.
   */
  private readonly onControlMouseDown = (event: MouseEvent): void => {
    const active = document.activeElement;
    const editing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || isRichEditor(active);
    if (!editing || !this.root.contains(active)) return;
    const target = event.target as HTMLElement | null;
    if (target && !target.closest('input, textarea, [contenteditable]')) event.preventDefault();
  };

  /** Phase 12: a bound field (`data-sb-focus`) reports focus changes. */
  private readonly onFocusChange = (event: FocusEvent): void => {
    // A screen's `keyboard` toolbars show while one of its text inputs is
    // focused: the focus moving is a layout change there.
    if (this.commits > 0 && this.hasKeyboardToolbars()) this.layout();
    // A bound `texteditor` (UIKit's text views) reports focus like a field.
    const input = event.target as HTMLElement | null;
    const isField =
      (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) && input.classList.contains('sb-textfield');
    const isEditor = (input instanceof HTMLTextAreaElement || isRichEditor(input)) && input!.classList.contains('sb-texteditor');
    if (!input || (!isField && !isEditor)) return;
    if (input.dataset['sbFocus'] === undefined || !this.root.contains(input)) return;
    const id = Number(input.dataset[ID_ATTR]);
    if (!Number.isInteger(id)) return;
    const focused = event.type === 'focusin';
    if (input.dataset['sbFocus'] === String(focused)) return;
    input.dataset['sbFocus'] = String(focused);
    this.onEvent({ type: 'focus', id, value: focused });
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.menu) {
      event.preventDefault();
      this.closeMenu();
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    // Phase 12: Enter in a text field under `.onSubmit`.
    if (
      event.key === 'Enter' &&
      target.classList.contains('sb-textfield') &&
      target.dataset['sbSubmit'] === 'true' &&
      !(event.shiftKey && target instanceof HTMLTextAreaElement)
    ) {
      const id = Number(target.dataset[ID_ATTR]);
      if (Number.isInteger(id)) {
        event.preventDefault();
        this.onEvent({ type: 'submit', id });
      }
      return;
    }
    if (
      !target.classList.contains('sb-navlink') &&
      !target.classList.contains('sb-menu') &&
      !(target.classList.contains('sb-picker') && target.dataset['sbStyle'] === 'menu') &&
      !(target.classList.contains('sb-toggle') && target.dataset['sbToggleStyle'] === 'button')
    ) {
      return;
    }
    event.preventDefault();
    target.click();
  };

  /**
   * While a picker menu is open, a click anywhere outside it closes the menu;
   * inside the screen the click is swallowed so nothing under the pointer
   * fires (iOS dismisses a menu without passing the tap through).
   */
  private readonly onDocumentClick = (event: MouseEvent): void => {
    const menu = this.menu;
    if (!menu) return;
    const target = event.target as Node | null;
    if (target && menu.el.contains(target)) return;
    this.closeMenu();
    if (target && this.root.contains(target)) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  /**
   * Backspace in a `textfield` or plain `texteditor` with `deleteBackward`,
   * nothing selected and the caret at the start: the key deletes nothing
   * and the app hears `deletebackward` (the rich editor's own `beforeinput`
   * does the same, src/richEditor.ts).
   */
  private readonly onBeforeInput = (event: InputEvent): void => {
    if (event.inputType !== 'deleteContentBackward') return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) || !this.root.contains(input) || !wantsDeleteBackward(input)) return;
    if (input.selectionStart !== 0 || input.selectionEnd !== 0) return;
    const id = Number(input.dataset[ID_ATTR]);
    if (!Number.isInteger(id)) return;
    event.preventDefault();
    this.onEvent({ type: 'deletebackward', id });
  };

  private readonly onInput = (event: Event): void => {
    const input = event.target as HTMLInputElement | null;
    if (!input || !this.root.contains(input)) return;
    // A `textfield` (or a Phase 8 `texteditor`) is the input itself; a
    // `searchfield` wraps its input.
    const isField = input.classList.contains('sb-textfield') || input.classList.contains('sb-texteditor');
    const isSearch = input.classList.contains('sb-searchfield-input');
    if (!isField && !isSearch) return;
    const owner = isField ? input : input.closest<HTMLElement>('.sb-searchfield');
    const id = Number(owner?.dataset[ID_ATTR]);
    if (!Number.isInteger(id)) return;
    const value = isRichEditor(input) ? richEditorText(input) : input.value;
    noteReportedText(input, value);
    this.onEvent({ type: 'text', id, value });
  };

  /**
   * Phase 8: the native `<input type="date|time">` over a `datepicker`
   * capsule committed a value. The changed component replaces its part of
   * the picker's `seconds`; the other part is kept.
   */
  private readonly onChange = (event: Event): void => {
    const input = event.target as HTMLInputElement | null;
    if (!input || !input.classList.contains('sb-datepicker-input') || !this.root.contains(input)) return;
    const picker = input.closest<HTMLElement>('.sb-datepicker');
    const id = Number(picker?.dataset[ID_ATTR]);
    if (!picker || !Number.isInteger(id) || input.value === '') return;
    const seconds = Number(picker.dataset['sbSeconds']) || 0;
    const value = combineDateSeconds(seconds, input.type === 'date' ? { date: input.value } : { time: input.value }, picker.dataset['sbTimeZone'] || undefined);
    if (value === seconds) return;
    // Show the new value right away; Swift's `update` confirms (or reverts) it.
    picker.dataset['sbSeconds'] = String(value);
    setDatePickerValue(picker, value);
    this.onEvent({ type: 'date', id, value });
  };

  // Phase 8 slider: a pointer on the slider moves the thumb to it and follows
  // it; `slide` events are coalesced to one per animation frame.
  private sliderDrag: SliderDrag | null = null;

  private beginSliderDrag(slider: HTMLElement, event: PointerEvent): void {
    const track = slider.querySelector<HTMLElement>(':scope > .sb-slider-track');
    const id = Number(slider.dataset[ID_ATTR]);
    if (!track || !Number.isInteger(id)) return;
    event.preventDefault();
    const drag: SliderDrag = { slider, track, id, pointerId: event.pointerId, pending: null, sent: null, frame: null, starting: true };
    this.sliderDrag = drag;
    window.addEventListener('pointermove', this.onSliderMove);
    window.addEventListener('pointerup', this.onSliderUp);
    window.addEventListener('pointercancel', this.onSliderUp);
    this.slideTo(drag, event.clientX);
  }

  private slideTo(drag: SliderDrag, clientX: number): void {
    const rect = drag.track.getBoundingClientRect();
    // The presenting tree may be scaled (behind a sheet): measure the thumb in screen px too.
    const scale = drag.track.offsetWidth > 0 ? rect.width / drag.track.offsetWidth : 1;
    const thumb = SLIDER_THUMB * scale;
    const travel = rect.width - thumb;
    const fraction = travel > 0 ? (clientX - rect.left - thumb / 2) / travel : 0;
    const min = Number(drag.slider.dataset['sbMin']) || 0;
    const max = Number(drag.slider.dataset['sbMax']);
    const stepText = drag.slider.dataset['sbStep'];
    const step = stepText ? Number(stepText) : null;
    const value = snapSliderValue(fraction, min, Number.isFinite(max) ? max : 1, step);
    setSliderPosition(drag.slider, sliderFraction(value, min, Number.isFinite(max) ? max : 1));
    drag.pending = value;
    if (drag.frame === null) {
      drag.frame = window.requestAnimationFrame(() => {
        drag.frame = null;
        this.flushSlide(drag);
      });
    }
  }

  private flushSlide(drag: SliderDrag): void {
    if (drag.pending === null || (drag.pending === drag.sent && !drag.starting)) return;
    drag.sent = drag.pending;
    // The drag's first value says editing began (`onEditingChanged(true)`); the last, on release, that it ended.
    this.onEvent(drag.starting ? { type: 'slide', id: drag.id, value: drag.pending, editing: true } : { type: 'slide', id: drag.id, value: drag.pending });
    drag.starting = false;
  }

  private readonly onSliderMove = (event: PointerEvent): void => {
    const drag = this.sliderDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.slideTo(drag, event.clientX);
  };

  private readonly onSliderUp = (event: PointerEvent): void => {
    const drag = this.sliderDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.sliderDrag = null;
    window.removeEventListener('pointermove', this.onSliderMove);
    window.removeEventListener('pointerup', this.onSliderUp);
    window.removeEventListener('pointercancel', this.onSliderUp);
    if (drag.frame !== null) window.cancelAnimationFrame(drag.frame);
    drag.frame = null;
    this.flushSlide(drag);
    if (drag.sent !== null) this.onEvent({ type: 'slide', id: drag.id, value: drag.sent, editing: false });
    // The click that follows is the slide itself, not a tap on an enclosing control.
    this.swallowNextClick();
  };

  /** Suppress the click that follows a drag so a control under the pointer does not fire. */
  private swallowNextClick(): void {
    const swallow = (click: Event): void => {
      click.stopPropagation();
      click.preventDefault();
    };
    this.root.addEventListener('click', swallow, { capture: true, once: true });
    window.setTimeout(() => this.root.removeEventListener('click', swallow, { capture: true }), 0);
  }

  // Phase 8 slider: the pointer down that starts a drag (the gesture
  // controller in src/interaction.ts leaves `.sb-slider` alone).
  private readonly onSliderPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || this.sliderDrag) return;
    const target = event.target as HTMLElement | null;
    const slider = target?.closest<HTMLElement>('.sb-slider');
    if (slider && this.root.contains(slider)) this.beginSliderDrag(slider, event);
  };

  /**
   * The sidebar widths the user dragged each `navsplit`'s column divider to
   * (a Mac), by element id: the engine lays the split out with them, clamped
   * to the column's bounds (`LayoutEnvironment.sidebarWidths`).
   */
  private readonly sidebarWidths: Record<number, number> = {};
  private sidebarDrag: SidebarDrag | null = null;

  // A Mac split view's divider: the pointer down that starts resizing the sidebar.
  private readonly onDividerPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || this.sidebarDrag) return;
    const target = event.target as HTMLElement | null;
    const divider = target?.closest<HTMLElement>('.sb-navsplit-divider');
    const split = divider?.parentElement;
    if (!divider || !split || !this.root.contains(divider)) return;
    const id = Number(split.dataset[ID_ATTR]);
    const width = Number(divider.dataset['sbWidth']);
    if (!Number.isInteger(id) || !Number.isFinite(width)) return;
    event.preventDefault();
    // The presenting tree may be scaled (behind a sheet): pointer distances are in screen px.
    const rect = split.getBoundingClientRect();
    const scale = split.offsetWidth > 0 ? rect.width / split.offsetWidth : 1;
    this.sidebarDrag = { id, pointerId: event.pointerId, startX: event.clientX, startWidth: width, scale, frame: null, pending: width };
    this.root.classList.add('sb-resizing-sidebar');
    window.addEventListener('pointermove', this.onDividerMove);
    window.addEventListener('pointerup', this.onDividerUp);
    window.addEventListener('pointercancel', this.onDividerUp);
  };

  private readonly onDividerMove = (event: PointerEvent): void => {
    const drag = this.sidebarDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    drag.pending = Math.round(drag.startWidth + (event.clientX - drag.startX) / drag.scale);
    if (drag.frame !== null) return;
    // One layout per frame, however many pointer moves arrive.
    drag.frame = window.requestAnimationFrame(() => {
      drag.frame = null;
      if (this.sidebarWidths[drag.id] === drag.pending) return;
      this.sidebarWidths[drag.id] = drag.pending;
      if (this.commits > 0) this.layout();
    });
  };

  private readonly onDividerUp = (event: PointerEvent): void => {
    const drag = this.sidebarDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (drag.frame !== null) window.cancelAnimationFrame(drag.frame);
    this.sidebarDrag = null;
    this.root.classList.remove('sb-resizing-sidebar');
    window.removeEventListener('pointermove', this.onDividerMove);
    window.removeEventListener('pointerup', this.onDividerUp);
    window.removeEventListener('pointercancel', this.onDividerUp);
    if (this.sidebarWidths[drag.id] !== drag.pending) {
      this.sidebarWidths[drag.id] = drag.pending;
      if (this.commits > 0) this.layout();
    }
    // The width the sidebar is laid out at (clamped to the column's bounds), once a drag that changed it ends.
    const divider = this.elements.get(drag.id)?.querySelector<HTMLElement>(':scope > .sb-navsplit-divider');
    const width = Number(divider?.dataset['sbWidth']);
    if (Number.isFinite(width) && width !== drag.startWidth) this.onEvent({ type: 'columnwidth', id: drag.id, width });
  };

  constructor(root: HTMLElement, onEvent: EventCallback) {
    this.root = root;
    this.onEvent = onEvent;
    root.dataset[ID_ATTR] = String(ROOT_ID);
    root.dataset[KIND_ATTR] = 'root';
    this.elements.set(ROOT_ID, root);
    this.nodes.set(ROOT_ID, { id: ROOT_ID, kind: 'root', props: {}, children: [] });
    this.animator = new Animator(root, {
      selfAnimatingKinds: SELF_ANIMATING,
      playOwnFrames: (el, animation) => hasPathFrames(el) && playPathFrames(el, animation),
    });
    // Delegated listeners instead of one per control: elements can be
    // created detached and moved around freely.
    root.addEventListener('click', this.onClick);
    root.addEventListener('pointerdown', this.onRowPress);
    root.addEventListener('mousedown', this.onControlMouseDown);
    root.addEventListener('keydown', this.onKeyDown);
    root.addEventListener('beforeinput', this.onBeforeInput);
    root.addEventListener('input', this.onInput);
    root.addEventListener('focusin', this.onFocusChange);
    root.addEventListener('focusout', this.onFocusChange);
    root.addEventListener('scroll', this.onScrollDismissKeyboard, true);
    root.addEventListener('scroll', this.queueLazyCheck, { capture: true, passive: true });
    root.addEventListener('scroll', this.queueVisibilityCheck, { capture: true, passive: true });
    root.addEventListener('scroll', this.onPositionScroll, { capture: true, passive: true });
    root.addEventListener('scrollend', this.onPositionScrollEnd, { capture: true, passive: true });
    root.addEventListener('touchstart', this.onPositionTouchStart, { capture: true, passive: true });
    root.addEventListener('touchend', this.onPositionTouchEnd, { capture: true, passive: true });
    root.addEventListener('touchcancel', this.onPositionTouchEnd, { capture: true, passive: true });
    this.videoEvents = new VideoEvents(root, (event) => this.onEvent(event));
    // Phase 8: pointer gestures (the sheet drag among them) are delegated too.
    const host: InteractionHost = {
      root,
      send: (event) => this.onEvent(event),
      nodeOf: (id) => this.nodes.get(id),
      fileDrops: () => this.fileDrops,
      openContextMenu: (anchor, id) => this.openContextMenu(anchor, id),
      revealScreen: (screen) => this.revealScreen(screen),
      isMenuOpen: (id) => this.menu?.anchorId === id,
      closeMenu: () => this.closeMenu(),
      // iOS 26: the bar morphs with a swipe back (src/barMorph.ts).
      barMorphBegin: (screen, below) => {
        const stack = screen.closest<HTMLElement>('.sb-navstack');
        if (stack) this.beginBarMorph(stack, screen, below, true);
      },
      barMorphScrub: (screen, progress) => this.swipeBackMorphOf(screen)?.scrub(progress),
      barMorphSnapBack: (screen) => this.swipeBackMorphOf(screen)?.cancel(),
    };
    this.interaction = new InteractionController(host);
    // iOS 26: the glass under a pressed bar button swells and springs back (src/press.ts).
    this.pressEffects = new PressEffects(root);
    this.externalDrops = new ExternalDrops(host);
    // Two-finger gestures: MagnifyGesture and RotateGesture (src/pinch.ts).
    this.pinches = new PinchController(host);
    // Multi-touch: every pointer on a `touches` element (src/touches.ts).
    this.touches = new TouchesController(host);
    // A finger on a paging scroll view pages it as iOS does (src/touchPaging.ts);
    // the view it drives reports its position once the drive rests it on a page.
    this.touchPaging = new TouchPaging(root, (el) => {
      this.heldScrolls.delete(el);
      this.armPositionReport(el);
    });
    root.addEventListener('change', this.onChange);
    root.addEventListener('pointerdown', this.onSliderPointerDown);
    root.addEventListener('pointerdown', this.onDividerPointerDown);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        const before = this.rootSize;
        this.rootSize = null;
        if (!before || this.commits === 0) return;
        const now = this.readRootSize();
        if (now.width !== before.width || now.height !== before.height) this.layout();
      });
      this.resizeObserver.observe(root);
    }
    this.measurer.measureHost = (content, width) => this.measureHostContent(content, width);
    if (typeof ResizeObserver !== 'undefined') {
      this.hostObserver = new ResizeObserver((entries) => {
        let changed = false;
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const size = { width: el.offsetWidth, height: el.offsetHeight };
          const last = this.hostSizes.get(el);
          if (last && last.width === size.width && last.height === size.height) continue;
          this.hostSizes.set(el, size);
          changed = true;
        }
        if (changed) this.hostContentChanged();
      });
    }
    this.stopHostChanges = onHostContentChange(() => this.hostContentChanged());
    const editorId = (el: HTMLElement): number | null => {
      const id = Number(el.dataset[ID_ATTR]);
      return Number.isInteger(id) ? id : null;
    };
    this.stopRichEditors = listenToRichEditors(root, editorId, (event) => this.onEvent(event));
    // After `onInput` above, so a keystroke's `text` goes before its `selection`.
    this.stopPlainEditors = listenToPlainEditors(root, editorId, (event) => this.onEvent(event));
    this.hover = new HoverTracker(root, (event) => this.onEvent(event));
    root.addEventListener('scroll', this.queueFrameCheck, { capture: true, passive: true });
    // Scroll transitions and pinned views follow the scroll in the frame it is drawn in.
    root.addEventListener('scroll', drawScrollTransitions, { capture: true, passive: true });
    root.addEventListener('scroll', drawPinnedViews, { capture: true, passive: true });
    root.addEventListener('scroll', this.queueScrollGeometryCheck, { capture: true, passive: true });
  }

  /**
   * `reportsGeometry` scroll views report where they are once per animation
   * frame while anything scrolls (the offsets change without a layout pass).
   */
  private readonly queueScrollGeometryCheck = (): void => {
    if (this.scrollGeometryQueued || this.commits === 0 || this.scrollReporters.size === 0) return;
    this.scrollGeometryQueued = true;
    requestAnimationFrame(() => {
      this.scrollGeometryQueued = false;
      if (!this.layoutResult) return;
      this.collectScrollGeometry(this.layoutResult);
      if (this.applying === 0) this.flushGeometry();
    });
  };

  /** Queue a `scrollgeometry` event for every reporting scroll view whose geometry changed. */
  private collectScrollGeometry(result: LayoutResult): void {
    for (const id of this.scrollReporters) {
      const el = this.elements.get(id);
      if (!el || !el.isConnected) continue;
      const frame = result.frames.get(id);
      if (!frame) continue; // inside a hidden tab
      const covered = result.chrome.get(id)?.['bottomInset'];
      const bottomInset = covered ? Math.max(0, frame.y + frame.height - covered.y) : 0;
      const event = this.scrollGeometry.report(id, readScrollGeometry(el, frame, result.contentSizes.get(id), bottomInset));
      if (event) this.pendingGeometry.push(event);
    }
  }

  /**
   * Geometry readers with `reportsFrame` report where they are on screen,
   * which scrolling changes without a layout pass: after a scroll their
   * frames are checked once per animation frame.
   */
  private readonly queueFrameCheck = (): void => {
    if (this.frameCheckQueued || this.commits === 0) return;
    this.frameCheckQueued = true;
    requestAnimationFrame(() => {
      this.frameCheckQueued = false;
      if (!this.layoutResult) return;
      this.collectGeometry(this.layoutResult, true);
      if (this.applying === 0) this.flushGeometry();
    });
  };

  /**
   * A `host` element's content changed (the page said so, or it resized):
   * the hosts are measured again and the tree laid out. A host whose content
   * was not registered when its element was created (a page that registers
   * from an effect, after its commit) adopts it now.
   */
  private hostContentChanged(): void {
    if (this.commits === 0) return;
    for (const id of this.hosts) {
      if (this.hostContents.has(id)) continue;
      const el = this.elements.get(id);
      const node = this.nodes.get(id);
      if (el && node) this.adoptHostContent(id, el, node.props as unknown as HostProps);
    }
    if (this.hostContents.size === 0) return;
    for (const id of this.hostContents.keys()) {
      const node = this.nodes.get(id);
      if (node) this.engine?.invalidate(node);
    }
    this.layout();
  }

  /**
   * The host measurer (src/layout, `measureHost`): the content element laid
   * out at `width`, or at its own ideal width when null. Measured in place,
   * so the content sees the device's tokens and fonts; the styles it needs
   * for the measurement are restored right after.
   */
  private measureHostContent(content: string, width: number | null): { width: number; height: number } {
    const key = `${content}\u0000${width ?? ''}`;
    const cached = this.hostMeasures.get(key);
    if (cached) return cached;
    const el = hostContent(content);
    if (!el) return { width: 0, height: 0 };
    const { style } = el;
    const saved = { width: style.width, height: style.height, maxWidth: style.maxWidth };
    style.height = 'auto';
    if (width === null) {
      style.width = 'max-content';
      style.maxWidth = 'none';
    } else {
      style.width = `${width}px`;
    }
    const rect = el.getBoundingClientRect();
    const size = { width: Math.ceil(rect.width), height: Math.ceil(rect.height) };
    style.width = saved.width;
    style.height = saved.height;
    style.maxWidth = saved.maxWidth;
    this.hostMeasures.set(key, size);
    return size;
  }

  /** Apply a batch of ops in order. Throws `RendererError` on an invalid op. */
  applyOps(ops: readonly Op[]): void {
    // The scroll views' offsets, read while nothing is waiting to be laid
    // out: the pass's syncs use them (scrollSync.ts) instead of reading
    // after its writes.
    if (this.applying === 0 && ops.length > 0) readScrollOffsets();
    this.applying += 1;
    try {
      this.applyAll(ops);
    } finally {
      this.applying -= 1;
      if (this.applying === 0) discardScrollOffsets();
    }
    // Geometry events go out after the whole batch (one batch per layout
    // pass); Swift's answer re-enters `applyOps` with the next pass.
    if (this.applying === 0) this.flushGeometry();
  }

  private applyAll(ops: readonly Op[]): void {
    for (let index = 0; index < ops.length; index++) {
      const op = ops[index]!;
      if (this.passAnimates === null) {
        this.passAnimates = this.passCanAnimate(ops, index);
        if (this.passAnimates) this.prereadPass(ops, index);
      }
      switch (op.op) {
        case 'create':
          this.create(op.id, op.kind, op.props, op);
          break;
        case 'update':
          this.update(op.id, op.props, op);
          break;
        case 'insert':
          this.insert(op.parent, op.id, op.index, op);
          break;
        case 'remove':
          this.remove(op.id, op);
          break;
        case 'defaults':
        case 'request':
        case 'swiftdata':
          // Consumed by the loader (wasm.ts); nothing to render.
          break;
        case 'imagedata':
          // A bitmap's source, kept for the `image` elements that draw it (src/bitmaps.ts).
          bitmapStore.add(op);
          break;
        case 'commit': {
          const pass = this.pass;
          this.pass = emptyPass();
          this.passAnimates = null;
          this.passRootRect = null;
          this.preSnapshots.clear();
          this.preRects.clear();
          // Before the layout that first holds this pass's new screens: a
          // history entry pushed here (browserHistory.ts) is sized by Safari
          // from the page's last layout, without them.
          this.root.dispatchEvent(new CustomEvent('sb:precommit', { detail: { commits: this.commits + 1 } }));
          const changes = this.layout();
          const completion = op.completion;
          this.animator.commitNextFrame(pass, op.animation, changes, typeof completion === 'number' ? (started) => this.completeAnimation(completion, started) : undefined);
          this.beginBarMorphsNextFrame();
          this.commits += 1;
          const focusedBefore = this.focusedFieldId();
          this.syncFocus();
          // The focus the app moved shows or hides a screen's keyboard bar.
          if (this.focusedFieldId() !== focusedBefore && this.hasKeyboardToolbars()) this.layout();
          this.castShadows();
          this.performScrolls(op.animation != null);
          this.performOffsetScrolls(op.animation != null);
          this.checkLazySpacers();
          this.openPendingMenu();
          this.root.dispatchEvent(
            new CustomEvent('sb:commit', { detail: { commits: this.commits, animation: op.animation ?? null } }),
          );
          break;
        }
        default: {
          const unknown: never = op;
          throw new RendererError(`Unknown op ${JSON.stringify(unknown)}`);
        }
      }
    }
  }

  /**
   * SwiftUI's `.shadow` on a container falls on each view it draws (its texts, shapes, images, controls and
   * backgrounds), on their own shadows too, and not on the container as a whole unless a `.compositingGroup()` in
   * between draws the views as one. Each box with a shadow (`data-sb-shadow`) hands its views the shadows to cast:
   * its own, then those of the boxes around it, up to a compositing group (`--sb-shadow-filter`, which styles.css
   * gives each drawn view's `filter`, and which a box that clips, masks or composites takes for itself). A box's own
   * background, painted with its children inside, casts them as `box-shadow` instead.
   */
  private castShadows(): void {
    const sources = this.root.querySelectorAll<HTMLElement>('[data-sb-shadow]');
    const cast = new Set(this.root.querySelectorAll<HTMLElement>('[data-sb-shadow-cast]'));
    if (sources.length === 0 && cast.size === 0) return;
    const filters = new Map<HTMLElement, { filter: string; box: string }>();
    // The shadows that reach the views inside `el` from the boxes around it, up to a compositing group or a drawn
    // view, which takes those around it as one and hands none on.
    const above = (el: HTMLElement): { filter: string; box: string } => {
      for (let up = el.parentElement; up && up !== this.root; up = up.parentElement) {
        if (up.dataset['sbComposite'] !== undefined || DRAWN_KINDS.has(up.dataset[KIND_ATTR] ?? '')) break;
        const found = filters.get(up);
        if (found) return found;
      }
      return { filter: '', box: '' };
    };
    // Document order: a box comes before the boxes inside it.
    for (const el of sources) {
      const outer = above(el);
      const filter = `${el.dataset['sbShadow']} ${outer.filter}`.trim();
      const box = [el.dataset['sbShadowBox'], outer.box].filter(Boolean).join(', ');
      filters.set(el, { filter, box });
      if (el.style.getPropertyValue('--sb-shadow-filter') !== filter) el.style.setProperty('--sb-shadow-filter', filter);
    }
    // A background painted on a box (its children inside) casts them as a box-shadow.
    if (sources.length > 0) {
      // A box that clips, masks or composites casts them for its background already, with what it holds.
      for (const el of this.root.querySelectorAll<HTMLElement>('.sb-styled[data-sb-background]:not([data-sb-as-one])')) {
        const { box } = filters.get(el) ?? above(el);
        if (!box) continue;
        if (el.style.boxShadow !== box) el.style.boxShadow = box;
        el.dataset['sbShadowCast'] = '';
        cast.delete(el);
      }
    }
    // Those no shadow reaches any more.
    for (const el of cast) {
      el.style.boxShadow = '';
      delete el.dataset['sbShadowCast'];
    }
  }

  /**
   * `withAnimation(_:completionCriteria:_:completion:)`: the pass's `commit`
   * carried a completion serial; once the animations its commit started have
   * ended (at once when it started none) the app hears `animationcomplete`.
   * An animation that never ends (`repeatForever`) counts as ended after one
   * run of its duration, as `.logicallyComplete` does.
   */
  private completeAnimation(serial: number, started: readonly globalThis.Animation[]): void {
    const ended = started.map((animation) => {
      const timing = animation.effect?.getComputedTiming();
      const end = timing?.endTime;
      if (typeof end === 'number' && Number.isFinite(end)) return animation.finished.then(() => undefined, () => undefined);
      const run = (Number(timing?.delay) || 0) + (Number(timing?.duration) || 0);
      return new Promise<void>((resolve) => window.setTimeout(resolve, run));
    });
    const send = (): void => this.onEvent({ type: 'animationcomplete', serial });
    if (ended.length === 0) send();
    else void Promise.all(ended).then(send, send);
  }

  /**
   * Whether the pass starting at `ops[start]` can animate: its `commit` has
   * an animation, or one of its updates changes an `animationToken`
   * (`.animation(_:value:)`). Otherwise the Animator only retargets running
   * frame tweens, which needs nothing recorded before the changes. A pass
   * whose `commit` is not in this batch is assumed to animate.
   */
  private passCanAnimate(ops: readonly Op[], start: number): boolean {
    for (let index = start; index < ops.length; index++) {
      const op = ops[index]!;
      if (op.op === 'commit') return op.animation != null;
      if (op.op !== 'update') continue;
      const el = this.elements.get(op.id);
      const token = animationTokenOf(el?.dataset[KIND_ATTR] as ElementKind | undefined, op.props);
      if (token == null) continue;
      if (!el || el.dataset[ANIMATION_TOKEN] !== token) return true;
    }
    return true;
  }

  /**
   * A covered screen is about to show without a pop (a back swipe): the
   * engine leaves covered screens as they were, so lay this one out now if
   * the stack resized or its content changed while it was covered.
   */
  private revealScreen(screen: HTMLElement): void {
    const id = Number(screen.dataset[ID_ATTR]);
    const node = Number.isInteger(id) ? this.nodes.get(id) : undefined;
    if (!node || !this.engine) return;
    this.engine.uncover(node);
    this.layout();
  }

  /**
   * Before a pass that can animate writes anything: snapshot the styles of
   * the elements its updates will change and read the rectangles of those
   * its removes will take out, so the reads share the one clean layout
   * instead of each forcing a new one between the writes.
   */
  private prereadPass(ops: readonly Op[], start: number): void {
    for (let index = start; index < ops.length; index++) {
      const op = ops[index]!;
      if (op.op === 'commit') return;
      if (op.op === 'update') {
        const el = this.elements.get(op.id);
        if (el && !this.preSnapshots.has(el)) this.preSnapshots.set(el, this.animator.snapshot(el));
      } else if (op.op === 'remove') {
        const el = this.elements.get(op.id);
        if (!el || !el.parentElement || this.preRects.has(el)) continue;
        this.passRootRect ??= this.animator.rootRect();
        const matched: { id: string; el: HTMLElement; rect: DOMRect }[] = [];
        const candidates = el.dataset['sbMatched'] !== undefined ? [el] : [];
        candidates.push(...el.querySelectorAll<HTMLElement>('[data-sb-matched]'));
        for (const box of candidates) {
          const boxRect = this.animator.rectOf(box, this.passRootRect);
          if (boxRect) matched.push({ id: box.dataset['sbMatched']!, el: box, rect: boxRect });
        }
        this.preRects.set(el, { rect: this.animator.rectOf(el, this.passRootRect), matched });
      }
    }
  }

  /** Detach everything under the root and forget all non-root elements. */
  reset(): void {
    stopVideos(this.root);
    for (const morph of Array.from(this.barMorphs.values())) morph.dispose();
    this.pendingBarMorphs = [];
    this.root.replaceChildren();
    this.elements.clear();
    this.elements.set(ROOT_ID, this.root);
    this.nodes.clear();
    this.nodes.set(ROOT_ID, { id: ROOT_ID, kind: 'root', props: {}, children: [] });
    this.parents.clear();
    this.boxes.clear();
    this.dirty.clear();
    this.layoutResult = null;
    this.engine = null;
    this.pass = emptyPass();
    this.passAnimates = null;
    this.passRootRect = null;
    this.preSnapshots.clear();
    this.preRects.clear();
    this.closeMenu();
    this.reportedGeometry.clear();
    this.geometryReporters.clear();
    this.affinePaths.clear();
    this.layoutReporter.clear();
    this.pendingGeometry = [];
    refreshSheets(this.root);
  }

  // -------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------

  /**
   * Set the device environment (Dynamic Type size, color scheme, platform)
   * and re-lay the tree out. The page calls this on boot and whenever a
   * control changes. The platform is also on the document
   * (`html[data-sb-platform]`), which the stylesheet themes by.
   */
  setEnvironment(environment: Partial<RendererEnvironment>): void {
    const next = { ...this.environment, ...environment };
    if (next.dynamicTypeSize === this.environment.dynamicTypeSize && next.colorScheme === this.environment.colorScheme && next.platform === this.environment.platform) {
      return;
    }
    this.environment = next;
    this.root.ownerDocument.documentElement.dataset['sbPlatform'] = next.platform;
    if (this.commits > 0) this.layout();
  }

  /** The platform drawn (`setEnvironment`). */
  get platform(): Platform {
    return this.environment.platform;
  }

  /**
   * Phase 5: give the renderer the app's asset catalog manifest (src/assets.ts).
   * Named images take their intrinsic size and `src` from it, and every
   * catalog color becomes a `--sb-asset-color-<slug>` custom property
   * declared for `.device[data-theme="light"]` and `.device[data-theme="dark"]`
   * in one `<style>` element, so named colors follow the theme through plain
   * CSS (`var()`) and need no re-render when it changes. Images already in
   * the tree are re-resolved; the tree is laid out again.
   */
  setAssetCatalog(catalog: AssetCatalog | null): void {
    this.assets = catalog ?? EMPTY_CATALOG;
    const doc = this.root.ownerDocument;
    const css = assetColorsCSS(this.assets);
    if (css) {
      if (!this.assetStyle) {
        this.assetStyle = doc.createElement('style');
        this.assetStyle.dataset['sbAssetColors'] = '';
        (doc.head ?? doc.documentElement).append(this.assetStyle);
      }
      this.assetStyle.textContent = css;
    } else if (this.assetStyle) {
      this.assetStyle.remove();
      this.assetStyle = null;
    }
    for (const [id, node] of this.nodes) {
      if (node.kind !== 'image') continue;
      const el = this.elements.get(id);
      if (el) applyImage(el, node.props as unknown as ImageProps, this.assets);
    }
    // Catalog sizes feed image measurement: start the layout pass over.
    this.engine = null;
    if (this.commits > 0) this.layout();
  }

  /** The asset catalog in use (empty when the app has none). */
  get assetCatalog(): AssetCatalog {
    return this.assets;
  }

  /**
   * Use explicit safe-area insets (device mode: the real `env(safe-area-inset-*)`
   * values) instead of the frame's `--device-safe-top/bottom` tokens; `null`
   * goes back to the tokens. Re-lays the tree out when the insets changed.
   */
  setSafeArea(insets: LayoutEnvironment['safeArea'] | null): void {
    const next = insets ? { ...insets } : readSafeArea(this.root);
    const current = this.safeArea;
    if (
      current &&
      current.top === next.top &&
      current.leading === next.leading &&
      current.bottom === next.bottom &&
      current.trailing === next.trailing
    ) {
      return;
    }
    this.safeArea = next;
    if (this.commits > 0) this.layout();
  }

  /**
   * Device mode: the on-screen keyboard covers `height` CSS px of the
   * screen's bottom (0: it is down). The engine treats it as a bottom
   * safe-area inset, as iOS does: content stops above it, a bar under it
   * stays put. Re-lays the tree out when it changed.
   */
  setKeyboard(height: number): void {
    const next = Number.isFinite(height) && height > 0 ? Math.round(height) : 0;
    if (next === this.keyboard) return;
    const rose = next > this.keyboard;
    this.keyboard = next;
    if (this.commits === 0) return;
    this.layout();
    // The keyboard covers more: the text input being edited comes out from under it, as iOS keeps it above the keyboard.
    const active = document.activeElement;
    if (rose && active instanceof HTMLElement && this.root.contains(active) && (active.classList.contains('sb-textfield') || active.classList.contains('sb-texteditor'))) {
      this.revealFocused(active);
    }
  }

  /**
   * The size `text` in `fontProps` (a Font JSON object over the body font)
   * takes wrapped to `width` (one line when null), with the typography the
   * layout engine lays `text` elements out with: for an app that paginates
   * or sizes from its text (@swiftbrowser/react's `measureText`).
   */
  measureText(text: string, fontProps: unknown, width: number | null): { width: number; height: number; lines: number } {
    const env = this.layoutEnvironment();
    const font = resolveFont(fontProps, defaultFont(env), env);
    const wrapped = wrapText(text, font, width, null, this.measurer);
    return { width: wrapped.width, height: textHeight(wrapped.lines.length, font), lines: wrapped.lines.length };
  }

  /**
   * The root's size changed (device mode: a resize or rotation): re-read it
   * and re-lay out now rather than when the ResizeObserver reports it.
   */
  viewportChanged(): void {
    this.rootSize = null;
    if (this.commits > 0) this.layout();
  }

  /** The environment the engine lays out for. */
  layoutEnvironment(): LayoutEnvironment {
    const screen = this.resizeObserver ? (this.rootSize ??= this.readRootSize()) : this.readRootSize();
    if (!this.safeArea) this.safeArea = readSafeArea(this.root);
    const focused = this.focusedFieldId();
    return {
      screen: { ...screen },
      safeArea: this.safeArea,
      dynamicTypeSize: this.environment.dynamicTypeSize,
      devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
      colorScheme: this.environment.colorScheme,
      platform: this.environment.platform,
      assets: this.assets,
      ...(this.contentHeight ? { contentHeight: true } : {}),
      ...(this.keyboard > 0 ? { keyboard: this.keyboard } : {}),
      ...(focused !== null ? { focusedField: focused } : {}),
      ...(Object.keys(this.sidebarWidths).length > 0 ? { sidebarWidths: { ...this.sidebarWidths } } : {}),
    };
  }

  /** The element id of the text input being edited in the root (a field, an editor or a search field's input), or null. */
  private focusedFieldId(): number | null {
    const active = typeof document === 'undefined' ? null : document.activeElement;
    if (!(active instanceof HTMLElement) || !this.root.contains(active)) return null;
    const owner = active.classList.contains('sb-searchfield-input') ? active.closest<HTMLElement>('.sb-searchfield') : active;
    if (!owner || !(owner.classList.contains('sb-textfield') || owner.classList.contains('sb-texteditor') || owner.classList.contains('sb-searchfield'))) return null;
    const id = Number(owner.dataset[ID_ATTR]);
    return Number.isInteger(id) ? id : null;
  }

  /** Whether a screen has `keyboard` toolbars: their bar follows the focus, so a focus change lays out again. */
  private hasKeyboardToolbars(): boolean {
    return this.root.querySelector('.sb-toolbar[data-sb-placement="keyboard"]') !== null;
  }

  private readRootSize(): { width: number; height: number } {
    return {
      width: this.root.clientWidth || DEFAULT_SCREEN.width,
      height: this.root.clientHeight || DEFAULT_SCREEN.height,
    };
  }

  /** Called after each layout pass (main.ts keeps the browser chrome's color in step). */
  afterLayout: (() => void) | null = null;

  /**
   * Sends a tap for an element, as a click on it would: a `navscreen` pops
   * it (the back button's tap), a `sheet` asks to be dismissed (the scrim's).
   * For the page's own controls, such as the browser's history (browserHistory.ts).
   */
  tap(id: ElementId): void {
    this.onEvent({ type: 'tap', id });
  }

  /**
   * Screens the browser's own history move pops (`popWithoutAnimation`):
   * the browser has animated the pop already, so the renderer does not.
   */
  private instantPops = new Set<ElementId>();

  /**
   * iOS 26: one bar through a push or pop (src/barMorph.ts). The morph in
   * flight on each stack, and the pushes and pops this pass started, whose
   * morphs begin once the commit's layout has placed the screens.
   */
  private readonly barMorphs = new Map<HTMLElement, BarMorph>();
  private pendingBarMorphs: { stack: HTMLElement; from: HTMLElement; to: HTMLElement }[] = [];

  /** Begins the morph of the bar of `from` into the bar of `to` on `stack`, in place of any in flight there. */
  private beginBarMorph(stack: HTMLElement, from: HTMLElement, to: HTMLElement, interactive: boolean): BarMorph | null {
    this.barMorphs.get(stack)?.dispose();
    if (!from.isConnected || !to.isConnected) return null;
    // (a push or pop that another in the same pass overtook: the later one's morph)
    if (!interactive && to.dataset['sbNavPosition'] !== 'top') return null;
    const morph = BarMorph.begin(stack, from, to, {
      duration: NAV_TRANSITION_MS,
      interactive,
      onDone: () => {
        if (this.barMorphs.get(stack) === morph) this.barMorphs.delete(stack);
      },
    });
    if (morph) this.barMorphs.set(stack, morph);
    return morph;
  }

  /** The morph a swipe back of `screen` drives, if one is in flight. */
  private swipeBackMorphOf(screen: HTMLElement): BarMorph | null {
    const stack = screen.closest<HTMLElement>('.sb-navstack');
    const morph = stack ? this.barMorphs.get(stack) : undefined;
    return morph && morph.from === screen ? morph : null;
  }

  /**
   * Begins the morphs of this pass's pushes and pops in the frame their
   * slides start, after the Animator's commit (`commitNextFrame`), from where
   * the layout has put the bars' parts; the last push or pop of a stack in
   * the pass is the one shown.
   */
  private beginBarMorphsNextFrame(): void {
    if (this.pendingBarMorphs.length === 0) return;
    const pending = new Map(this.pendingBarMorphs.map((entry) => [entry.stack, entry]));
    this.pendingBarMorphs = [];
    const begin = (): void => {
      for (const { stack, from, to } of pending.values()) this.beginBarMorph(stack, from, to, false);
    };
    if (typeof requestAnimationFrame !== 'function' || (typeof document !== 'undefined' && document.hidden)) begin();
    else requestAnimationFrame(begin);
  }

  /**
   * The next removal of screen `id` is a pop the browser has already shown
   * (its back swipe or button, `browserHistory.ts`): the screen goes without
   * the slide, and the screen it uncovers takes its place at once rather
   * than sliding back, so the pop does not play a second time.
   */
  popWithoutAnimation(id: ElementId): void {
    this.instantPops.add(id);
    // A pop the app refused leaves nothing to skip for long.
    window.setTimeout(() => this.instantPops.delete(id), NAV_TRANSITION_MS * 5);
  }

  /** The repaint keep-alive (`keepRepainting`): its box, timer and deadline while it runs. */
  private repaintKeepAlive: { el: HTMLElement; timer: number; until: number; on: boolean } | null = null;

  /**
   * Keeps the page painting for `duration` ms: a 1 px box in the root
   * alternates between two near-identical colors every few frames, so each
   * frame carries a real repaint. Safari's back and forward swipe shows a
   * snapshot of the entry it goes to and, once its own animation ends, hides
   * the live page until the page commits a new frame; a pop that has already
   * finished animating by then leaves nothing to commit, and Safari keeps the
   * snapshot (a still image, under which the live page takes the touches)
   * until a 3 s watchdog; and a page that stops painting before that
   * watchdog has a touch in progress interrupted when it fires, so the
   * painting runs through it. Called from browserHistory.ts for every
   * history navigation the browser makes; a call while one runs extends it.
   */
  keepRepainting(duration = REPAINT_KEEPALIVE_MS): void {
    const until = performance.now() + duration;
    if (this.repaintKeepAlive) {
      this.repaintKeepAlive.until = Math.max(this.repaintKeepAlive.until, until);
      return;
    }
    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;pointer-events:none;z-index:2147483647;background:rgba(127,127,127,0.02)';
    this.root.append(el);
    const state = { el, timer: 0, until, on: false };
    this.repaintKeepAlive = state;
    state.timer = window.setInterval(() => {
      if (performance.now() >= state.until) {
        this.stopRepainting();
        return;
      }
      state.on = !state.on;
      el.style.background = state.on ? 'rgba(127,127,127,0.03)' : 'rgba(127,127,127,0.02)';
    }, REPAINT_KEEPALIVE_INTERVAL_MS);
  }

  private stopRepainting(): void {
    const state = this.repaintKeepAlive;
    if (!state) return;
    this.repaintKeepAlive = null;
    window.clearInterval(state.timer);
    state.el.remove();
  }

  /**
   * Run the layout engine over the current tree and position every element.
   * Returns the elements whose box changed. Runs at every `commit`; call it
   * directly after something the engine cannot see changed (fonts loaded,
   * device size).
   */
  layout(): BoxChange[] {
    const root = this.nodes.get(ROOT_ID);
    if (!root) return [];
    this.hostMeasures.clear();
    const env = this.layoutEnvironment();
    const envKey = JSON.stringify({ ...env, assets: undefined });
    if (!this.engine || envKey !== this.engineEnvKey) {
      this.engine = new LayoutPass(env, this.measurer, (node) => {
        const parentId = this.parents.get(node.id);
        return parentId === undefined ? null : (this.nodes.get(parentId) ?? null);
      });
      this.engineEnvKey = envKey;
    }
    const result = this.engine.run(root.children);
    this.layoutResult = result;
    this.layouts += 1;
    const ctx = {
      root: this.root,
      elements: this.elements,
      nodes: this.nodes,
      parents: this.parents,
      boxes: this.boxes,
      dirty: this.dirty,
      env,
    };
    const changes = applyLayout(ctx, result);
    this.boxes = ctx.boxes;
    // `.defaultScrollAnchor`: scroll views that keep their end (or another point) in view as they grow.
    keepScrollAnchors();
    if (this.contentHeight) {
      // The island's root is as tall as its content.
      let bottom = 0;
      for (const child of root.children) {
        const frame = result.frames.get(child.id);
        if (frame) bottom = Math.max(bottom, frame.y + frame.height);
      }
      this.root.style.height = `${bottom + env.safeArea.bottom}px`;
    }
    drawScrollTransitions();
    drawPinnedViews();
    for (const id of this.affinePaths) {
      const frame = result.frames.get(id);
      const el = this.elements.get(id);
      if (frame && el) layOutAffinePath(el, frame.width, frame.height);
    }
    this.collectGeometry(result);
    this.collectScrollGeometry(result);
    this.collectLayouts(result);
    if (this.applying === 0) this.flushGeometry();
    this.queueVisibilityCheck();
    // `.statusBarHidden` on a screen that shows: the frame's drawn status bar fades (styles.css).
    const statusHidden = this.root.querySelector('.sb-navscreen[data-sb-status-hidden]:not([hidden])') !== null;
    if (document.documentElement.hasAttribute('data-sb-status-hidden') !== statusHidden) {
      document.documentElement.toggleAttribute('data-sb-status-hidden', statusHidden);
    }
    this.afterLayout?.();
    return changes;
  }

  /**
   * Queue a `geometry` event for every `geometry` element (and every element
   * whose props set `reportsSize`, an anchor preference's) whose laid-out size
   * (whole px) differs from the one last reported. Nothing is queued for an
   * unchanged size, which is what lets the Swift re-render loop converge.
   */
  private collectGeometry(result: LayoutResult, framesOnly = false): void {
    let rootRect: DOMRect | null = null;
    for (const id of this.geometryReporters) {
      const node = this.nodes.get(id);
      if (!node) continue;
      const reportsFrame = node.props['reportsFrame'] === true;
      if (framesOnly && !reportsFrame) continue;
      const frame = result.frames.get(id);
      if (!frame) continue; // e.g. inside a hidden tab
      const width = Math.round(frame.width);
      const height = Math.round(frame.height);
      const event: GeometryEvent = { type: 'geometry', id, width, height };
      let key = `${width}x${height}`;
      if (reportsFrame) {
        // Where it is drawn now (scrolled), in the root's coordinates.
        const el = this.elements.get(id);
        rootRect ??= this.root.getBoundingClientRect();
        const rect = el?.getBoundingClientRect();
        const x = rect ? Math.round(rect.left - rootRect.left) : Math.round(frame.x);
        const y = rect ? Math.round(rect.top - rootRect.top) : Math.round(frame.y);
        const env = this.layoutEnvironment();
        event.x = x;
        event.y = y;
        event.safeArea = safeAreaOverlap({ x, y, width, height }, env.screen, env.safeArea);
        key += `@${x},${y}/${event.safeArea.top},${event.safeArea.leading},${event.safeArea.bottom},${event.safeArea.trailing}`;
        // The visible rectangle of the scroll view (or list) the reader is in.
        const scroller = el?.parentElement?.closest<HTMLElement>('.sb-scrollview, .sb-list');
        if (scroller) {
          const view = scroller.getBoundingClientRect();
          event.scrollView = {
            x: Math.round(view.left - rootRect.left),
            y: Math.round(view.top - rootRect.top),
            width: Math.round(view.width),
            height: Math.round(view.height),
            axis: scroller.dataset['sbAxes'] === 'horizontal' ? 'horizontal' : 'vertical',
          };
          const v = event.scrollView;
          key += `/${v.x},${v.y},${v.width},${v.height},${v.axis}`;
        }
      }
      const last = this.reportedGeometry.get(id);
      if (last && last.key === key) continue;
      this.reportedGeometry.set(id, { width, height, key });
      this.pendingGeometry.push(event);
    }
  }

  /**
   * Phase 6: queue a `layout` event for every `layout` element whose record
   * (the proposal it was placed with and its children's min / ideal / max)
   * differs from the last one sent. Swift's answer updates the element's
   * props, which lays out again; an unchanged record sends nothing, so the
   * loop converges.
   */
  private collectLayouts(result: LayoutResult): void {
    for (const event of this.layoutReporter.collect(result)) this.pendingGeometry.push(event);
  }

  /** Phase 6: the last `layout` event sent for every `layout` element (for tests). */
  get reportedLayouts(): ReadonlyMap<ElementId, LayoutEvent> {
    return this.layoutReporter.reported;
  }

  private flushGeometry(): void {
    if (this.pendingGeometry.length === 0) return;
    const events = this.pendingGeometry;
    this.pendingGeometry = [];
    // One `batch` per pass: Swift renders once for all of them.
    if (events.length === 1) this.onEvent(events[0]!);
    else this.onEvent({ type: 'batch', events });
  }

  // -------------------------------------------------------------------------
  // Popover menus (a `menu` picker's options, or a Phase 6 `menu`'s items)
  // -------------------------------------------------------------------------

  /** The rows of a `menu` picker's popover: one radio row per option. */
  private pickerRows(id: ElementId): MenuRow[] {
    const node = this.nodes.get(id);
    const props = (node?.props ?? {}) as Partial<PickerProps>;
    const options = Array.isArray(props.options) ? props.options.map(String) : [];
    const selected = clampIndex(props.selected, options.length);
    return options.map((title, index) => ({ kind: 'select', id, index, title, checked: index === selected }));
  }

  /**
   * The rows of a `menu` element's popover, built from its current items
   * (every child but the label) so conditional items are reflected: buttons
   * and navlinks become tap rows, pickers a checked section, dividers and
   * sections separators and captions, texts disabled captions.
   */
  /**
   * A navscreen's More menu: its `secondary` toolbar items the engine
   * collapsed (those it gave no frame), in order. As the overflow menu
   * (`»`, chrome `overflow`) of a Mac detail column beside a sidebar too
   * narrow for its own bar items, those items first.
   */
  private collapsedSecondaryRows(id: ElementId): MenuRow[] {
    const screen = this.nodes.get(id);
    if (!screen) return [];
    const frames = this.layoutResult?.frames;
    const unframed = (node: LayoutNode, placements: (placement: unknown) => boolean): LayoutNode[] =>
      node.children
        .filter((child) => child.kind === 'toolbar' && placements(child.props['placement']))
        .flatMap((toolbar) => toolbar.children)
        .filter((item) => !frames?.has(item.id));
    const overflowed = this.layoutResult?.chrome.get(id)?.['overflow'] !== undefined ? this.sidebarScreens(id).flatMap((side) => unframed(side, (p) => p === 'leading' || p === 'trailing' || p === undefined)) : [];
    return collectMenuRows([...overflowed, ...unframed(screen, (p) => p === 'secondary')]);
  }

  /** The screens of the sidebar beside the detail column `id` is in (its split view's first column), top first. */
  private sidebarScreens(id: ElementId): LayoutNode[] {
    let current: ElementId | undefined = id;
    let child: ElementId | undefined;
    while (current !== undefined && this.nodes.get(current)?.kind !== 'navsplit') {
      child = current;
      current = this.parents.get(current);
    }
    const split = current === undefined ? undefined : this.nodes.get(current);
    const sidebar = split?.children[0];
    if (!split || !sidebar || sidebar.id === child) return [];
    const screens: LayoutNode[] = [];
    const visit = (node: LayoutNode): void => {
      if (node.kind === 'navscreen') screens.push(node);
      else node.children.forEach(visit);
    };
    visit(sidebar);
    return screens.reverse();
  }

  private menuRows(id: ElementId): MenuRow[] {
    const node = this.nodes.get(id);
    return node ? collectMenuRows(node.children.slice(1)) : [];
  }

  /** Open a popover of `rows` under (or above) `anchor`, which is element `id`. */
  private openMenu(anchor: HTMLElement, id: ElementId, rows: MenuRow[]): void {
    this.closeMenu();
    const menu = div('sb-picker-menu');
    menu.setAttribute('role', 'menu');
    menu.dataset['sbFor'] = String(id);
    // No hairline at either end (a section first or last).
    while (rows[0]?.kind === 'separator') rows = rows.slice(1);
    while (rows[rows.length - 1]?.kind === 'separator') rows = rows.slice(0, -1);
    const checks = rows.some((row) => row.kind === 'select' || row.kind === 'toggle');
    // iOS indents the whole menu around its check column: section titles start with the plain titles.
    if (checks) menu.dataset['sbChecks'] = '';
    let height = 2 * MENU_PADDING;
    for (const row of rows) {
      menu.append(menuRowElement(row, checks));
      height +=
        row.kind === 'caption'
          ? MENU_CAPTION_HEIGHT
          : row.kind === 'separator'
            ? MENU_SEPARATOR_HEIGHT
            : (row.kind === 'tap' || row.kind === 'toggle') && row.subtitle
              ? MENU_SUBTITLED_ROW
              : PICKER_MENU_ROW;
    }
    // Anchor to the control: trailing edges aligned, below it when there is
    // room, else above; clamped inside the screen. A menu taller than the
    // room on either side (a picker of every hour) takes the roomier side,
    // as tall as that room, and scrolls inside, opened at its checked row.
    const rootRect = this.root.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const width = Math.min(PICKER_MENU_WIDTH, Math.max(0, rootRect.width - 2 * PICKER_MENU_MARGIN));
    let left = anchorRect.right - rootRect.left - width;
    left = Math.max(PICKER_MENU_MARGIN, Math.min(left, rootRect.width - width - PICKER_MENU_MARGIN));
    // A toolbar button's menu grows out of the button and covers it (iOS 26);
    // any other control's opens just under it.
    const inToolbar = anchor.closest('.sb-toolbar') !== null;
    const belowTop = inToolbar ? anchorRect.top - rootRect.top : anchorRect.bottom - rootRect.top + PICKER_MENU_GAP;
    const roomBelow = rootRect.height - PICKER_MENU_MARGIN - belowTop;
    const roomAbove = anchorRect.top - rootRect.top - PICKER_MENU_GAP - PICKER_MENU_MARGIN;
    const below = height <= roomBelow || (height > roomAbove && roomBelow >= roomAbove);
    const shown = Math.min(height, Math.max(0, below ? roomBelow : roomAbove));
    const top = below ? belowTop : anchorRect.top - rootRect.top - PICKER_MENU_GAP - shown;
    menu.dataset['sbPlacement'] = below ? 'below' : 'above';
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(Math.max(PICKER_MENU_MARGIN, top))}px`;
    menu.style.width = `${Math.round(width)}px`;
    if (shown < height) menu.style.maxHeight = `${Math.round(shown)}px`;
    this.root.append(menu);
    if (shown < height) menu.querySelector('[aria-checked="true"]')?.scrollIntoView({ block: 'nearest' });
    anchor.setAttribute('aria-expanded', 'true');
    this.menu = { el: menu, anchor, anchorId: id };
    document.addEventListener('click', this.onDocumentClick, true);
  }

  /**
   * Phase 8: open the popover of a `contextmenu` element (a long press or a
   * right click on its content), its items (every child but the content)
   * listed like a `menu`'s. Choosing one sends `tap` with the item's id.
   */
  openContextMenu(anchor: HTMLElement, id: ElementId): void {
    if (this.menu?.anchorId === id) return;
    if (!this.nodes.has(id) || !this.root.contains(anchor)) return;
    this.openMenuWhenBuilt(anchor, id, 'contextmenu');
  }

  /**
   * Lazy menus: a `deferred` menu asks Swift for its items (`menu` event,
   * `open: true`) and opens once they are there, which is usually as soon as
   * the event returns (the app answers with the ops at once), else at the
   * commit that brings them.
   */
  private openMenuWhenBuilt(anchor: HTMLElement, id: ElementId, kind: 'menu' | 'contextmenu'): void {
    const node = this.nodes.get(id);
    if (!node) return;
    if (node.props['deferred'] !== true) {
      this.showMenu(anchor, id, kind);
      return;
    }
    this.closeMenu();
    this.pendingMenu = { anchor, id, kind };
    this.onEvent({ type: 'menu', id, open: true });
    this.openPendingMenu();
  }

  private showMenu(anchor: HTMLElement, id: ElementId, kind: 'menu' | 'contextmenu'): void {
    const node = this.nodes.get(id);
    if (!node) return;
    this.openMenu(anchor, id, kind === 'menu' ? this.menuRows(id) : collectMenuRows(node.children.slice(1)));
  }

  /** Opens the menu waiting for its items once they arrived (dropped when its element left). */
  private openPendingMenu(): void {
    const pending = this.pendingMenu;
    if (!pending) return;
    const node = this.nodes.get(pending.id);
    if (!node || !pending.anchor.isConnected) {
      this.pendingMenu = null;
      return;
    }
    if (node.props['deferred'] === true) return;
    this.pendingMenu = null;
    this.showMenu(pending.anchor, pending.id, pending.kind);
  }

  /** Close the open popover (no-op when none is open). */
  closeMenu(): void {
    const menu = this.menu;
    if (!menu) return;
    this.menu = null;
    document.removeEventListener('click', this.onDocumentClick, true);
    dismissMenu(menu.el);
    menu.anchor.setAttribute('aria-expanded', 'false');
    // Lazy menus: Swift drops the items. After the current task, so that the
    // tap of the item chosen (sent right after closing) reaches its button.
    const id = menu.anchorId;
    if (this.nodes.get(id)?.props['deferred'] === false) {
      queueMicrotask(() => {
        if (this.nodes.get(id)?.props['deferred'] === false && this.menu?.anchorId !== id) {
          this.onEvent({ type: 'menu', id, open: false });
        }
      });
    }
  }

  /** The open popover element (for tests), or null. */
  get openMenuElement(): HTMLElement | null {
    return this.menu?.el ?? null;
  }

  /** Drop cached text widths (after web fonts load) and lay out again. */
  fontsChanged(): void {
    this.measurer.clearCache();
    this.engine = null;
    if (this.commits > 0) this.layout();
  }

  dispose(): void {
    this.stopRepainting();
    this.closeMenu();
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('pointerdown', this.onRowPress);
    this.root.removeEventListener('mousedown', this.onControlMouseDown);
    this.root.removeEventListener('keydown', this.onKeyDown);
    this.root.removeEventListener('beforeinput', this.onBeforeInput);
    this.root.removeEventListener('input', this.onInput);
    this.root.removeEventListener('focusin', this.onFocusChange);
    this.root.removeEventListener('focusout', this.onFocusChange);
    this.root.removeEventListener('scroll', this.onScrollDismissKeyboard, true);
    this.root.removeEventListener('scroll', this.queueVisibilityCheck, true);
    this.root.removeEventListener('scroll', this.queueFrameCheck, true);
    this.root.removeEventListener('scroll', drawScrollTransitions, true);
    this.root.removeEventListener('scroll', drawPinnedViews, true);
    this.root.removeEventListener('scroll', this.queueScrollGeometryCheck, true);
    this.root.removeEventListener('scroll', this.onPositionScroll, true);
    this.root.removeEventListener('scrollend', this.onPositionScrollEnd, true);
    this.root.removeEventListener('touchstart', this.onPositionTouchStart, true);
    this.root.removeEventListener('touchend', this.onPositionTouchEnd, true);
    this.root.removeEventListener('touchcancel', this.onPositionTouchEnd, true);
    for (const timer of this.positionTimers.values()) window.clearTimeout(timer);
    this.positionTimers.clear();
    this.interaction.dispose();
    this.pressEffects.dispose();
    this.externalDrops.dispose();
    this.pinches.dispose();
    this.touches.dispose();
    this.touchPaging.dispose();
    this.videoEvents.dispose();
    this.resizeObserver?.disconnect();
    this.hostObserver?.disconnect();
    this.stopHostChanges();
    this.stopRichEditors();
    this.stopPlainEditors();
    this.hover.dispose();
    this.root.removeEventListener('scroll', this.queueFrameCheck, true);
    this.root.removeEventListener('scroll', drawScrollTransitions, true);
    this.root.removeEventListener('change', this.onChange);
    this.root.removeEventListener('pointerdown', this.onSliderPointerDown);
    this.root.removeEventListener('pointerdown', this.onDividerPointerDown);
    if (this.sidebarDrag) this.onDividerUp({ pointerId: this.sidebarDrag.pointerId } as PointerEvent);
    this.reset();
  }

  // -------------------------------------------------------------------------
  // Ops
  // -------------------------------------------------------------------------

  private create(id: ElementId, kind: ElementKind, props: ElementProps, op: Op): void {
    if (id === ROOT_ID) throw new RendererError('Element 0 is the root and cannot be created', op);
    if (this.elements.has(id)) throw new RendererError(`Element ${id} already exists`, op);
    if (!ALL_KINDS.has(kind)) throw new RendererError(`Unknown element kind ${String(kind)}`, op);
    // A vertical `textfield` is a `<textarea>` (its axis is fixed at creation),
    // a `texteditor` with runs the rich editor (src/richEditor.ts).
    const el =
      kind === 'textfield' && (props as TextFieldProps).axis === 'vertical'
        ? createMultilineField()
        : kind === 'texteditor' && Array.isArray((props as TextEditorProps).runs)
          ? createRichEditor()
          : createShell(kind);
    el.className = `sb-${kind}`;
    el.dataset[ID_ATTR] = String(id);
    el.dataset[KIND_ATTR] = kind;
    this.elements.set(id, el);
    this.nodes.set(id, { id, kind, props: asRecord(props), children: [] });
    if (kind === 'geometry' || (props as { reportsSize?: unknown }).reportsSize === true) this.geometryReporters.add(id);
    if ((props as { affinePath?: unknown }).affinePath) this.affinePaths.add(id);
    applyProps(el, kind, props, this.assets);
    this.noteScroll(id, kind, props);
    this.noteVisibility(id, kind, props);
    if (kind === 'list' || kind === 'scrollview') trackScroller(el);
    if (kind === 'lazyspacer') this.lazySpacers.add(id);
    if (kind === 'host') {
      this.hosts.add(id);
      this.adoptHostContent(id, el, props as HostProps);
    }
    if (kind === 'registered') this.adoptRegistered(id, el, props as RegisteredProps);
  }

  /**
   * A `registered` element's view (src/viewRegistry.ts): built for its name,
   * given the new props after; a name nothing registered shows itself, as an
   * unknown kind does. The element's own children are the native view's: on
   * a page the stylesheet hides them.
   */
  private adoptRegistered(id: ElementId, el: HTMLElement, props: RegisteredProps): void {
    const values = asRecord(props.props);
    const current = this.registeredViews.get(id);
    if (current && current.name === props.name) {
      current.instance?.update?.(values);
      return;
    }
    current?.instance?.dispose?.();
    const name = props.name;
    const instance = ViewRegistry.make(name, { id, props: values, send: (value) => this.onEvent({ type: 'registered', id, name, value }) });
    this.registeredViews.set(id, { name, instance });
    let content = instance?.element;
    if (!content) {
      content = div('sb-registered-missing');
      content.textContent = name;
    }
    content.classList.add('sb-registered-content');
    el.querySelector(':scope > .sb-registered-content')?.remove();
    el.prepend(content);
  }

  /** A toolbar whose placement changed to or from `largeTitle` moves between its screen's content slot and large title. */
  private rehomeToolbar(id: ElementId, el: HTMLElement, op: Op): void {
    const parentId = this.parents.get(id);
    const parent = parentId === undefined ? undefined : this.elements.get(parentId);
    if (parentId === undefined || !parent || !parent.classList.contains('sb-navscreen') || !el.isConnected) return;
    const expected = el.dataset['sbPlacement'] === 'largeTitle' ? (largeTitleOf(parent) ?? slotOf(parent)) : slotOf(parent);
    if (el.parentElement === expected) return;
    const node = this.nodes.get(id);
    const index = node ? (this.nodes.get(parentId)?.children.indexOf(node) ?? -1) : -1;
    if (index >= 0) this.insert(parentId, id, index, op);
  }

  /** A `host` element takes the page's content element (src/hostContent.ts) and watches its size. */
  private adoptHostContent(id: ElementId, el: HTMLElement, props: HostProps): void {
    const content = hostContent(props.content);
    const previous = this.hostContents.get(id);
    if (previous && previous !== content) {
      this.hostObserver?.unobserve(previous);
      previous.remove();
      this.hostContents.delete(id);
    }
    if (!content) return;
    if (content.parentElement !== el) el.appendChild(content);
    if (previous !== content) {
      this.hostContents.set(id, content);
      this.hostObserver?.observe(content);
    }
  }

  /** After a scroll, the lazy spacers are checked on the next frame (once per frame). */
  private readonly queueLazyCheck = (): void => {
    if (this.lazyCheckQueued || this.lazySpacers.size === 0) return;
    this.lazyCheckQueued = true;
    requestAnimationFrame(() => {
      this.lazyCheckQueued = false;
      this.checkLazySpacers();
    });
  };

  /**
   * Lazy rows: a `lazyspacer` within a screen of its scroll view's visible
   * area asks Swift for the next rows (a `lazy` event), once; the next batch
   * comes with a new spacer, which is checked after that pass's layout.
   */
  private checkLazySpacers(): void {
    for (const id of this.lazySpacers) {
      const el = this.elements.get(id);
      if (!el || !el.isConnected) {
        this.lazySpacers.delete(id);
        this.lazyReported.delete(id);
        continue;
      }
      if (this.lazyReported.has(id) || !lazySpacerIsNear(el)) continue;
      this.lazyReported.add(id);
      this.onEvent({ type: 'lazy', id });
    }
  }

  // -------------------------------------------------------------------------
  // `.onScrollVisibilityChange` (src/scrollVisibility.ts)
  // -------------------------------------------------------------------------

  /** A `styled` box with `scrollVisibility` is watched; any other forgets it was. */
  private noteVisibility(id: ElementId, kind: ElementKind, props: ElementProps): void {
    const spec = kind === 'styled' ? (props as Partial<StyledProps>)?.style?.scrollVisibility : undefined;
    if (spec && typeof spec.threshold === 'number') {
      this.visibilityTargets.set(id, spec.threshold);
      return;
    }
    this.visibilityTargets.delete(id);
    this.visibilityReported.delete(id);
  }

  /** After a scroll or a layout, the watched boxes are checked on the next frame (once per frame). */
  private readonly queueVisibilityCheck = (): void => {
    if (this.visibilityCheckQueued || this.visibilityTargets.size === 0) return;
    this.visibilityCheckQueued = true;
    requestAnimationFrame(() => {
      this.visibilityCheckQueued = false;
      this.checkVisibility();
    });
  };

  /**
   * Each watched box against its scroll view's visible area (the view's box
   * less the bars it runs under), or the root's outside one: told when it
   * comes into view or leaves it, and once when first checked.
   */
  private checkVisibility(): void {
    for (const [id, threshold] of this.visibilityTargets) {
      const el = this.elements.get(id);
      if (!el || !el.isConnected) {
        this.visibilityTargets.delete(id);
        this.visibilityReported.delete(id);
        continue;
      }
      const box = el.getBoundingClientRect();
      if (box.width === 0 && box.height === 0) continue;
      const scroller = el.parentElement?.closest<HTMLElement>('.sb-scrollview, .sb-list') ?? null;
      const area = (scroller ?? this.root).getBoundingClientRect();
      const viewport = { x: area.left, y: area.top, width: area.width, height: area.height };
      if (scroller) {
        const chrome = this.layoutResult?.chrome.get(Number(scroller.dataset[ID_ATTR]));
        const top = chrome?.['topInset']?.height ?? 0;
        const covered = chrome?.['bottomInset'];
        viewport.y += top;
        viewport.height -= top;
        if (covered) viewport.height = Math.max(0, viewport.height - covered.height);
      }
      const visible = isScrollVisible({ x: box.left, y: box.top, width: box.width, height: box.height }, viewport, threshold);
      if (this.visibilityReported.get(id) === visible) continue;
      this.visibilityReported.set(id, visible);
      this.onEvent({ type: 'scrollvisibility', id, visible });
    }
  }

  // -------------------------------------------------------------------------
  // `.scrollPosition(id:)` reading back (src/scrollPosition.ts)
  // -------------------------------------------------------------------------

  /** A `reportsPosition` scroll view scrolled: it has settled when no further scroll follows for a moment (and no finger is on it). */
  private readonly onPositionScroll = (event: Event): void => {
    const el = event.target;
    if (!(el instanceof HTMLElement) || el.dataset['sbReportsPosition'] === undefined) return;
    if (this.heldScrolls.has(el)) return;
    this.armPositionReport(el);
  };

  /** The browser says the scroll ended (Chrome, Firefox; Safari waits for the timer). */
  private readonly onPositionScrollEnd = (event: Event): void => {
    const el = event.target;
    if (!(el instanceof HTMLElement) || el.dataset['sbReportsPosition'] === undefined) return;
    if (this.heldScrolls.has(el)) return;
    this.reportPosition(el);
  };

  /**
   * A finger came down inside `reportsPosition` scroll views: a pause in the
   * drag is not a settled scroll (the page would be reported, and an app
   * that re-centres on it would move the content under the finger). They
   * report once the finger lifts and the scroll (the snap after a fling)
   * settles.
   */
  private readonly onPositionTouchStart = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    for (let el = target.closest<HTMLElement>('[data-sb-reports-position]'); el; el = el.parentElement?.closest<HTMLElement>('[data-sb-reports-position]') ?? null) {
      this.heldScrolls.add(el);
      const pending = this.positionTimers.get(el);
      if (pending !== undefined) {
        window.clearTimeout(pending);
        this.positionTimers.delete(el);
      }
    }
  };

  /** The last finger lifted: the held scroll views settle from here (one the finger paged, once its run rests). */
  private readonly onPositionTouchEnd = (event: Event): void => {
    if ((event as TouchEvent).touches?.length > 0) return;
    for (const el of this.heldScrolls) {
      if (this.touchPaging.drives(el)) continue;
      this.heldScrolls.delete(el);
      this.armPositionReport(el);
    }
  };

  private armPositionReport(el: HTMLElement): void {
    const pending = this.positionTimers.get(el);
    if (pending !== undefined) window.clearTimeout(pending);
    this.positionTimers.set(
      el,
      window.setTimeout(() => this.reportPosition(el), POSITION_SETTLE_MS),
    );
  }

  /**
   * Which scroll target of a settled scroll view is at its anchor point: the
   * `scrollID` boxes inside it whose nearest scrolling ancestor it is, along
   * the axis it scrolls on (the vertical one when it scrolls both ways). Sent
   * to that target when it is not the one reported last. A snapping scroll
   * view that settled off its snap points is still between a gesture and
   * the browser's snap animation: it waits (a bounded number of settles)
   * until the animation has carried it onto one, so the target reported is
   * the page the browser chose, never one passing by mid-snap.
   */
  private reportPosition(el: HTMLElement, waited = 0): void {
    const pending = this.positionTimers.get(el);
    if (pending !== undefined) window.clearTimeout(pending);
    this.positionTimers.delete(el);
    const node = this.nodes.get(Number(el.dataset[ID_ATTR]));
    if (!node || !el.isConnected) return;
    const horizontal = node.props['axes'] === 'horizontal';
    const anchor = node.props['positionAnchor'] as { x?: unknown; y?: unknown } | undefined;
    const fraction = horizontal ? anchor?.x : anchor?.y;
    const box = el.getBoundingClientRect();
    const viewport = horizontal ? { start: box.left, length: box.width } : { start: box.top, length: box.height };
    const point = anchorPoint(viewport, typeof fraction === 'number' ? fraction : 0);
    const candidates: PositionCandidate[] = [];
    for (const target of el.querySelectorAll<HTMLElement>('[data-sb-id]')) {
      const id = Number(target.dataset[ID_ATTR]);
      const style = (this.nodes.get(id)?.props as Partial<StyledProps> | undefined)?.style;
      if (!style?.scrollID || target.parentElement?.closest('.sb-scrollview, .sb-list') !== el) continue;
      const rect = target.getBoundingClientRect();
      candidates.push(horizontal ? { id, start: rect.left, length: rect.width } : { id, start: rect.top, length: rect.height });
    }
    if (el.dataset['sbSnap'] !== undefined && waited < POSITION_SNAP_WAITS) {
      const computed = getComputedStyle(el);
      const padding = parseFloat(horizontal ? computed.scrollPaddingLeft : computed.scrollPaddingTop) || 0;
      const offset = horizontal ? el.scrollLeft : el.scrollTop;
      const max = horizontal ? el.scrollWidth - el.clientWidth : el.scrollHeight - el.clientHeight;
      if (!restsOnSnapPoint(candidates, viewport.start + padding, viewport.length, offset, max)) {
        this.positionTimers.set(
          el,
          window.setTimeout(() => this.reportPosition(el, waited + 1), POSITION_SETTLE_MS),
        );
        return;
      }
    }
    const target = positionTarget(candidates, point);
    if (target === null || this.reportedPositions.get(el) === target) return;
    this.reportedPositions.set(el, target);
    this.onEvent({ type: 'scrollposition', id: target });
  }

  /**
   * A `styled` whose style carries a `scrollTo` not seen before is scrolled
   * to after this pass's layout; so is a `scrollview` whose `scrollOffset`
   * command is new. A `scrollview` with `reportsGeometry` is noted to report.
   */
  private noteScroll(id: ElementId, kind: ElementKind, props: ElementProps): void {
    if (kind === 'scrollview') {
      const scroll = props as ScrollViewProps;
      if (scroll.reportsGeometry === true) this.scrollReporters.add(id);
      else this.scrollReporters.delete(id);
      const serial = scroll.scrollOffset?.serial;
      if (typeof serial === 'number' && serial > (this.offsetSerials.get(id) ?? 0)) this.pendingOffsets.push(id);
      return;
    }
    if (kind !== 'styled') return;
    const serial = (props as StyledProps)?.style?.scrollTo?.serial;
    if (typeof serial !== 'number' || serial <= this.scrollSerial) return;
    this.pendingScrolls.push(id);
  }

  /**
   * `ScrollContext.animateScroll` (src/scrollTo.ts): an animated `scrollTo`
   * of a snapping scroll view on one axis runs as the touch pager's run (the
   * snap off until it rests, the last frame writing the offset), held from
   * reporting its position until then as a finger's drive is, so an app
   * re-centring its pages on the page it reports does so once, at rest.
   */
  animateScroll(el: HTMLElement, left: number, top: number): boolean {
    if (el.dataset['sbSnap'] === undefined) return false;
    const axes = el.dataset['sbAxes'];
    if (axes !== 'horizontal' && axes !== 'vertical') return false;
    if (el.dataset['sbReportsPosition'] !== undefined) {
      this.heldScrolls.add(el);
      const pending = this.positionTimers.get(el);
      if (pending !== undefined) {
        window.clearTimeout(pending);
        this.positionTimers.delete(el);
      }
    }
    this.touchPaging.animateTo(el, axes === 'horizontal', axes === 'horizontal' ? left : top);
    return true;
  }

  /** `ScrollViewProxy.scrollTo`: scroll to the targets this pass marked (src/scrollTo.ts). */
  private performScrolls(animated: boolean): void {
    if (this.pendingScrolls.length === 0) return;
    const ids = this.pendingScrolls;
    this.pendingScrolls = [];
    const result = this.layoutResult;
    if (!result) return;
    // Several commands in one pass: the latest wins (it scrolls last).
    const commands = ids
      .map((id) => ({ id, command: (this.nodes.get(id)?.props as Partial<StyledProps> | undefined)?.style?.scrollTo }))
      .filter((entry): entry is { id: ElementId; command: ScrollToCommand } => entry.command !== undefined)
      .sort((a, b) => a.command.serial - b.command.serial);
    // A page a finger set running gives way to the app's commands; a drag in progress carries on across them.
    const fresh = commands.some(({ command }) => command.serial > this.scrollSerial);
    if (fresh) this.touchPaging.beforeScrollCommands();
    for (const { id, command } of commands) {
      if (command.serial <= this.scrollSerial) continue;
      scrollToTarget(this, result, id, command, animated);
      // The scroll views it moved report their position afresh when they next settle, even one they
      // reported last: the app set its binding itself, and where the view rests may not be it.
      for (let scroller = this.elements.get(id)?.parentElement?.closest<HTMLElement>('.sb-scrollview, .sb-list'); scroller; scroller = scroller.parentElement?.closest<HTMLElement>('.sb-scrollview, .sb-list') ?? null) {
        this.reportedPositions.delete(scroller);
      }
    }
    if (fresh) this.touchPaging.afterScrollCommands();
    for (const { command } of commands) this.scrollSerial = Math.max(this.scrollSerial, command.serial);
  }

  /**
   * `scrollOffset` (a `.scrollPosition` bound to a point, `scrollTo(x:y:)`):
   * each scroll view with a command not carried out yet scrolls to it,
   * smoothly when the pass's commit is animated; an axis the command leaves
   * out keeps its offset. The view reports its position afresh when it next
   * settles, as after a `scrollTo` to a target.
   */
  private performOffsetScrolls(animated: boolean): void {
    if (this.pendingOffsets.length === 0) return;
    const ids = this.pendingOffsets;
    this.pendingOffsets = [];
    for (const id of ids) {
      const el = this.elements.get(id);
      const command = (this.nodes.get(id)?.props as Partial<ScrollViewProps> | undefined)?.scrollOffset;
      if (!el || !command || !(command.serial > (this.offsetSerials.get(id) ?? 0))) continue;
      this.offsetSerials.set(id, command.serial);
      const left = typeof command.x === 'number' && Number.isFinite(command.x) ? Math.max(0, command.x) : el.scrollLeft;
      const top = typeof command.y === 'number' && Number.isFinite(command.y) ? Math.max(0, command.y) : el.scrollTop;
      if (animated && typeof el.scrollTo === 'function') el.scrollTo({ left, top, behavior: 'smooth' });
      else {
        el.scrollLeft = left;
        el.scrollTop = top;
      }
      this.reportedPositions.delete(el);
    }
  }

  private update(id: ElementId, props: ElementProps, op: Op): void {
    if (id === ROOT_ID) {
      // Phase 12: the root carries no element props; an update of it is the
      // app's `.preferredColorScheme`, which the page applies as its theme.
      const scheme = (props as RootProps).colorScheme;
      if (scheme === 'light' || scheme === 'dark' || scheme === null) {
        this.root.dispatchEvent(new CustomEvent('sb:color-scheme', { detail: { colorScheme: scheme } }));
        return;
      }
      throw new RendererError('Element 0 is the root and has no props', op);
    }
    const el = this.elements.get(id);
    if (!el) throw new RendererError(`Cannot update unknown element ${id}`, op);
    const kind = el.dataset[KIND_ATTR] as ElementKind;
    const node = this.nodes.get(id);
    // `.contentTransition`: a text changing under one keeps a copy of how it
    // looked, for the Animator to play the change with (when the pass animates).
    if (kind === 'text' && node && this.passAnimates !== false && (props as TextProps).text !== node.props['text']) {
      const host = el.closest<HTMLElement>('[data-sb-content-transition]');
      if (host && this.root.contains(host)) {
        this.passRootRect ??= this.animator.rootRect();
        const rect = this.animator.rectOf(el, this.passRootRect);
        if (rect) {
          this.pass.contentChanges.push({
            el,
            clone: ghostClone(el),
            rect,
            kind: host.dataset['sbContentTransition'] as 'numericText' | 'opacity' | 'interpolate',
            countsDown: countsDown(host.dataset['sbCountsDown'], String(node.props['text'] ?? ''), String((props as TextProps).text ?? '')),
          });
        }
      }
    }
    // iOS 26: a bar group moved to another placement is not the same group
    // on screen; it leaves as it was and comes in where it goes (the Swift
    // runtime keys groups by placement, so this is for other hosts).
    const replaces = kind === 'toolbar' && node !== undefined && (props as { placement?: string }).placement !== node.props['placement'] && isBarToolbar(el);
    if (replaces) this.replaceToolbar(el);
    if (node) {
      const next = asRecord(props);
      // Another Swift view now reads this element's size (a `geometry`
      // element kept for a new page's reader): it knows no size yet, so the
      // next layout pass reports the size even when it is unchanged.
      if (kind === 'geometry' || next['reportsSize'] === true) {
        this.geometryReporters.add(id);
        if (next['reader'] !== node.props['reader']) this.reportedGeometry.delete(id);
      } else {
        this.geometryReporters.delete(id);
      }
      if (next['affinePath']) this.affinePaths.add(id);
      else this.affinePaths.delete(id);
      node.props = next;
      // Phase 9: the next layout re-measures this subtree and its ancestors;
      // a screen's title also feeds the next screen's back label.
      if (node.kind === 'navscreen') {
        const stackId = this.parents.get(id);
        const stack = stackId === undefined ? undefined : this.nodes.get(stackId);
        if (stack) this.engine?.invalidateDown(stack);
      }
      this.engine?.invalidate(node);
    }
    // Remember how the element looked before, in case the pass is animated.
    const previous = this.pass.updated.get(el);
    const before = previous ? previous.before : this.preSnapshots.has(el) ? this.preSnapshots.get(el)! : this.passAnimates !== false ? this.animator.snapshot(el) : null;
    const oldToken = el.dataset[ANIMATION_TOKEN];
    // `update` replaces the props entirely, so drop whatever the previous
    // props produced before applying the new ones. The layout box is kept:
    // the engine re-lays out at `commit`, and removals recorded later in the
    // pass measure their old position against intact ancestors.
    // A text input's props produce no inline style (layout sets its box,
    // font and padding), and it is updated on every keystroke while it is
    // being edited: its style stays as it is.
    if (kind !== 'textfield' && kind !== 'texteditor') {
      const { left, top, width, height } = el.style;
      el.removeAttribute('style');
      if (left) el.style.left = left;
      if (top) el.style.top = top;
      if (width) el.style.width = width;
      if (height) el.style.height = height;
    }
    for (const key of Object.keys(el.dataset)) {
      if (key !== ID_ATTR && key !== KIND_ATTR) delete el.dataset[key];
    }
    ANIMATIONS.delete(el);
    setTransition(el, undefined);
    if (kind === 'text') el.textContent = '';
    applyProps(el, kind, props, this.assets);
    this.noteScroll(id, kind, props);
    this.noteVisibility(id, kind, props);
    if (kind === 'host') this.adoptHostContent(id, el, props as HostProps);
    if (kind === 'registered') this.adoptRegistered(id, el, props as RegisteredProps);
    if (kind === 'toolbar') this.rehomeToolbar(id, el, op);
    const newToken = el.dataset[ANIMATION_TOKEN];
    const tokenChanged = (previous?.tokenChanged ?? false) || (newToken !== undefined && newToken !== oldToken);
    const record: UpdatedRecord = { el, before, tokenChanged };
    if (IMPLICITLY_ANIMATED.has(kind)) record.implicit = true;
    const animation = ANIMATIONS.get(el);
    if (animation) record.animation = animation;
    this.pass.updated.set(el, record);
    // A replaced toolbar enters anew: its style change is not tweened.
    if (replaces) this.pass.updated.delete(el);
    this.dirty.add(id);
    if (kind === 'navscreen') {
      const stack = el.parentElement?.closest<HTMLElement>('.sb-navstack');
      if (stack) refreshNavStack(stack);
    }
    if (kind === 'sheet' && el.parentElement) refreshSheets(el.parentElement);
    if (kind === 'tabview') refreshTabBar(el, this.assets);
    if (kind === 'tab') {
      const tabview = tabViewOf(el);
      if (tabview) refreshTabBar(tabview, this.assets);
    }
    // The popover shows what its control had when it opened; drop it when
    // the control (or, for a `menu`, anything inside it) changes.
    if (this.menu && (this.menu.anchorId === id || this.menu.anchor.contains(el))) this.closeMenu();
  }

  private insert(parentId: ElementId, id: ElementId, index: number, op: Op): void {
    if (id === ROOT_ID) throw new RendererError('Element 0 is the root and cannot be inserted', op);
    const parent = this.elements.get(parentId);
    if (!parent) throw new RendererError(`Cannot insert into unknown parent ${parentId}`, op);
    const el = this.elements.get(id);
    if (!el) throw new RendererError(`Cannot insert unknown element ${id}`, op);
    if (el === parent || el.contains(parent)) {
      throw new RendererError(`Inserting ${id} into ${parentId} would create a cycle`, op);
    }
    if (!Number.isInteger(index) || index < 0) {
      throw new RendererError(`Invalid child index ${index}`, op);
    }
    // A section's view header / footer lives beside its rows' card, in the
    // section element itself; the rows after a header are one DOM slot lower.
    const supplement = isSectionSupplement(el);
    // A `.largeTitle` toolbar lives in its screen's large title element, which scrolls with the content.
    const inLargeTitle = el.dataset['sbPlacement'] === 'largeTitle' && parent.classList.contains('sb-navscreen');
    const container = supplement && parent.classList.contains('sb-section') ? parent : inLargeTitle ? (largeTitleOf(parent) ?? slotOf(parent)) : slotOf(parent);
    // Moves: `index` is the element's position in the parent's child list
    // *after* the op is applied. Detaching first makes the index arithmetic
    // identical for fresh inserts and for moves within the same parent.
    const previousParent = el.parentElement;
    const wasAttached = el.isConnected;
    el.remove();
    let slotIndex = index;
    if (!supplement && index > 0 && parent.classList.contains('sb-section')) {
      const first = this.nodes.get(parentId)?.children.find((child) => child.id !== id);
      if (first?.kind === 'sectionheader') slotIndex -= 1;
    }
    // A screen's children may live in two containers (its content slot and
    // its large title): the next sibling in the same container is the reference.
    const screenReference = (): Element | null => {
      const siblings = (this.nodes.get(parentId)?.children ?? []).filter((child) => child.id !== id);
      for (const sibling of siblings.slice(index)) {
        const sib = this.elements.get(sibling.id);
        if (sib && sib.parentElement === container) return sib;
      }
      return null;
    };
    const reference = supplement
      ? el.classList.contains('sb-sectionheader') ? container.firstChild : null
      : parent.classList.contains('sb-navscreen')
        ? screenReference()
        : (container.children[slotIndex] ?? null);
    container.insertBefore(el, reference);
    if (!wasAttached) this.pass.inserted.add(el);
    // Mirror the move in the layout tree.
    const node = this.nodes.get(id);
    const parentNode = this.nodes.get(parentId);
    if (node && parentNode) {
      const oldParentId = this.parents.get(id);
      const oldParent = oldParentId !== undefined ? this.nodes.get(oldParentId) : undefined;
      if (oldParent) {
        const at = oldParent.children.indexOf(node);
        if (at >= 0) oldParent.children.splice(at, 1);
        this.engine?.invalidateUp(oldParent);
      }
      parentNode.children.splice(Math.min(index, parentNode.children.length), 0, node);
      this.parents.set(id, parentId);
      // Phase 9: the subtree's context may differ under its new parent (or
      // position); the parents on both sides may change size.
      this.engine?.invalidateUp(parentNode);
      this.engine?.invalidateDown(node);
    }
    if (el.classList.contains('sb-navscreen') && parent.classList.contains('sb-navstack')) {
      if (container.children.length > 1 && previousParent !== container) {
        animatePush(el);
        // iOS 26: the bar of the screen it covers morphs into its bar (at commit).
        const covered = el.previousElementSibling;
        if (covered instanceof HTMLElement && covered.classList.contains('sb-navscreen')) {
          this.pendingBarMorphs.push({ stack: parent, from: covered, to: el });
        }
      }
      refreshNavStack(parent);
    }
    if (previousParent) {
      const oldStack = previousParent.closest<HTMLElement>('.sb-navstack');
      if (oldStack && oldStack !== parent) refreshNavStack(oldStack);
      if (previousParent !== container && previousParent.querySelector(':scope > .sb-sheet') === null) {
        refreshSheets(previousParent);
      }
    }
    if (el.classList.contains('sb-sheet')) {
      if (!wasAttached) animateSheetIn(el);
      refreshSheets(container);
    }
    // Phase 8: an alert fades in (and scales from 1.1) on its own.
    if (el.classList.contains('sb-alert') && !wasAttached) animateAlertIn(el);
    if (el.classList.contains('sb-tab')) {
      if (parent.classList.contains('sb-tabview')) refreshTabBar(parent, this.assets);
      const oldTabView = previousParent && previousParent !== container ? tabViewOf(previousParent) : null;
      if (oldTabView && oldTabView !== parent) refreshTabBar(oldTabView, this.assets);
    }
  }

  /**
   * Hands the Animator a bar toolbar as removed (a clone of it as it is, where
   * it is: called before its new placement is applied) and inserted (itself),
   * so that at commit it scales out from its old place and in at its new one
   * instead of tweening between the two.
   */
  private replaceToolbar(el: HTMLElement): void {
    const parent = el.parentElement;
    if (!parent) return;
    this.passRootRect ??= this.animator.rootRect();
    const rect = this.animator.rectOf(el, this.passRootRect);
    const ancestors: HTMLElement[] = [];
    for (let a: HTMLElement | null = parent; a && a !== this.root; a = a.parentElement) ancestors.push(a);
    this.pass.removed.push({ el: ghostClone(el), ancestors, rect, transition: transitionOf(el) });
    this.pass.inserted.add(el);
  }

  private remove(id: ElementId, op: Op): void {
    if (id === ROOT_ID) throw new RendererError('Element 0 is the root and cannot be removed', op);
    const el = this.elements.get(id);
    if (!el) throw new RendererError(`Cannot remove unknown element ${id}`, op);
    const parent = el.parentElement;
    // Clones of what left this subtree earlier (a bar group's, src/animation.ts) go with it.
    this.animator.dropExitClonesIn(el);
    // So does the bar morph of a stack in it (its overlay is in the stack).
    for (const [stack, morph] of this.barMorphs) {
      if (stack === el || el.contains(stack)) morph.dispose();
    }
    const stack = parent?.classList.contains('sb-navstack-screens')
      ? parent.parentElement
      : null;
    if (stack && el.classList.contains('sb-navscreen') && el === parent?.lastElementChild && el.previousElementSibling) {
      // Phase 8: after a swipe back the clone starts where the drag left the
      // screen (`--sb-nav-from`), and the screen below is handed back to the
      // stylesheet so it finishes its slide to 0 as the new top screen.
      const uncovered = el.previousElementSibling as HTMLElement;
      this.interaction.screenRemoved(el);
      const morph = this.barMorphs.get(stack);
      if (this.instantPops.delete(id)) {
        settleWithoutTransition(stack);
        morph?.dispose();
      } else {
        const clone = animatePop(stack, el);
        // iOS 26: its bar morphs into the uncovered screen's: on from where a
        // swipe back has it, else from the start, at commit.
        if (morph && morph.from === el) morph.popped(clone);
        else this.pendingBarMorphs.push({ stack, from: clone, to: uncovered });
      }
    }
    const isSheet = el.classList.contains('sb-sheet');
    if (isSheet && parent) animateSheetOut(parent, el);
    // Phase 8: a dismissed alert leaves a fading ghost, like a sheet.
    const isAlert = el.classList.contains('sb-alert');
    if (isAlert && parent) animateAlertOut(parent, el);
    const tabview = el.classList.contains('sb-tab') ? tabViewOf(el) : null;
    if (this.menu && (el === this.menu.anchor || el.contains(this.menu.anchor) || this.menu.anchor.contains(el))) {
      this.closeMenu();
    }
    if (parent && !isSheet && !isAlert) {
      // Keep what the Animator needs for an exit transition; it decides at
      // `commit` whether the pass is animated.
      const ancestors: HTMLElement[] = [];
      for (let a = parent; a && a !== this.root; a = a.parentElement!) ancestors.push(a);
      let rect: DOMRect | null = null;
      let matched: { id: string; el: HTMLElement; rect: DOMRect }[] = [];
      const preread = this.preRects.get(el);
      // A bar toolbar's items (and the toolbar) leave with a transition of
      // their own in any pass (src/animation.ts, `implicitToolbars`).
      if (preread) {
        rect = preread.rect;
        matched = preread.matched;
      } else if (this.passAnimates !== false || barToolbarOf(el, parent)) {
        this.passRootRect ??= this.animator.rootRect();
        rect = this.animator.rectOf(el, this.passRootRect);
        // `.matchedGeometryEffect`: where the matched boxes in the subtree were.
        const candidates = el.dataset['sbMatched'] !== undefined ? [el] : [];
        candidates.push(...el.querySelectorAll<HTMLElement>('[data-sb-matched]'));
        for (const box of candidates) {
          const boxRect = this.animator.rectOf(box, this.passRootRect);
          if (boxRect) matched.push({ id: box.dataset['sbMatched']!, el: box, rect: boxRect });
        }
      }
      this.pass.removed.push({ el, ancestors, rect, transition: transitionOf(el), ...(matched.length ? { matched } : {}) });
    }
    this.pass.inserted.delete(el);
    this.pass.updated.delete(el);
    // A detached <video> would keep playing its sound.
    stopVideos(el);
    el.remove();
    // Phase 9: the layout pass drops the subtree's output; the parent re-measures.
    const removedNode = this.nodes.get(id);
    if (removedNode) {
      const parentId = this.parents.get(id);
      const parentNode = parentId === undefined ? undefined : this.nodes.get(parentId);
      if (parentNode) this.engine?.invalidateUp(parentNode);
      this.engine?.remove(removedNode);
    }
    this.forget(id);
    for (const descendant of el.querySelectorAll<HTMLElement>('[data-sb-id]')) {
      this.forget(Number(descendant.dataset[ID_ATTR]));
    }
    if (stack) refreshNavStack(stack);
    if (isSheet && parent) refreshSheets(parent);
    if (tabview) refreshTabBar(tabview, this.assets);
  }

  /** Forget an element in every map; detaches its node from the tree. */
  private forget(id: ElementId): void {
    const content = this.hostContents.get(id);
    if (content) {
      this.hostObserver?.unobserve(content);
      this.hostContents.delete(id);
    }
    this.hosts.delete(id);
    this.registeredViews.get(id)?.instance?.dispose?.();
    this.registeredViews.delete(id);
    this.elements.delete(id);
    this.boxes.delete(id);
    this.dirty.delete(id);
    this.reportedGeometry.delete(id);
    this.scrollReporters.delete(id);
    this.scrollGeometry.forget(id);
    this.offsetSerials.delete(id);
    this.geometryReporters.delete(id);
    this.affinePaths.delete(id);
    this.layoutReporter.forget(id);
    const node = this.nodes.get(id);
    const parentId = this.parents.get(id);
    const parentNode = parentId !== undefined ? this.nodes.get(parentId) : undefined;
    if (node && parentNode) {
      const at = parentNode.children.indexOf(node);
      if (at >= 0) parentNode.children.splice(at, 1);
    }
    this.nodes.delete(id);
    this.parents.delete(id);
  }
}

interface OpenMenu {
  el: HTMLElement;
  /** The control that opened it (a `menu` picker or a `menu` element). */
  anchor: HTMLElement;
  anchorId: ElementId;
}

/** One row of a popover menu. */
type MenuRow =
  | { kind: 'tap'; id: ElementId; title: string; subtitle: string | null; symbol: string | null; destructive: boolean; disabled: boolean }
  | { kind: 'toggle'; id: ElementId; title: string; subtitle: string | null; symbol: string | null; isOn: boolean; disabled: boolean }
  | { kind: 'select'; id: ElementId; index: number; title: string; checked: boolean }
  | { kind: 'caption'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'separator' };

// `textOf`, `symbolOf` and `unwrapStyledNode` live in src/nodeQueries.ts
// (Phase 8 shares them with the swipe actions).

/** Whether a `styled` wrapper in the chain down to the element disables it. */
function disabledOf(node: LayoutNode): boolean {
  let current: LayoutNode | undefined = node;
  while (current && current.kind === 'styled') {
    const style = current.props['style'];
    if (typeof style === 'object' && style !== null && (style as Record<string, unknown>)['disabled'] === true) return true;
    current = current.children.length === 1 ? current.children[0] : undefined;
  }
  return false;
}

/**
 * A menu item's title and subtitle, as SwiftUI reads a menu label: its
 * first text is the title, any after it the subtitle under it.
 */
function titlesOf(node: LayoutNode): { title: string; subtitle: string | null } {
  const texts: string[] = [];
  const visit = (n: LayoutNode): void => {
    if (n.kind === 'text' && typeof n.props['text'] === 'string' && n.props['text'].trim().length > 0) texts.push(n.props['text'].trim());
    n.children.forEach(visit);
  };
  visit(node);
  return { title: texts[0] ?? '', subtitle: texts.length > 1 ? texts.slice(1).join(' ') : null };
}

/**
 * Turn a `menu`'s items into popover rows. A picker's options form their
 * own group, separated from neighbouring rows like iOS does; explicit
 * `divider`s are separators too (never doubled).
 */
function collectMenuRows(items: readonly LayoutNode[], rows: MenuRow[] = []): MenuRow[] {
  let pendingSeparator = false;
  const separate = (): void => {
    if (rows.length > 0 && rows[rows.length - 1]!.kind !== 'separator') rows.push({ kind: 'separator' });
  };
  for (const item of items) {
    const base = unwrapStyledNode(item);
    if (pendingSeparator && base.kind !== 'divider') {
      separate();
      pendingSeparator = false;
    }
    switch (base.kind) {
      case 'button':
      case 'navlink':
        rows.push({
          kind: 'tap',
          id: base.id,
          ...titlesOf(base),
          symbol: symbolOf(base),
          destructive: base.props['role'] === 'destructive',
          disabled: disabledOf(item),
        });
        break;
      case 'toggle':
        // A toggle in a menu is a checkable row (SwiftUI's), its label the title.
        rows.push({ kind: 'toggle', id: base.id, ...titlesOf(base), symbol: symbolOf(base), isOn: base.props['isOn'] === true, disabled: disabledOf(item) });
        break;
      case 'picker': {
        const raw = base.props['options'];
        const options = Array.isArray(raw) ? raw.map(String) : [];
        const selected = clampIndex(base.props['selected'], options.length);
        separate();
        options.forEach((title, index) => rows.push({ kind: 'select', id: base.id, index, title, checked: index === selected }));
        pendingSeparator = true;
        break;
      }
      case 'divider':
        separate();
        break;
      case 'section': {
        // An inline section, as iOS draws one: a hairline before and after it, its header a caption.
        separate();
        const header = base.props['header'];
        if (typeof header === 'string' && header.length > 0) rows.push({ kind: 'caption', text: header });
        collectMenuRows(base.children, rows);
        pendingSeparator = true;
        break;
      }
      case 'sectionheader': {
        // A view header: its text as the caption.
        const text = textOf(base);
        if (text.length > 0) rows.push({ kind: 'caption', text });
        break;
      }
      case 'sectionfooter':
        break;
      case 'text':
        rows.push({ kind: 'text', text: textOf(base) });
        break;
      case 'menu': {
        // A nested menu: its label as a caption, its items flattened.
        const [label, ...nested] = base.children;
        if (label) rows.push({ kind: 'caption', text: textOf(label) });
        collectMenuRows(nested, rows);
        break;
      }
      default:
        // Groups, stacks and the like: their children in order.
        collectMenuRows(base.children, rows);
        break;
    }
  }
  return rows;
}

/**
 * Build the DOM for one popover row (the renderer's click handler reads the
 * data attributes). In a menu with checkable rows (`checks`), every item row
 * keeps a leading column for the checkmark, as iOS does, so titles line up;
 * an item's symbol follows the check, its subtitle sits under its title.
 */
function menuRowElement(row: MenuRow, checks: boolean): HTMLElement {
  switch (row.kind) {
    case 'select':
    case 'toggle':
    case 'tap': {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'sb-picker-menu-item';
      item.dataset['sbTarget'] = String(row.id);
      item.dataset['sbAction'] = row.kind;
      if (row.kind === 'select') {
        item.setAttribute('role', 'menuitemradio');
        item.setAttribute('aria-checked', String(row.checked));
        item.dataset['sbIndex'] = String(row.index);
      } else if (row.kind === 'toggle') {
        item.setAttribute('role', 'menuitemcheckbox');
        item.setAttribute('aria-checked', String(row.isOn));
      } else {
        item.setAttribute('role', 'menuitem');
        if (row.destructive) item.dataset['sbRole'] = 'destructive';
      }
      if (row.kind !== 'select' && row.disabled) item.disabled = true;
      if (checks) {
        const check = span('sb-picker-menu-check');
        const glyph = renderSymbol('checkmark');
        if (glyph) check.append(glyph);
        item.append(check);
      }
      const symbol = row.kind === 'select' ? null : row.symbol;
      if (symbol) {
        const holder = span('sb-picker-menu-symbol');
        const glyph = renderSymbol(symbol);
        if (glyph) holder.append(glyph);
        holder.dataset['sbSymbol'] = symbol;
        item.append(holder);
      }
      const title = span('sb-picker-menu-title');
      title.textContent = row.title;
      const subtitle = row.kind === 'select' ? null : row.subtitle;
      if (subtitle) {
        const titles = span('sb-picker-menu-titles');
        const sub = span('sb-picker-menu-subtitle');
        sub.textContent = subtitle;
        titles.append(title, sub);
        item.append(titles);
        item.dataset['sbSubtitled'] = '';
      } else {
        item.append(title);
      }
      return item;
    }
    case 'caption': {
      const caption = div('sb-picker-menu-caption');
      caption.textContent = row.text;
      return caption;
    }
    case 'text': {
      const text = div('sb-picker-menu-static');
      text.setAttribute('aria-disabled', 'true');
      text.textContent = row.text;
      return text;
    }
    case 'separator': {
      const separator = div('sb-picker-menu-separator');
      separator.setAttribute('role', 'separator');
      return separator;
    }
  }
}

/** Whether scrolling dismisses the keyboard; a page cannot drag it down, so `interactively` blurs as `immediately` does. */
function dismissesKeyboard(mode: unknown): boolean {
  return mode === true || mode === 'immediately' || mode === 'interactively';
}

/** Props as the engine reads them (untrusted JSON, so a plain record). */
function asRecord(props: unknown): Record<string, unknown> {
  return typeof props === 'object' && props !== null ? (props as Record<string, unknown>) : {};
}

/** Safe-area insets from the page's device tokens (`--device-safe-top/bottom`). */
function readSafeArea(root: HTMLElement): LayoutEnvironment['safeArea'] {
  const cs = getComputedStyle(root);
  const read = (name: string, fallback: number): number => {
    const value = parseFloat(cs.getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };
  return {
    top: read('--device-safe-top', DEFAULT_SAFE_AREA.top),
    leading: 0,
    bottom: read('--device-safe-bottom', DEFAULT_SAFE_AREA.bottom),
    trailing: 0,
  };
}

/** Convenience factory mirroring the function-style API in the brief. */
export function createRenderer(root: HTMLElement, onEvent: EventCallback): Renderer {
  return new Renderer(root, onEvent);
}

// ---------------------------------------------------------------------------
// Element shells and slots
// ---------------------------------------------------------------------------

/** Slot elements of compound kinds, registered by `createShell`. */
const SLOTS = new WeakMap<HTMLElement, HTMLElement>();

/** The DOM node that receives an element's protocol children. */
export function slotOf(el: HTMLElement): HTMLElement {
  return SLOTS.get(el) ?? el;
}

function div(className: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = className;
  return el;
}

/** Create the element and any chrome it owns; props are applied separately. */
function createShell(kind: ElementKind): HTMLElement {
  switch (kind) {
    case 'host':
      // The page's content element is adopted by the renderer (`adoptHostContent`).
      return div('sb-host');
    case 'registered':
      // Its view is built by the renderer (`adoptRegistered`).
      return div('sb-registered');
    case 'button': {
      const el = document.createElement('button');
      el.type = 'button';
      return el;
    }
    case 'text':
      return document.createElement('span');
    case 'textfield': {
      const el = document.createElement('input');
      el.autocomplete = 'off';
      el.spellcheck = false;
      return el;
    }
    case 'image': {
      const el = document.createElement('span');
      el.setAttribute('role', 'img');
      return el;
    }
    case 'divider': {
      const el = document.createElement('div');
      el.setAttribute('role', 'separator');
      return el;
    }
    case 'navstack': {
      const el = document.createElement('div');
      const slot = div('sb-slot sb-navstack-screens');
      el.append(slot);
      SLOTS.set(el, slot);
      return el;
    }
    case 'list': {
      // The inner box is sized to the content so the list scrolls it.
      const el = document.createElement('div');
      const slot = div('sb-slot sb-list-content');
      el.append(slot);
      SLOTS.set(el, slot);
      return el;
    }
    case 'scrollview': {
      const el = document.createElement('div');
      const slot = div('sb-slot sb-scroll-content');
      el.append(slot);
      SLOTS.set(el, slot);
      return el;
    }
    case 'navscreen': {
      const el = document.createElement('div');
      const bar = div('sb-navbar');
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'sb-navbar-back';
      const chevron = renderSymbol('chevron.left');
      if (chevron) back.append(chevron);
      back.append(span('sb-navbar-back-label'));
      const inlineTitle = span('sb-navbar-title');
      // `.navigationSubtitle`: under the title in a Mac's unified title bar (chrome.subtitle).
      const subtitle = span('sb-navbar-subtitle');
      subtitle.hidden = true;
      // The More button of the collapsed `secondary` items (chrome.more).
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'sb-navbar-more';
      more.setAttribute('aria-label', 'More');
      more.setAttribute('aria-haspopup', 'menu');
      more.hidden = true;
      const ellipsis = renderSymbol('ellipsis');
      if (ellipsis) more.append(ellipsis);
      // A Mac sidebar's overflow menu shows `»` instead (styles.css, `data-sb-overflow`).
      const chevrons = renderSymbol('chevron.right.2');
      if (chevrons) more.append(chevrons);
      bar.append(back, inlineTitle, subtitle, div('sb-navbar-trailing'), more);
      const body = div('sb-navscreen-body');
      const slot = div('sb-slot sb-navscreen-content');
      // The `keyboard` toolbars' bar (chrome `keyboardBar`), under their items.
      const keyboardBar = div('sb-keyboard-bar');
      keyboardBar.hidden = true;
      body.append(div('sb-navbar-large-title'), slot, keyboardBar);
      // Phase 8: a 24px strip along the leading edge of a pushed screen
      // that, on touch devices, keeps the browser from scrolling so a swipe
      // back can start there (styles.css; the controller forwards plain taps).
      const edge = div('sb-navscreen-edge');
      edge.setAttribute('aria-hidden', 'true');
      el.append(bar, body, edge);
      SLOTS.set(el, slot);
      return el;
    }
    case 'section': {
      const el = document.createElement('div');
      const slot = div('sb-slot sb-section-rows');
      el.append(div('sb-section-header'), slot, div('sb-section-footer'));
      SLOTS.set(el, slot);
      return el;
    }
    case 'disclosuregroup': {
      // The label (the first child) and the content below it, laid out as
      // a stack; a button over the label and a chevron at its trailing
      // edge, placed by layoutDom from the label's frame.
      const el = document.createElement('div');
      const slot = div('sb-slot');
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'sb-disclosure-toggle';
      toggle.setAttribute('aria-expanded', 'false');
      el.append(slot, toggle);
      const chevron = renderSymbol('chevron.right');
      if (chevron) {
        chevron.classList.add('sb-disclosure-chevron');
        el.append(chevron);
      }
      SLOTS.set(el, slot);
      return el;
    }
    case 'toggle': {
      const el = document.createElement('div');
      const label = div('sb-slot sb-toggle-label');
      const sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'sb-switch';
      sw.setAttribute('role', 'switch');
      sw.setAttribute('aria-checked', 'false');
      sw.append(span('sb-switch-knob'));
      el.append(label, sw);
      SLOTS.set(el, label);
      return el;
    }
    case 'navlink': {
      const el = document.createElement('div');
      el.setAttribute('role', 'button');
      el.tabIndex = 0;
      const chevron = renderSymbol('chevron.right');
      const slot = div('sb-slot sb-navlink-label');
      el.append(slot);
      SLOTS.set(el, slot);
      if (chevron) {
        chevron.classList.add('sb-navlink-chevron');
        el.append(chevron);
      }
      return el;
    }
    case 'sheet': {
      // A full-screen layer: a scrim over the presenting content and the
      // card (grabber + content slot) sliding up from the bottom.
      const el = document.createElement('div');
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      const scrim = div('sb-sheet-scrim');
      const card = div('sb-sheet-card');
      const grabber = div('sb-sheet-grabber');
      grabber.setAttribute('aria-hidden', 'true');
      const slot = div('sb-slot sb-sheet-content');
      card.append(grabber, slot);
      el.append(scrim, card);
      SLOTS.set(el, slot);
      return el;
    }
    case 'color':
    case 'shape': {
      const el = document.createElement('div');
      el.setAttribute('aria-hidden', 'true');
      return el;
    }
    case 'tabview': {
      // The tabs go into the content slot; the bar is chrome drawn on top.
      const el = document.createElement('div');
      const slot = div('sb-slot sb-tabview-content');
      const bar = div('sb-tabbar');
      bar.setAttribute('role', 'tablist');
      el.append(slot, bar);
      SLOTS.set(el, slot);
      return el;
    }
    case 'tab': {
      const el = document.createElement('div');
      el.setAttribute('role', 'tabpanel');
      return el;
    }
    case 'picker':
      // Segments or the label/value row are built from the props.
      return document.createElement('div');
    case 'gauge': {
      // The labels are protocol children (in the slot); the track and its
      // fill are chrome positioned from the engine's `track` / `fill` rects.
      const el = document.createElement('div');
      el.setAttribute('role', 'meter');
      el.setAttribute('aria-valuemin', '0');
      el.setAttribute('aria-valuemax', '1');
      const slot = div('sb-slot sb-gauge-labels');
      const track = div('sb-gauge-track');
      track.setAttribute('aria-hidden', 'true');
      const fill = div('sb-gauge-fill');
      fill.setAttribute('aria-hidden', 'true');
      // A circular style's ring (gaugeRing.ts), placed from the `ring` rect.
      el.append(slot, track, fill, createGaugeRing());
      SLOTS.set(el, slot);
      return el;
    }
    case 'menu': {
      // Phase 6: the label (first child) is the visible control; the other
      // children are the items, hidden inline and listed in the popover.
      const el = document.createElement('div');
      el.setAttribute('role', 'button');
      el.setAttribute('aria-haspopup', 'menu');
      el.setAttribute('aria-expanded', 'false');
      el.tabIndex = 0;
      return el;
    }
    case 'toolbar': {
      const el = document.createElement('div');
      el.setAttribute('role', 'toolbar');
      return el;
    }
    case 'chart': {
      // Phase 7: the marks, grid, ticks, text labels and legend are chrome
      // (src/chartDom.ts, drawn from the engine's `plot` and label rects);
      // the `chartitem` children live in the slot above it so annotations
      // draw over the marks.
      const el = document.createElement('div');
      el.setAttribute('role', 'img');
      const chrome = div('sb-chart-chrome');
      chrome.setAttribute('aria-hidden', 'true');
      const slot = div('sb-slot sb-chart-items');
      el.append(chrome, slot);
      SLOTS.set(el, slot);
      return el;
    }
    case 'chartitem':
      return document.createElement('div');
    case 'searchfield': {
      // Phase 6: a rounded field with a magnifier; the input sends `text` events.
      const el = document.createElement('div');
      el.setAttribute('role', 'search');
      const glyph = renderSymbol('magnifyingglass');
      if (glyph) el.append(glyph);
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'sb-searchfield-input';
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.setAttribute('aria-label', 'Search');
      el.append(input);
      return el;
    }
    case 'tapgesture':
      return document.createElement('div');
    case 'inspector': {
      // The content and the panel go into the slot; the panel draws the
      // hairline before it (styles.css), so it slides with the panel.
      const el = document.createElement('div');
      const slot = div('sb-slot sb-inspector-columns');
      el.append(slot);
      SLOTS.set(el, slot);
      return el;
    }
    case 'navsplit': {
      // Phase 7: the two columns go into the slot; the sidebar column draws
      // the hairline in the gap the engine leaves (styles.css), so it slides
      // with the column.
      const el = document.createElement('div');
      const slot = div('sb-slot sb-navsplit-columns');
      // iPadOS: the sidebar toggle, at the engine's `toggle` rect; a tap
      // asks Swift to show or hide the sidebar.
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'sb-navsplit-toggle';
      toggle.setAttribute('aria-label', 'Toggle Sidebar');
      const symbol = renderSymbol('sidebar.left');
      if (symbol) toggle.append(symbol);
      // The Mac's floating sidebar panel, under the columns (the engine's `panel` rect).
      const panel = div('sb-navsplit-panel');
      panel.hidden = true;
      // The Mac's column divider (the engine's `divider` rect): dragging it
      // resizes the sidebar, as SwiftUI's does.
      const divider = div('sb-navsplit-divider');
      divider.hidden = true;
      el.append(panel, slot, divider, toggle);
      SLOTS.set(el, slot);
      return el;
    }
    case 'viewthatfits':
      return document.createElement('div');
    // Phase 8: gestures, rows, navigation; drag and drop
    case 'gesture':
    case 'touches':
    case 'contextmenu':
    case 'draggable':
    case 'dropdestination':
      return document.createElement('div');
    case 'swipeactions':
    case 'listrow': {
      // The protocol children go into a content slot that paints the row
      // background and slides aside; the action buttons the controller
      // builds (src/interaction.ts) sit behind it. A `listrow` also owns
      // the edit-mode control (selection circle / minus) at `chrome.editControl`.
      const el = document.createElement('div');
      const slot = div('sb-slot sb-swipe-content');
      el.append(slot);
      SLOTS.set(el, slot);
      if (kind === 'listrow') {
        const control = document.createElement('button');
        control.type = 'button';
        control.className = 'sb-listrow-control';
        control.hidden = true;
        control.tabIndex = -1;
        const circle = span('sb-listrow-circle');
        const check = renderSymbol('checkmark');
        if (check) {
          check.classList.add('sb-listrow-check');
          circle.append(check);
        }
        const minus = renderSymbol('minus');
        if (minus) {
          minus.classList.add('sb-listrow-minus');
          circle.append(minus);
        }
        control.append(circle);
        el.append(control);
        // The reorder grip of a movable row (`chrome.reorderControl`), at the
        // trailing edge: three lines, as iOS draws it. It lives in the content
        // slot, so it slides with the content when the minus (or a swipe)
        // reveals Delete; the control above stays, as on iOS.
        const grip = div('sb-listrow-grip');
        grip.hidden = true;
        grip.setAttribute('aria-label', 'Reorder');
        grip.append(gripGlyph());
        slot.append(grip);
      }
      return el;
    }
    case 'alert': {
      // Phase 8: a full-screen layer: the scrim, then a layer (scaled by the
      // appear animation about the card's center) holding the card(s), the
      // title and message, the button slot and the renderer-drawn OK. The
      // engine positions all of them in screen coordinates.
      const el = document.createElement('div');
      el.setAttribute('role', 'alertdialog');
      el.setAttribute('aria-modal', 'true');
      const scrim = div('sb-alert-scrim');
      const layer = div('sb-alert-layer');
      const card = div('sb-alert-card');
      card.setAttribute('aria-hidden', 'true');
      const cancelCard = div('sb-alert-cancel-card');
      cancelCard.setAttribute('aria-hidden', 'true');
      const slot = div('sb-slot sb-alert-buttons');
      const ok = document.createElement('button');
      ok.type = 'button';
      ok.className = 'sb-alert-ok';
      ok.textContent = 'OK';
      layer.append(card, cancelCard, div('sb-alert-title'), div('sb-alert-message'), slot, ok);
      el.append(scrim, layer);
      SLOTS.set(el, slot);
      return el;
    }
    case 'stepper': {
      // Phase 8: the label (slot) leads; the −/+ control is chrome at the engine's `control` rect.
      const el = document.createElement('div');
      const slot = div('sb-slot sb-stepper-label');
      const control = div('sb-stepper-control');
      const half = (step: number, symbol: string, label: string): HTMLButtonElement => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sb-stepper-button';
        button.dataset['sbStep'] = String(step);
        button.setAttribute('aria-label', label);
        const glyph = renderSymbol(symbol);
        if (glyph) button.append(glyph);
        else button.textContent = step < 0 ? '−' : '+';
        return button;
      };
      control.append(half(-1, 'minus', 'Decrement'), half(1, 'plus', 'Increment'));
      el.append(slot, control);
      SLOTS.set(el, slot);
      return el;
    }
    case 'slider': {
      // Phase 8: the labels are protocol children; track, fill and thumb are chrome.
      const el = document.createElement('div');
      el.setAttribute('role', 'slider');
      const slot = div('sb-slot sb-slider-labels');
      const track = div('sb-slider-track');
      const fill = div('sb-slider-fill');
      const thumb = div('sb-slider-thumb');
      for (const part of [track, fill, thumb]) part.setAttribute('aria-hidden', 'true');
      el.append(slot, track, fill, thumb);
      SLOTS.set(el, slot);
      return el;
    }
    case 'datepicker': {
      // Phase 8: the label (slot) leads; a date and a time capsule (text plus
      // an invisible native input over it) are chrome at `date` / `time`.
      const el = document.createElement('div');
      const slot = div('sb-slot sb-datepicker-label');
      const capsule = (type: 'date' | 'time'): HTMLElement => {
        const box = div(`sb-datepicker-capsule sb-datepicker-${type}`);
        const text = span('sb-datepicker-text');
        const input = document.createElement('input');
        input.type = type;
        input.className = 'sb-datepicker-input';
        input.setAttribute('aria-label', type === 'date' ? 'Date' : 'Time');
        box.append(text, input);
        return box;
      };
      el.append(slot, capsule('date'), capsule('time'));
      SLOTS.set(el, slot);
      return el;
    }
    case 'canvas':
      return createCanvasElement();
    case 'texteditor': {
      const el = document.createElement('textarea');
      el.autocomplete = 'off';
      el.spellcheck = false;
      return el;
    }
    default:
      return document.createElement('div');
  }
}

/** The list row `el` is in (a direct child of a section's rows or a list's content), or null. */
function listRowOf(el: HTMLElement, root: HTMLElement): HTMLElement | null {
  for (let node: HTMLElement | null = el; node && node !== root; node = node.parentElement) {
    const parent = node.parentElement;
    if (parent?.classList.contains('sb-section-rows') || parent?.classList.contains('sb-list-content')) return node;
  }
  return null;
}

/** Controls a row may hold besides its button for the row to stay the button's: none. */
const ROW_CONTROLS =
  'input, textarea, [contenteditable], .sb-navlink, .sb-switch, .sb-picker, .sb-stepper, .sb-slider, .sb-menu, .sb-tapgesture, .sb-link, .sb-datepicker';

/**
 * A list row holding one button (with other views beside it, or under a
 * modifier) taps the button wherever it is tapped, as a UITableView cell
 * does on iOS; a row that is the button itself is that already. Not a
 * `.plain` or `.borderless` button, which SwiftUI keeps to its own label in
 * a row, a disabled one, or a row with other controls.
 */
function rowAction(el: HTMLElement, root: HTMLElement): HTMLElement | null {
  const row = listRowOf(el, root);
  if (!row || row.matches('.sb-button, .sb-navlink, .sb-listrow[data-sb-selectable]')) return null;
  const buttons = row.querySelectorAll<HTMLElement>('.sb-button');
  if (buttons.length !== 1) return null;
  const button = buttons[0]!;
  if (button.dataset['sbStyle'] === 'plain' || button.dataset['sbStyle'] === 'borderless' || button.closest('[data-sb-disabled]')) return null;
  if (row.querySelector(ROW_CONTROLS)) return null;
  return button;
}

/** A vertical `textfield`: a `<textarea>` the layout sizes to its lines. */
function createMultilineField(): HTMLTextAreaElement {
  const el = document.createElement('textarea');
  el.autocomplete = 'off';
  el.spellcheck = false;
  el.rows = 1;
  return el;
}

function isSectionSupplement(el: HTMLElement): boolean {
  return el.classList.contains('sb-sectionheader') || el.classList.contains('sb-sectionfooter');
}

/** The reorder grip as iOS draws it: three 21 pt lines, 1.33 thick, 4.67 apart. */
function gripGlyph(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 21 11');
  svg.setAttribute('aria-hidden', 'true');
  for (const y of [0.67, 5.33, 10]) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '0');
    line.setAttribute('x2', '21');
    line.setAttribute('y1', String(y));
    line.setAttribute('y2', String(y));
    svg.append(line);
  }
  return svg;
}

function span(className: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = className;
  return el;
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/**
 * Recompute per-screen state for a `navstack`: only the last screen is
 * interactive, and every screen at depth > 0 shows a back button labelled
 * with the previous screen's title.
 */
function refreshNavStack(stack: HTMLElement): void {
  const screens = Array.from(slotOf(stack).children) as HTMLElement[];
  screens.forEach((screen, i) => {
    const isTop = i === screens.length - 1;
    screen.toggleAttribute('inert', !isTop);
    screen.setAttribute('aria-hidden', String(!isTop));
    screen.dataset['sbNavPosition'] = isTop ? 'top' : 'below';
    const previous = screens[i - 1];
    const back = screen.querySelector<HTMLElement>(':scope > .sb-navbar > .sb-navbar-back');
    const label = back?.querySelector<HTMLElement>('.sb-navbar-back-label');
    const depth = Number(screen.dataset['sbDepth'] ?? '0');
    if (back && label) {
      const show = depth > 0;
      back.hidden = !show;
      // The layout engine picks the label (previous title, "Back" or none)
      // so it never collides with the title; fall back until it has run.
      label.textContent = label.dataset['sbLayoutLabel'] ?? (previous?.dataset['sbTitle'] || 'Back');
      back.dataset['sbTarget'] = screen.dataset[ID_ATTR] ?? '';
    }
  });
}

/**
 * Run `done` once the CSS animation on `target` itself has ended (not one
 * bubbling up from a descendant). A late first frame (heavy layout) can push
 * the end past `expectedMs`, so the fallback waits for the running animation
 * objects instead of cutting the animation short.
 */
function afterAnimation(target: HTMLElement, expectedMs: number, done: () => void): void {
  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    target.removeEventListener('animationend', onEnd);
    target.removeEventListener('animationcancel', onEnd);
    done();
  };
  const onEnd = (event: Event): void => {
    if (event.target === target) finish();
  };
  target.addEventListener('animationend', onEnd);
  target.addEventListener('animationcancel', onEnd);
  window.setTimeout(() => {
    const running = target.getAnimations().filter((a) => a.playState === 'running');
    if (running.length === 0) {
      finish();
      return;
    }
    Promise.allSettled(running.map((a) => a.finished)).then(finish, finish);
    window.setTimeout(finish, expectedMs * 3);
  }, expectedMs + 50);
}

function animatePush(screen: HTMLElement): void {
  screen.classList.remove('sb-navscreen--push');
  // Flush the style (not the layout: a layout forced here, with the screen
  // just inserted, would size the history entry it covers by it, see
  // browserHistory.ts) so re-adding the class restarts the animation.
  void getComputedStyle(screen).animationName;
  screen.classList.add('sb-navscreen--push');
  afterAnimation(screen, NAV_TRANSITION_MS, () => screen.classList.remove('sb-navscreen--push'));
}

/**
 * The protocol removes a popped screen immediately; to animate it out a
 * visual clone (stripped of element ids so it is invisible to the renderer)
 * slides away on top of the stack and is dropped afterwards.
 */
/**
 * A pop the browser has already animated: the stack's screens take their new
 * places without a transition (styles.css, `data-sb-instant`) for the frames
 * the change lands in.
 */
function settleWithoutTransition(stack: HTMLElement): void {
  stack.dataset['sbInstant'] = '';
  requestAnimationFrame(() => requestAnimationFrame(() => delete stack.dataset['sbInstant']));
}

function animatePop(stack: HTMLElement, screen: HTMLElement): HTMLElement {
  const clone = ghostClone(screen);
  clone.removeAttribute('inert');
  clone.setAttribute('aria-hidden', 'true');
  clone.classList.add('sb-navscreen--pop');
  stack.append(clone);
  afterAnimation(clone, NAV_TRANSITION_MS, () => clone.remove());
  return clone;
}

// ---------------------------------------------------------------------------
// Tab views (Phase 4)
// ---------------------------------------------------------------------------

/** The `tabview` element whose content slot holds `el` (a tab or the slot itself), or null. */
function tabViewOf(el: HTMLElement): HTMLElement | null {
  const slot = el.classList.contains('sb-tabview-content') ? el : el.parentElement;
  if (!slot?.classList.contains('sb-tabview-content')) return null;
  const tabview = slot.parentElement;
  return tabview?.classList.contains('sb-tabview') ? tabview : null;
}

function clampIndex(value: unknown, count: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : 0;
  return count === 0 ? 0 : Math.min(count - 1, Math.max(0, n));
}

/**
 * Rebuild the tab bar items from the `tab` children (title + symbol) and mark
 * the selected tab: only it is visible and interactive. The layout engine
 * confirms the hidden set at `commit`; this keeps the DOM right in between.
 */
function refreshTabBar(tabview: HTMLElement, assets: AssetCatalog): void {
  const bar = tabview.querySelector<HTMLElement>(':scope > .sb-tabbar');
  if (!bar) return;
  const tabs = Array.from(slotOf(tabview).children).filter((c) => c.classList.contains('sb-tab')) as HTMLElement[];
  const selected = clampIndex(Number(tabview.dataset['sbSelected'] ?? 0), tabs.length);
  while (bar.children.length > tabs.length) bar.lastElementChild?.remove();
  while (bar.children.length < tabs.length) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'sb-tabbar-item';
    item.setAttribute('role', 'tab');
    item.append(span('sb-tabbar-icon'), span('sb-tabbar-title'), span('sb-tabbar-badge'));
    bar.append(item);
  }
  tabs.forEach((tab, i) => {
    const item = bar.children[i] as HTMLElement;
    const title = tab.dataset['sbTitle'] ?? '';
    const symbol = tab.dataset['sbSymbol'] ?? '';
    const isSelected = i === selected;
    item.dataset['sbIndex'] = String(i);
    item.setAttribute('aria-selected', String(isSelected));
    item.setAttribute('aria-label', title);
    item.toggleAttribute('data-sb-selected', isSelected);
    const icon = item.querySelector<HTMLElement>(':scope > .sb-tabbar-icon');
    const asset = symbol ? '' : (tab.dataset['sbAsset'] ?? '');
    if (icon && (icon.dataset['sbSymbol'] !== symbol || (icon.dataset['sbAsset'] ?? '') !== asset)) {
      icon.dataset['sbSymbol'] = symbol;
      if (asset) icon.dataset['sbAsset'] = asset;
      else delete icon.dataset['sbAsset'];
      // iOS draws tab bar symbols in their filled form.
      const glyph = symbol ? renderSymbol(filledSymbolName(symbol)) : asset ? tabAssetIcon(asset, assets) : null;
      if (glyph) icon.replaceChildren(glyph);
      else icon.replaceChildren();
      icon.toggleAttribute('data-sb-missing', Boolean(symbol || asset) && !glyph);
      icon.hidden = !symbol && !asset;
    }
    const label = item.querySelector<HTMLElement>(':scope > .sb-tabbar-title');
    if (label && label.textContent !== title) label.textContent = title;
    // `Tab(…).badge(…)` and `Tab(role: .search)` (the iOS 18 tab API).
    const badge = item.querySelector<HTMLElement>(':scope > .sb-tabbar-badge');
    const badgeText = tab.dataset['sbBadge'] ?? '';
    if (badge && badge.textContent !== badgeText) badge.textContent = badgeText;
    if (badge) badge.hidden = badgeText === '';
    const role = tab.dataset['sbRole'];
    if (role) item.dataset['sbRole'] = role;
    else delete item.dataset['sbRole'];
    setTabHidden(tab, !isSelected);
  });
}

/**
 * A tab bar item's catalog icon: a custom symbol masks the item's color, in
 * its `.fill` variant when the catalog has one (as iOS draws SF Symbols in a
 * tab bar); any other image shows as it is. Null when the catalog has neither.
 */
function tabAssetIcon(name: string, assets: AssetCatalog): Element | null {
  const asset = assets.images[`${name}.fill`]?.kind === 'symbol' ? assets.images[`${name}.fill`] : assets.images[name];
  if (!asset) return null;
  if (asset.kind === 'symbol') {
    const mask = document.createElement('span');
    mask.className = 'sb-tabbar-mask';
    const url = `url("${asset.src}")`;
    mask.style.setProperty('mask-image', url);
    mask.style.setProperty('-webkit-mask-image', url);
    return mask;
  }
  const img = document.createElement('img');
  img.src = asset.src;
  img.alt = '';
  img.draggable = false;
  return img;
}

// ---------------------------------------------------------------------------
// Sheets
// ---------------------------------------------------------------------------

/**
 * Recompute sheet state under `container` (normally the root): only the last
 * sheet is interactive, and the container records the top sheet's detent so
 * styles.css can scale and dim the presenting content behind a `large` one.
 */
function refreshSheets(container: HTMLElement): void {
  // Exit clones of dismissed sheets are not sheets any more.
  const sheets = Array.from(container.children).filter(
    (c) => c.classList.contains('sb-sheet') && !c.classList.contains(EXIT_CLONE_CLASS),
  ) as HTMLElement[];
  sheets.forEach((sheet, i) => {
    const isTop = i === sheets.length - 1;
    sheet.dataset['sbSheetPosition'] = isTop ? 'top' : 'below';
    sheet.toggleAttribute('inert', !isTop);
  });
  const top = sheets[sheets.length - 1];
  if (top) container.dataset['sbSheet'] = top.dataset['sbDetent'] ?? 'large';
  else delete container.dataset['sbSheet'];
}

function animateSheetIn(sheet: HTMLElement): void {
  sheet.classList.remove('sb-sheet--in');
  void sheet.offsetWidth;
  sheet.classList.add('sb-sheet--in');
  const card = sheet.querySelector<HTMLElement>(':scope > .sb-sheet-card') ?? sheet;
  afterAnimation(card, SHEET_TRANSITION_MS, () => sheet.classList.remove('sb-sheet--in'));
}

/**
 * Like `animatePop`: the protocol removes a dismissed sheet at once, so a
 * clone (without element ids) slides down and fades its scrim, then goes.
 */
function animateSheetOut(container: HTMLElement, sheet: HTMLElement): void {
  const clone = ghostClone(sheet);
  clone.setAttribute('aria-hidden', 'true');
  clone.toggleAttribute('inert', true);
  clone.classList.add('sb-sheet--out', EXIT_CLONE_CLASS);
  const card = clone.querySelector<HTMLElement>(':scope > .sb-sheet-card');
  if (card) {
    // The slide starts where a drag left the card (`--sb-sheet-drag`).
    card.style.transition = '';
    card.style.transform = '';
  }
  container.append(clone);
  afterAnimation(card ?? clone, SHEET_TRANSITION_MS, () => clone.remove());
}

function applySheet(el: HTMLElement, props: SheetProps): void {
  const detents = Array.isArray(props.detents) && props.detents.length ? props.detents : ['large'];
  // Phase 8: `full` is `.fullScreenCover` (no radius, grabber or drag; the presenting tree keeps its scale).
  // A custom height (`height:` / `fraction:`) leaves the presenting tree unscaled, like `medium`.
  const custom = typeof detents[0] === 'string' && /^(height|fraction):/.test(detents[0]);
  const first = detents[0] === 'medium' || custom ? 'medium' : detents[0] === 'full' ? 'full' : 'large';
  el.dataset['sbDetent'] = first;
  el.dataset['sbDetents'] = detents.join(' ');
}

// Phase 8: alerts appear with a 0.25s fade (the alert card scales from 1.1,
// see styles.css) and leave with a fade played by a ghost clone, like sheets.
function animateAlertIn(alert: HTMLElement): void {
  alert.classList.remove('sb-alert--in');
  void alert.offsetWidth;
  alert.classList.add('sb-alert--in');
  const layer = alert.querySelector<HTMLElement>(':scope > .sb-alert-layer') ?? alert;
  afterAnimation(layer, ALERT_TRANSITION_MS, () => alert.classList.remove('sb-alert--in'));
}

function animateAlertOut(container: HTMLElement, alert: HTMLElement): void {
  const clone = ghostClone(alert);
  clone.setAttribute('aria-hidden', 'true');
  clone.toggleAttribute('inert', true);
  clone.classList.remove('sb-alert--in');
  clone.classList.add('sb-alert--out', EXIT_CLONE_CLASS);
  container.append(clone);
  afterAnimation(clone, ALERT_TRANSITION_MS, () => clone.remove());
}

/** A drag of a Mac split view's column divider (`.sb-navsplit-divider`). */
interface SidebarDrag {
  /** The `navsplit` element. */
  id: ElementId;
  pointerId: number;
  startX: number;
  /** The sidebar's width when the drag began (the divider's `data-sb-width`). */
  startWidth: number;
  /** Screen px per layout px of the split view (the presenting tree may be scaled). */
  scale: number;
  frame: number | null;
  /** The width the pointer asks for, laid out at the next animation frame. */
  pending: number;
}

interface SliderDrag {
  slider: HTMLElement;
  track: HTMLElement;
  id: ElementId;
  pointerId: number;
  /** The latest snapped value, sent at the next animation frame. */
  pending: number | null;
  /** The last value sent, so an unchanged frame sends nothing. */
  sent: number | null;
  frame: number | null;
  /** Whether the drag's first `slide` (with `editing: true`) is still to be sent. */
  starting: boolean;
}

function applyAlert(el: HTMLElement, props: AlertProps): void {
  el.dataset['sbStyle'] = props.style === 'dialog' ? 'dialog' : 'alert';
  // The title and message are drawn at layout from the engine's centered lines.
  el.setAttribute('aria-label', props.title ?? '');
}

function applyStepper(el: HTMLElement, props: StepperProps): void {
  const halves = el.querySelectorAll<HTMLElement>(':scope > .sb-stepper-control > .sb-stepper-button');
  halves[0]?.setAttribute('aria-disabled', String(props.canDecrement === false));
  halves[1]?.setAttribute('aria-disabled', String(props.canIncrement === false));
}

function applySlider(el: HTMLElement, props: SliderProps): void {
  const min = Number.isFinite(props.min) ? props.min : 0;
  const max = Number.isFinite(props.max) && props.max > min ? props.max : min + 1;
  const value = Number.isFinite(props.value) ? props.value : min;
  const step = props.step != null && Number.isFinite(props.step) && props.step > 0 ? props.step : null;
  el.dataset['sbMin'] = String(min);
  el.dataset['sbMax'] = String(max);
  el.dataset['sbStep'] = step === null ? '' : String(step);
  el.dataset['sbValue'] = String(value);
  el.setAttribute('aria-valuemin', String(min));
  el.setAttribute('aria-valuemax', String(max));
  el.setAttribute('aria-valuenow', String(value));
}

/**
 * Move the thumb and fill of a slider to `fraction` (0...1) right away, from
 * the track's current box; the engine's next layout places them from `value`.
 */
function setSliderPosition(slider: HTMLElement, fraction: number): void {
  const track = slider.querySelector<HTMLElement>(':scope > .sb-slider-track');
  const fill = slider.querySelector<HTMLElement>(':scope > .sb-slider-fill');
  const thumb = slider.querySelector<HTMLElement>(':scope > .sb-slider-thumb');
  if (!track || !fill || !thumb) return;
  const travel = Math.max(0, track.offsetWidth - SLIDER_THUMB);
  const center = track.offsetLeft + SLIDER_THUMB / 2 + fraction * travel;
  thumb.style.left = `${center - SLIDER_THUMB / 2}px`;
  fill.style.width = `${Math.max(0, center - track.offsetLeft)}px`;
}

function applyDatePicker(el: HTMLElement, props: DatePickerProps): void {
  const seconds = Number.isFinite(props.seconds) ? props.seconds : 0;
  const components = props.components === 'date' || props.components === 'hourAndMinute' ? props.components : 'dateAndTime';
  el.dataset['sbSeconds'] = String(seconds);
  el.dataset['sbComponents'] = components;
  el.dataset['sbMin'] = props.min != null && Number.isFinite(props.min) ? String(props.min) : '';
  el.dataset['sbMax'] = props.max != null && Number.isFinite(props.max) ? String(props.max) : '';
  el.dataset['sbTimeZone'] = typeof props.timeZone === 'string' ? props.timeZone : '';
  if (props.labelsHidden === true) el.dataset['sbLabelsHidden'] = '';
  setDatePickerValue(el, seconds);
}

/**
 * Show `seconds` in the capsules (US English, in the picker's zone) and in
 * the native inputs; `min` / `max` (from the element's data) bound the date
 * input, and the time input only when they fall on the shown day.
 */
function setDatePickerValue(el: HTMLElement, seconds: number): void {
  const bound = (key: 'sbMin' | 'sbMax'): number | null => {
    const text = el.dataset[key];
    return text ? Number(text) : null;
  };
  const min = bound('sbMin');
  const max = bound('sbMax');
  const zone = el.dataset['sbTimeZone'] || undefined;
  for (const type of ['date', 'time'] as const) {
    const capsule = el.querySelector<HTMLElement>(`:scope > .sb-datepicker-${type}`);
    if (!capsule) continue;
    const text = capsule.querySelector<HTMLElement>(':scope > .sb-datepicker-text');
    const input = capsule.querySelector<HTMLInputElement>(':scope > .sb-datepicker-input');
    const label = type === 'date' ? formatPickerDate(seconds, zone) : formatPickerTime(seconds, zone);
    if (text && text.textContent !== label) text.textContent = label;
    if (!input) continue;
    const format = (s: number): string => (type === 'date' ? dateInputValue(s, zone) : timeInputValue(s, zone));
    const value = format(seconds);
    if (input.value !== value) input.value = value;
    const limit = (b: number | null): string =>
      b !== null && Number.isFinite(b) && (type === 'date' || sameDay(b, seconds, zone)) ? format(b) : '';
    input.min = limit(min);
    input.max = limit(max);
  }
}

/**
 * Like `applyTextField`: the caret survives an echo of the text the editor
 * shows. A text from outside (not what the editor reported) would put the
 * caret at the end: the editor's value and caret from before are kept for
 * `applyPlainEditorSelection` to move it past the change.
 */
function applyTextEditor(area: HTMLTextAreaElement, props: TextEditorProps): void {
  const text = props.text ?? '';
  const before = area.value;
  const outside = before !== text && textArrived(area, text) === 'remote';
  const focused = area.ownerDocument.activeElement === area;
  const caret = outside && focused ? { start: area.selectionStart, end: area.selectionEnd } : null;
  if (before !== text) area.value = text;
  // Bound focus, as for a `textfield` (moved in `syncFocus` after the commit).
  if (typeof props.focused === 'boolean') area.dataset['sbFocus'] = String(props.focused);
  else delete area.dataset['sbFocus'];
  markDeleteBackward(area, props.deleteBackward === true);
  // A bound selection, under the rich editor's rules (src/plainEditor.ts).
  applyPlainEditorSelection(area, props, outside ? { before, caret } : null);
}

// ---------------------------------------------------------------------------
// Props → DOM
// ---------------------------------------------------------------------------

/** Elements whose `aria-label` is their `accessibilityLabel` prop. */
const LABELLED = new WeakSet<HTMLElement>();

function applyProps(el: HTMLElement, kind: ElementKind, props: ElementProps, assets: AssetCatalog): void {
  // A label that went away leaves the element's own (an image's name) to the kind below.
  if (LABELLED.delete(el)) el.removeAttribute('aria-label');
  applyKindProps(el, kind, props, assets);
  // `.accessibilityLabel(_:)`, or a `Label`'s title (an icon-only label in
  // a button is named by it): the element's accessible name.
  const label = (props as { accessibilityLabel?: unknown }).accessibilityLabel;
  if (typeof label === 'string' && label !== '') {
    el.setAttribute('aria-label', label);
    LABELLED.add(el);
  }
}

function applyKindProps(el: HTMLElement, kind: ElementKind, props: ElementProps, assets: AssetCatalog): void {
  switch (kind) {
    case 'host':
      el.dataset['sbSizing'] = (props as HostProps).sizing;
      break;
    case 'text':
      applyText(el, props as TextProps, assets);
      break;
    case 'vstack':
      applyVStack(el, props as VStackProps);
      applyScrollTarget(el, (props as VStackProps).scrollTarget === true);
      setPinnedViews(el, (props as VStackProps).pinned);
      break;
    case 'hstack':
      applyHStack(el, props as HStackProps);
      applyScrollTarget(el, (props as HStackProps).scrollTarget === true);
      setPinnedViews(el, (props as HStackProps).pinned);
      break;
    case 'spacer':
      applySpacer(el, props as SpacerProps);
      break;
    case 'button':
      applyButton(el, (props as ButtonProps) ?? {});
      break;
    case 'styled':
      applyStyle(el, (props as StyledProps).style ?? {});
      break;
    case 'zstack':
      applyZStack(el, props as ZStackProps);
      break;
    case 'image':
      applyImage(el, props as ImageProps, assets);
      break;
    case 'tapgesture': {
      // `location`: the tap is sent with its point (`onTapGesture { point in }`); `count`: the taps it takes.
      const tap = props as TapGestureProps | undefined;
      el.toggleAttribute('data-sb-tap-location', tap?.location === true);
      const count = Math.floor(tap?.count ?? 1);
      if (count > 1) el.dataset['sbTapCount'] = String(count);
      else delete el.dataset['sbTapCount'];
      break;
    }
    case 'divider':
    case 'navstack':
    case 'geometry':
    case 'menu':
    case 'inspector':
      // The engine reads `presented`. Showing or hiding the panel animates
      // by default, as in SwiftUI (`animationTokenOf`).
      el.dataset[ANIMATION_TOKEN] = animationTokenOf(kind, props)!;
      break;
    case 'inspectorpanel':
      break;
    case 'navsplit':
      // No props (a navsplit's `sidebarVisible` is read by the engine and layoutDom).
      break;
    case 'navlink':
      // Phase 7: a selected row (inside a `List(selection:)`) gets a rounded fill.
      el.toggleAttribute('data-sb-selected', (props as NavLinkProps | undefined)?.selected === true);
      if ((props as NavLinkProps | undefined)?.style === 'plain') el.dataset['sbStyle'] = 'plain';
      else delete el.dataset['sbStyle'];
      break;
    case 'viewthatfits': {
      const axes = (props as ViewThatFitsProps | undefined)?.axes;
      el.dataset['sbAxes'] = axes === 'horizontal' || axes === 'vertical' ? axes : 'both';
      break;
    }
    case 'toolbar': {
      const placement = (props as ToolbarProps).placement;
      // `secondary` items shown in the bar look like the trailing ones (glass capsules).
      el.dataset['sbPlacement'] =
        placement === 'leading' || placement === 'principal' || placement === 'bottom' || placement === 'largeTitle' || placement === 'keyboard' ? placement : 'trailing';
      // A `ToolbarSpacer`: draws nothing.
      el.toggleAttribute('data-sb-spacer', typeof (props as ToolbarProps).spacer === 'string');
      break;
    }
    case 'searchfield':
      applySearchField(el, props as SearchFieldProps);
      break;
    case 'list': {
      const list = props as ListProps;
      el.dataset['sbListStyle'] = list.style ?? 'insetGrouped';
      // Phase 8: edit mode (the engine shifts `listrow` content) and pull to refresh.
      el.toggleAttribute('data-sb-editing', list.editing === true);
      applyRefreshable(el, list.refreshable === true, list.refreshing === true);
      // `.scrollContentBackground(.hidden)`: no grouped background (styles.css).
      if (list.background === 'hidden') el.dataset['sbBackground'] = 'hidden';
      if (dismissesKeyboard(list.dismissesKeyboard)) el.dataset['sbDismissesKeyboard'] = 'true';
      applyEdgeEffect(el, list.edgeEffect);
      break;
    }
    case 'tabview': {
      const selected = (props as TabViewProps).selected;
      el.dataset['sbSelected'] = String(Number.isFinite(selected) ? Math.max(0, Math.floor(selected)) : 0);
      break;
    }
    case 'tab': {
      const { title, systemImage, badge, role, image } = props as TabProps;
      el.dataset['sbTitle'] = title ?? '';
      el.dataset['sbSymbol'] = systemImage ?? '';
      if (image) el.dataset['sbAsset'] = image;
      else delete el.dataset['sbAsset'];
      if (badge) el.dataset['sbBadge'] = badge;
      else delete el.dataset['sbBadge'];
      if (role) el.dataset['sbRole'] = role;
      else delete el.dataset['sbRole'];
      el.setAttribute('aria-label', title ?? '');
      break;
    }
    case 'picker':
      applyPicker(el, props as PickerProps);
      break;
    case 'scrollview': {
      const scroll = props as ScrollViewProps;
      el.dataset['sbAxes'] = scroll.axes ?? 'vertical';
      applyRefreshable(el, scroll.refreshable === true, scroll.refreshing === true);
      if (scroll.snap === 'viewAligned' || scroll.snap === 'paging') el.dataset['sbSnap'] = scroll.snap;
      else delete el.dataset['sbSnap'];
      if (dismissesKeyboard(scroll.dismissesKeyboard)) el.dataset['sbDismissesKeyboard'] = 'true';
      if (scroll.reportsPosition === true) el.dataset['sbReportsPosition'] = '';
      else delete el.dataset['sbReportsPosition'];
      applyEdgeEffect(el, scroll.edgeEffect);
      applyDefaultScrollAnchor(el, scroll.defaultScrollAnchor);
      break;
    }
    case 'section':
      applySection(el, props as SectionProps);
      break;
    case 'disclosuregroup': {
      const expanded = (props as DisclosureGroupProps).expanded === true;
      el.toggleAttribute('data-sb-expanded', expanded);
      el.querySelector<HTMLElement>(':scope > .sb-disclosure-toggle')?.setAttribute('aria-expanded', String(expanded));
      break;
    }
    case 'textfield':
      applyTextField(el as HTMLInputElement | HTMLTextAreaElement, props as TextFieldProps);
      break;
    case 'toggle': {
      const toggle = props as ToggleProps;
      // `switch` is marked too, so a bar draws it as a switch, not its own toggle.
      if (toggle.style === 'button' || toggle.style === 'switch') el.dataset['sbToggleStyle'] = toggle.style;
      else delete el.dataset['sbToggleStyle'];
      if (toggle.style === 'button') {
        el.setAttribute('role', 'button');
        el.tabIndex = 0;
      } else if (el.getAttribute('role') === 'button') {
        el.removeAttribute('role');
        el.removeAttribute('aria-pressed');
        el.removeAttribute('tabindex');
      }
      setSwitchState(el, Boolean(toggle.isOn));
      break;
    }
    case 'navscreen':
      applyNavScreen(el, props as NavScreenProps);
      break;
    case 'color':
      applyColor(el, props as ColorProps);
      break;
    case 'shape':
      applyShape(el, props as ShapeProps);
      break;
    case 'sheet':
      applySheet(el, props as SheetProps);
      break;
    case 'layout':
      // Pure layout: `proposal`, `size` and `frames` are read by the engine.
      break;
    case 'grid': {
      const grid = props as GridProps;
      el.dataset['sbAlignment'] = grid.alignment ?? 'center';
      break;
    }
    case 'gridrow': {
      const row = props as GridRowProps;
      if (row.alignment) el.dataset['sbAlignment'] = row.alignment;
      break;
    }
    case 'lazyvgrid': {
      const grid = props as LazyVGridProps;
      el.dataset['sbAlignment'] = grid.alignment ?? 'center';
      el.dataset['sbColumns'] = String(Array.isArray(grid.columns) ? grid.columns.length : 0);
      setPinnedViews(el, grid.pinned);
      break;
    }
    case 'lazyhgrid': {
      const grid = props as LazyHGridProps;
      el.dataset['sbAlignment'] = grid.alignment ?? 'center';
      el.dataset['sbRows'] = String(Array.isArray(grid.rows) ? grid.rows.length : 0);
      setPinnedViews(el, grid.pinned);
      break;
    }
    case 'gauge':
      applyGauge(el, props as GaugeProps);
      break;
    case 'decorated': {
      const decorated = props as DecoratedProps;
      el.dataset['sbRole'] = decorated.role === 'background' ? 'background' : 'overlay';
      el.dataset['sbAlignment'] = decorated.alignment ?? 'center';
      break;
    }
    case 'chart': {
      // Phase 7: the chrome is (re)drawn at layout from the node's props, so
      // an `update` with new marks redraws at the next commit; nothing
      // animates. Only a summary lands on the element itself.
      const chart = props as ChartProps;
      el.dataset['sbMarks'] = String(Array.isArray(chart.marks) ? chart.marks.length : 0);
      break;
    }
    case 'chartitem': {
      const item = props as ChartItemProps;
      el.dataset['sbRole'] = item.role ?? 'annotation';
      el.dataset['sbPosition'] = item.position ?? 'overlay';
      break;
    }
    // Phase 8: gestures, rows, navigation. The attributes are what the
    // controller (src/interaction.ts) and the stylesheet read; the engine
    // reads the props from the node tree.
    case 'gesture': {
      const gesture = (props as GestureProps | undefined) ?? {};
      el.toggleAttribute('data-sb-drag', gesture.drag === true);
      el.toggleAttribute('data-sb-long-press', gesture.longPress === true);
      el.toggleAttribute('data-sb-pinch', gesture.magnify === true || gesture.rotate === true);
      break;
    }
    case 'swipeactions': {
      const swipe = (props as SwipeActionsProps | undefined) ?? { trailing: 0, leading: 0 };
      el.dataset['sbTrailing'] = String(Math.max(0, Math.floor(Number(swipe.trailing) || 0)));
      el.dataset['sbLeading'] = String(Math.max(0, Math.floor(Number(swipe.leading) || 0)));
      break;
    }
    case 'touches':
    case 'contextmenu':
      break;
    case 'draggable':
      break;
    case 'dropdestination': {
      const accepts = (props as DropDestinationProps | undefined)?.accepts;
      el.dataset['sbAccepts'] = Array.isArray(accepts) ? accepts.filter((a) => typeof a === 'string').join(' ') : '';
      break;
    }
    case 'listrow': {
      const row = (props as ListRowProps | undefined) ?? {};
      el.toggleAttribute('data-sb-selectable', row.selectable === true);
      el.toggleAttribute('data-sb-selected', row.selected === true);
      el.toggleAttribute('data-sb-deletable', row.deletable === true);
      el.toggleAttribute('data-sb-movable', row.movable === true);
      if (row.selectable) el.setAttribute('aria-selected', String(row.selected === true));
      else el.removeAttribute('aria-selected');
      const control = el.querySelector<HTMLElement>(':scope > .sb-listrow-control');
      control?.setAttribute('aria-label', row.selectable ? (row.selected ? 'Deselect' : 'Select') : 'Delete');
      break;
    }
    // Phase 8: alerts and controls.
    case 'alert':
      applyAlert(el, (props as AlertProps) ?? {});
      break;
    case 'stepper':
      applyStepper(el, (props as StepperProps) ?? {});
      break;
    case 'slider':
      applySlider(el, (props as SliderProps) ?? {});
      break;
    case 'datepicker':
      applyDatePicker(el, (props as DatePickerProps) ?? {});
      break;
    case 'texteditor':
      if (isRichEditor(el)) applyRichEditor(el, (props as TextEditorProps) ?? {});
      else applyTextEditor(el as HTMLTextAreaElement, (props as TextEditorProps) ?? {});
      break;
    case 'sectionheader':
    case 'sectionfooter':
      // Pure containers: the list layout positions them.
      break;
    case 'video':
      applyVideo(el, props as VideoProps);
      break;
    case 'canvas':
      applyCanvas(el, props as CanvasProps, assets);
      break;
    case 'lazyspacer':
      el.dataset['axis'] = (props as LazySpacerProps).axis === 'horizontal' ? 'horizontal' : 'vertical';
      break;
    case 'registered':
      // The renderer builds its view (`adoptRegistered`).
      break;
    default: {
      const unknown: never = kind;
      throw new RendererError(`Unknown element kind ${String(unknown)}`);
    }
  }
}

function applyText(el: HTMLElement, props: TextProps, assets: AssetCatalog): void {
  if (!Array.isArray(props.runs)) {
    runTemplates.delete(el);
    delete el.dataset['sbRich'];
    clearRichText(el);
    el.textContent = props.text ?? '';
    return;
  }
  // Rich text: a template per run; the layout pass draws the lines (src/richText.ts).
  el.dataset['sbRich'] = '';
  runTemplates.set(el, props.runs.map((run) => runTemplate(run, assets)));
}

/** A rich text run's span (or inline image element) with its style; the layout pass sets the font. */
function runTemplate(run: TextRun, assets: AssetCatalog): HTMLElement {
  let el: HTMLElement;
  if (run.image) {
    el = createShell('image');
    el.className = 'sb-image sb-text-image';
    applyImage(el, run.image, assets);
    // A symbol is a glyph: centered on the line like text, not stood on the baseline.
    if (!isBitmapImage(run.image) && !isUrlImage(run.image)) el.classList.add('sb-text-symbol');
  } else {
    el = document.createElement('span');
    el.className = 'sb-text-run';
  }
  const s = el.style;
  if (run.foreground !== undefined) s.color = styleToCSS(run.foreground as ShapeStyle, 'text').solid;
  if (run.background !== undefined) s.backgroundColor = styleToCSS(run.background as ShapeStyle, 'paint').solid ?? '';
  const lines: string[] = [];
  let lineColor: unknown;
  if (run.underline) {
    lines.push('underline');
    lineColor = run.underline.color;
  }
  if (run.strikethrough) {
    lines.push('line-through');
    lineColor ??= run.strikethrough.color;
  }
  if (lines.length > 0) {
    s.textDecorationLine = lines.join(' ');
    if (lineColor !== undefined) s.textDecorationColor = styleToCSS(lineColor as ShapeStyle, 'text').solid;
  }
  if (typeof run.kerning === 'number' && run.kerning !== 0) s.letterSpacing = `${run.kerning}px`;
  if (typeof run.baselineOffset === 'number' && run.baselineOffset !== 0) {
    s.position = 'relative';
    s.top = `${-run.baselineOffset}px`;
  }
  if (typeof run.link === 'string') {
    el.classList.add('sb-link');
    el.dataset['sbLink'] = run.link;
    el.setAttribute('role', 'link');
  }
  return el;
}

/**
 * Phase 8 `.refreshable`: a `list` / `scrollview` gets a 40px spinner row
 * (`.sb-refresh`, positioned by the stylesheet above the content) that shows
 * while `refreshing`; the controller sends `refresh` after a pull past 60.
 */
function applyRefreshable(el: HTMLElement, refreshable: boolean, refreshing: boolean): void {
  el.toggleAttribute('data-sb-refreshable', refreshable);
  el.toggleAttribute('data-sb-refreshing', refreshable && refreshing);
  let indicator = el.querySelector<HTMLElement>(':scope > .sb-refresh');
  if (!refreshable) {
    indicator?.remove();
    return;
  }
  if (!indicator) {
    indicator = div('sb-refresh');
    indicator.setAttribute('aria-hidden', 'true');
    indicator.append(div('sb-refresh-spinner'));
    el.prepend(indicator);
  }
}

/** Phase 6: the gauge's value (0...1) and style; the bar geometry comes from the engine. */
function applyGauge(el: HTMLElement, props: GaugeProps): void {
  const value = Number.isFinite(props.value) ? Math.min(1, Math.max(0, props.value)) : 0;
  el.dataset['sbStyle'] = props.style ?? 'automatic';
  el.dataset['sbValue'] = String(value);
  el.setAttribute('aria-valuenow', String(value));
  const ring = el.querySelector<HTMLElement>(':scope > .sb-gauge-ring');
  if (ring && isCircularGauge(props.style)) updateGaugeRing(ring, props.style, value);
}

function applyColor(el: HTMLElement, props: ColorProps): void {
  el.style.backgroundColor = props.color ? colorToCSS(props.color) : 'transparent';
}

/** Radius that turns a box into each shape (the layout engine sizes it). */
const SHAPE_RADIUS: Record<Exclude<ShapeProps['shape'], 'path'>, (cornerRadius: number) => string> = {
  rectangle: () => '0px',
  roundedRectangle: (r) => `${Math.max(0, r)}px`,
  circle: () => '50%',
  capsule: () => '9999px',
  ellipse: () => '50%',
  // Phase 6: `ContainerRelativeShape` draws like a rounded rectangle.
  containerRelative: (r) => `${Math.max(0, r)}px`,
};

/**
 * `fill: null` with no stroke paints the current foreground color (a bare
 * `Circle()`); with a stroke it is `Shape.stroke(_:)`, which only outlines.
 * Phase 6: `fill` and `stroke.color` are ShapeStyles (gradients, materials,
 * hierarchical fills). A gradient stroke uses its first stop (CSS borders are
 * flat). The stroke is a CSS border, which lies inside the box: that is
 * `.strokeBorder` (`inset: true`); a plain `.stroke` is drawn the same way,
 * and `data-sb-stroke` records which one it was.
 *
 * A shape that is only stroked is marked `data-sb-stroke-only`: it takes no
 * hits (styles.css), so an outline overlaid on a control leaves its box to
 * what is under it, as SwiftUI hit-tests a stroke only along its line.
 */
function applyShape(el: HTMLElement, props: ShapeProps): void {
  if (props.stroke && !props.fill) el.dataset['sbStrokeOnly'] = '';
  else delete el.dataset['sbStrokeOnly'];
  if (props.shape === 'path') {
    // A custom shape: an SVG of its path (src/pathShape.ts).
    el.dataset['sbShape'] = 'path';
    applyPathShape(el, props);
    return;
  }
  const shape = props.shape in SHAPE_RADIUS ? (props.shape as keyof typeof SHAPE_RADIUS) : 'rectangle';
  const s = el.style;
  el.dataset['sbShape'] = shape;
  s.borderRadius = SHAPE_RADIUS[shape](Number(props.cornerRadius) || 0);
  const stroke = props.stroke;
  if (props.fill) applyPaint(el, styleToCSS(props.fill, 'paint'));
  else if (!stroke) s.background = 'var(--sb-foreground-paint, currentColor)'; // a gradient foreground paints the shape too
  else s.backgroundColor = 'transparent';
  if (stroke) {
    const width = Number.isFinite(stroke.lineWidth) ? Math.max(0, stroke.lineWidth) : 1;
    const color = stroke.color ? styleToCSS(stroke.color, 'paint').solid : 'currentColor';
    // A dash pattern is drawn as CSS dashes (their lengths are the browser's).
    s.border = `${width}px ${stroke.dash?.length ? 'dashed' : 'solid'} ${color}`;
    el.dataset['sbStroke'] = stroke.inset ? 'inset' : 'center';
  }
  // A fill with a drop shadow (`.shadow(.drop(…))` on the style), then the shadows of the boxes around the shape
  // (castShadows), which styles.css gives a shape without one of its own.
  const shadow = (props.fill as { shadow?: ShapeShadow } | null)?.shadow;
  s.filter = shadow && typeof shadow === 'object' ? `${shapeShadowCSS(shadow)} var(--sb-shadow-filter, )` : '';
  const mask = shapeTrimMask(props.trim);
  if (mask) {
    s.setProperty('mask-image', mask);
    s.setProperty('-webkit-mask-image', mask);
  } else if (el.dataset['sbTrim']) {
    s.removeProperty('mask-image');
    s.removeProperty('-webkit-mask-image');
  }
  if (mask) el.dataset['sbTrim'] = `${props.trim!.from},${props.trim!.to}`;
  else delete el.dataset['sbTrim'];
}

/** A style's drop shadow (`ShadowStyle.drop`). */
interface ShapeShadow {
  color?: Color;
  radius?: number;
  x?: number;
  y?: number;
}

function shapeShadowCSS(shadow: ShapeShadow): string {
  const n = (v: number | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const color = shadow.color ? colorToCSS(shadow.color) : 'rgba(0, 0, 0, 0.33)';
  return `drop-shadow(${n(shadow.x)}px ${n(shadow.y)}px ${Math.max(0, n(shadow.radius))}px ${color})`;
}

/**
 * `.trim(from:to:)`: a conic gradient opaque between the fractions, starting
 * at 3 o'clock (CSS's 90deg) and turning clockwise like SwiftUI's circle path.
 * Undefined for an untrimmed (or fully kept) shape.
 */
export function shapeTrimMask(trim: ShapeProps['trim']): string | undefined {
  if (!trim) return undefined;
  const from = Math.min(1, Math.max(0, Number(trim.from) || 0));
  const to = Math.min(1, Math.max(0, Number(trim.to) || 0));
  if (from <= 0 && to >= 1) return undefined;
  const a = Math.round(from * 36000) / 100;
  const b = Math.round(Math.max(from, to) * 36000) / 100;
  return `conic-gradient(from 90deg, transparent ${a}deg, #000 ${a}deg, #000 ${b}deg, transparent ${b}deg)`;
}

/** Paint an area with a resolved ShapeStyle: a color, a gradient image or a material over a blurred backdrop. */
function applyPaint(el: HTMLElement, paint: PaintCSS): void {
  const s = el.style;
  if (paint.kind === 'gradient') {
    s.backgroundColor = 'transparent';
    s.backgroundImage = paint.value;
  } else {
    s.backgroundColor = paint.value;
  }
  if (paint.backdropFilter) {
    s.setProperty('backdrop-filter', paint.backdropFilter);
    s.setProperty('-webkit-backdrop-filter', paint.backdropFilter);
  }
  // Liquid Glass gets the rim (styles.css `[data-sb-glass]`).
  el.toggleAttribute('data-sb-glass', paint.glass === true);
}

// Stacks, spacers and zstacks are pure layout: the engine reads their props
// from the node tree; the attributes only make the DOM easy to inspect.
function applyVStack(el: HTMLElement, props: VStackProps): void {
  el.dataset['alignment'] = props.alignment;
  el.dataset['sbSpacing'] = props.spacing == null ? 'default' : String(props.spacing);
}

function applyHStack(el: HTMLElement, props: HStackProps): void {
  el.dataset['alignment'] = props.alignment;
  el.dataset['sbSpacing'] = props.spacing == null ? 'default' : String(props.spacing);
}

function applySpacer(el: HTMLElement, props: SpacerProps): void {
  el.dataset['sbMinLength'] = props.minLength == null ? 'default' : String(props.minLength);
}

function applyButton(el: HTMLElement, props: ButtonProps): void {
  el.dataset['sbStyle'] = props.style ?? 'automatic';
  if (props.role) el.dataset['sbRole'] = props.role;
}

function applyZStack(el: HTMLElement, props: ZStackProps): void {
  el.dataset['alignment'] = props.alignment;
}

/**
 * An `image` is an SF Symbol (`systemName`, drawn from the open icon set) or,
 * Phase 5, a catalog image (`name`): a bitmap in an `<img>` that fills the
 * frame the layout engine computed, or a `.symbolset` SVG used as a mask over
 * the current color so it tints like a symbol. Names the catalog does not
 * know get the same dashed placeholder as unmapped symbols, with the name as
 * the tooltip, so the gap is easy to spot and fix.
 */
function applyImage(el: HTMLElement, props: ImageProps, assets: AssetCatalog): void {
  const missing = (name: string): void => {
    el.replaceChildren();
    el.title = name;
    el.setAttribute('aria-label', name);
    el.dataset['sbMissing'] = '';
  };
  const found = (name: string, child: Element): void => {
    el.replaceChildren(child);
    el.removeAttribute('title');
    el.setAttribute('aria-label', name);
    delete el.dataset['sbMissing'];
  };
  // `.interpolation(.none)`: scaled without smoothing (styles.css).
  if ((props as { interpolation?: string }).interpolation === 'none') el.dataset['sbInterpolation'] = 'none';
  else delete el.dataset['sbInterpolation'];
  if (isBitmapImage(props)) {
    // A `UIImage` bitmap: a canvas the recipe is drawn on once the layout
    // engine has sized the box (src/bitmaps.ts).
    delete el.dataset['sbSymbol'];
    delete el.dataset['sbAsset'];
    delete el.dataset['sbUrl'];
    if (props.resizable) el.dataset['sbResizable'] = '';
    else delete el.dataset['sbResizable'];
    let canvas = el.firstElementChild;
    if (!(canvas instanceof HTMLCanvasElement)) {
      canvas = document.createElement('canvas');
      found('', canvas);
      el.removeAttribute('aria-label');
    }
    showBitmap(canvas as HTMLCanvasElement, props.bitmap);
    return;
  }
  if (isUrlImage(props)) {
    // Phase 13: the page decoded this URL for `AsyncImage` already, so the
    // <img> shows at once; the layout engine sized the box from the props.
    // A `file:` URL of the app's files loads at the object URL the files
    // host makes of it (hostRequests.ts `loadableURL`), once it has.
    delete el.dataset['sbSymbol'];
    delete el.dataset['sbAsset'];
    const changed = el.dataset['sbUrl'] !== props.url;
    el.dataset['sbUrl'] = props.url;
    if (props.resizable) el.dataset['sbResizable'] = '';
    else delete el.dataset['sbResizable'];
    // React's opt-in to the browser's own image interactions (styles.css).
    const web = props.webInteractions === true;
    el.toggleAttribute('data-sb-web', web);
    const url = props.url;
    const existing = el.firstElementChild;
    if (existing instanceof HTMLImageElement) {
      existing.draggable = web;
      if (changed) {
        const loadable = loadableURL(url);
        if (typeof loadable === 'string') swapImageSource(existing, loadable);
        else void loadable.then((src) => el.dataset['sbUrl'] === url && swapImageSource(existing, src));
      }
      return;
    }
    const img = document.createElement('img');
    const loadable = loadableURL(url);
    if (typeof loadable === 'string') img.src = loadable;
    else void loadable.then((src) => el.dataset['sbUrl'] === url && img.setAttribute('src', src));
    img.alt = '';
    img.draggable = web;
    img.decoding = 'async';
    found(url, img);
    return;
  }
  delete el.dataset['sbUrl'];
  el.removeAttribute('data-sb-web');
  if (isAssetImage(props)) {
    const name = props.name ?? '';
    delete el.dataset['sbSymbol'];
    el.dataset['sbAsset'] = name;
    if (props.resizable) el.dataset['sbResizable'] = '';
    else delete el.dataset['sbResizable'];
    const asset = assets.images[name];
    if (!asset) {
      missing(name);
      return;
    }
    if (asset.kind === 'symbol') {
      const mask = document.createElement('span');
      mask.className = 'sb-image-mask';
      const url = `url("${asset.src}")`;
      mask.style.setProperty('mask-image', url);
      mask.style.setProperty('-webkit-mask-image', url);
      found(name, mask);
      return;
    }
    // Reuse the <img>: replacing it on every update left the box empty
    // until the new bitmap decoded, which made the brand header's truck
    // frames flicker at 20 fps. A changed source is decoded off-screen
    // first and swapped in only once it is ready.
    const existing = el.firstElementChild;
    if (existing instanceof HTMLImageElement) {
      el.removeAttribute('title');
      el.setAttribute('aria-label', name);
      delete el.dataset['sbMissing'];
      // `src` the property is absolute; compare the attribute as written.
      if (existing.getAttribute('src') !== asset.src && existing.dataset['sbPending'] !== asset.src) {
        swapImageSource(existing, asset.src);
      }
      return;
    }
    const img = document.createElement('img');
    img.src = asset.src;
    img.alt = '';
    img.draggable = false;
    img.decoding = 'async';
    found(name, img);
    return;
  }
  const name = props.systemName ?? '';
  delete el.dataset['sbAsset'];
  delete el.dataset['sbResizable'];
  el.dataset['sbSymbol'] = name;
  const svg = renderSymbol(name);
  if (svg && props.renderingMode === 'palette' && svg.dataset['sbGlyphFill'] === 'knockout') {
    // The enclosure in the secondary level, the glyph in the primary.
    svg.style.color = 'var(--sb-hierarchy-2, currentColor)';
    svg.style.setProperty('--sb-icon-knockout', 'var(--sb-hierarchy-1, currentColor)');
  }
  if (svg) found(name, svg);
  else missing(name);
}

/**
 * Points `img` at `src` once the bitmap is decoded, so the previous frame
 * stays visible meanwhile; a newer request supersedes a pending one.
 */
function swapImageSource(img: HTMLImageElement, src: string): void {
  img.dataset['sbPending'] = src;
  const probe = new Image();
  probe.src = src;
  const commit = (): void => {
    if (img.dataset['sbPending'] !== src) return;
    delete img.dataset['sbPending'];
    img.setAttribute('src', src);
  };
  if (typeof probe.decode === 'function') probe.decode().then(commit, commit);
  else probe.complete ? commit() : (probe.onload = probe.onerror = commit);
}

function applySection(el: HTMLElement, props: SectionProps): void {
  const header = el.querySelector<HTMLElement>(':scope > .sb-section-header');
  const footer = el.querySelector<HTMLElement>(':scope > .sb-section-footer');
  if (header) {
    header.textContent = props.header ?? '';
    header.hidden = !props.header;
  }
  // `Section(isExpanded:)`: a chevron on the header (a string one, or the
  // `sectionheader` view) in a sidebar-style list (styles.css); its tap toggles.
  el.toggleAttribute('data-sb-collapsible', props.collapsible === true);
  el.toggleAttribute('data-sb-expanded', props.collapsible === true && props.expanded !== false);
  if (footer) {
    footer.textContent = props.footer ?? '';
    footer.hidden = !props.footer;
  }
}

function applyTextField(input: HTMLInputElement | HTMLTextAreaElement, props: TextFieldProps): void {
  // A vertical field is a `<textarea>`, which has no type.
  if (input instanceof HTMLInputElement) input.type = props.secure ? 'password' : 'text';
  else input.dataset['sbAxis'] = 'vertical';
  const placeholder = props.placeholder ?? '';
  if (input.placeholder !== placeholder) input.placeholder = placeholder;
  input.dataset['sbStyle'] = props.style ?? 'plain';
  const text = props.text ?? '';
  // Leave the caret alone when Swift echoes the value the field already shows.
  if (input.value !== text) input.value = text;
  // Phase 12: a bound field reports focus changes and follows the bound value;
  // the move happens once the element is in the document (see `syncFocus`).
  if (typeof props.focused === 'boolean') {
    input.dataset['sbFocus'] = String(props.focused);
  } else {
    delete input.dataset['sbFocus'];
  }
  if (props.submit === true) input.dataset['sbSubmit'] = 'true';
  else delete input.dataset['sbSubmit'];
  markDeleteBackward(input, props.deleteBackward === true);
  if (typeof props.submitLabel === 'string' && props.submitLabel.length > 0) input.enterKeyHint = props.submitLabel;
  else input.removeAttribute('enterkeyhint');
  applyTextInputHints(input, props);
}

/** `UIKeyboardType` → `inputmode`: the browser's nearest on-screen keyboard. */
const INPUT_MODES: Record<string, string> = {
  emailAddress: 'email',
  URL: 'url',
  numberPad: 'numeric',
  asciiCapableNumberPad: 'numeric',
  decimalPad: 'decimal',
  phonePad: 'tel',
  namePhonePad: 'tel',
  webSearch: 'search',
};

/** `UITextContentType` → `autocomplete`: what autofill offers. */
const AUTOCOMPLETE: Record<string, string> = {
  name: 'name',
  givenName: 'given-name',
  familyName: 'family-name',
  emailAddress: 'email',
  telephoneNumber: 'tel',
  username: 'username',
  password: 'current-password',
  newPassword: 'new-password',
  oneTimeCode: 'one-time-code',
  URL: 'url',
  fullStreetAddress: 'street-address',
  postalCode: 'postal-code',
  creditCardNumber: 'cc-number',
};

/**
 * `.keyboardType`, `.textContentType`, `.textInputAutocapitalization` and
 * `.autocorrectionDisabled` as the input's attributes (docs/ops-protocol.md,
 * "Text input hints"). Without a content type autofill stays off, as a field
 * in an app does not offer the page's saved values.
 */
function applyTextInputHints(input: HTMLInputElement | HTMLTextAreaElement, props: TextFieldProps): void {
  const mode = props.keyboard ? INPUT_MODES[props.keyboard] : undefined;
  if (mode) input.inputMode = mode;
  else input.removeAttribute('inputmode');
  input.autocomplete = ((props.contentType ? AUTOCOMPLETE[props.contentType] : undefined) ?? 'off') as AutoFill;
  const capitalize = props.autocapitalization === 'never' ? 'none' : props.autocapitalization;
  if (capitalize) input.setAttribute('autocapitalize', capitalize);
  else input.removeAttribute('autocapitalize');
  if (props.autocorrectionDisabled === true) input.setAttribute('autocorrect', 'off');
  else input.removeAttribute('autocorrect');
}

/** Phase 12 `.scrollTargetLayout()`: the stack's children snap (styles.css). */
function applyScrollTarget(el: HTMLElement, target: boolean): void {
  if (target) el.dataset['sbScrollTarget'] = 'true';
  else delete el.dataset['sbScrollTarget'];
}

/** Like `applyTextField`: the caret survives an echo of the value the field shows. */
function applySearchField(el: HTMLElement, props: SearchFieldProps): void {
  const input = el.querySelector<HTMLInputElement>(':scope > .sb-searchfield-input');
  if (!input) return;
  const placeholder = typeof props.placeholder === 'string' && props.placeholder.length > 0 ? props.placeholder : 'Search';
  input.placeholder = placeholder;
  input.setAttribute('aria-label', placeholder);
  const text = props.text ?? '';
  if (input.value !== text) input.value = text;
}

function setSwitchState(toggle: HTMLElement, isOn: boolean): void {
  toggle.dataset['sbOn'] = String(isOn);
  if (toggle.dataset['sbToggleStyle'] === 'button') toggle.setAttribute('aria-pressed', String(isOn));
  const sw = toggle.querySelector<HTMLElement>(':scope > .sb-switch');
  sw?.setAttribute('aria-checked', String(isOn));
}

function applyNavScreen(el: HTMLElement, props: NavScreenProps): void {
  const title = props.title ?? '';
  const depth = Number.isFinite(props.depth) ? props.depth : 0;
  const mode = props.displayMode ?? 'automatic';
  const large = mode === 'large' || (mode === 'automatic' && depth === 0);
  el.dataset['sbTitle'] = title;
  el.dataset['sbDepth'] = String(depth);
  el.dataset['sbDisplay'] = large ? 'large' : 'inline';
  const inlineTitle = el.querySelector<HTMLElement>(':scope > .sb-navbar > .sb-navbar-title');
  const largeTitle = largeTitleOf(el);
  // A large title that scrolls away collapses into the inline one (shown
  // with `data-sb-collapsed`, src/largeTitle.ts).
  const subtitleEl = el.querySelector<HTMLElement>(':scope > .sb-navbar > .sb-navbar-subtitle');
  if (subtitleEl) {
    const subtitle = props.subtitle ?? '';
    if (subtitleEl.textContent !== subtitle) subtitleEl.textContent = subtitle;
  }
  if (inlineTitle) {
    const appearing = inlineTitle.textContent === '' && title !== '' && inlineTitle.isConnected;
    inlineTitle.textContent = title;
    // An inline title given to an empty bar (a header row that has scrolled
    // under it handing the bar its name) fades in as a collapsing large title does.
    if (appearing && !large && typeof inlineTitle.animate === 'function') {
      inlineTitle.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'ease' });
    }
  }
  if (largeTitle) {
    largeTitleTextOf(largeTitle).textContent = large ? title : '';
    largeTitle.hidden = !large;
  }
  el.setAttribute('aria-label', title);
}

/**
 * Segmented: one `.sb-segment` radio button per option, the selected one
 * carrying the pill. Menu: the label leading, the selected option plus a
 * `chevron.up.chevron.down` glyph trailing; the element itself is the button
 * that opens the menu. Inline (Phase 6): a dim label row when the label is
 * non-empty, then one `.sb-picker-option` radio row per option with a
 * trailing checkmark on the selected one.
 */
function applyPicker(el: HTMLElement, props: PickerProps): void {
  const options = Array.isArray(props.options) ? props.options.map(String) : [];
  const selected = clampIndex(props.selected, options.length);
  const style = props.style === 'segmented' ? 'segmented' : props.style === 'inline' ? 'inline' : 'menu';
  const label = props.label ?? '';
  el.dataset['sbStyle'] = style;
  el.dataset['sbSelected'] = String(selected);
  el.dataset['sbLabel'] = label;
  el.replaceChildren();
  if (style === 'inline') {
    el.setAttribute('role', 'radiogroup');
    el.setAttribute('aria-label', label);
    el.removeAttribute('tabindex');
    el.removeAttribute('aria-haspopup');
    el.removeAttribute('aria-expanded');
    if (label) {
      const labelRow = div('sb-picker-inline-label');
      labelRow.textContent = label;
      el.append(labelRow);
    }
    options.forEach((option, index) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'sb-picker-option';
      row.setAttribute('role', 'radio');
      row.setAttribute('aria-checked', String(index === selected));
      row.dataset['sbIndex'] = String(index);
      const title = span('sb-picker-option-title');
      title.textContent = option;
      const check = span('sb-picker-option-check');
      const glyph = renderSymbol('checkmark');
      if (glyph) check.append(glyph);
      row.append(title, check);
      el.append(row);
    });
    return;
  }
  if (style === 'segmented') {
    el.setAttribute('role', 'radiogroup');
    el.setAttribute('aria-label', label);
    el.removeAttribute('tabindex');
    el.removeAttribute('aria-haspopup');
    el.removeAttribute('aria-expanded');
    // The gray track sits at the control's frame (which, in a list row, is
    // narrower than the row box the element occupies); the label shows only
    // where the engine places it (the Mac idiom).
    el.append(div('sb-segment-track'));
    const segmentedLabel = span('sb-picker-label');
    segmentedLabel.textContent = label;
    segmentedLabel.hidden = true;
    el.append(segmentedLabel);
    options.forEach((option, index) => {
      const segment = document.createElement('button');
      segment.type = 'button';
      segment.className = 'sb-segment';
      segment.setAttribute('role', 'radio');
      segment.setAttribute('aria-checked', String(index === selected));
      segment.dataset['sbIndex'] = String(index);
      const text = span('sb-segment-title');
      text.textContent = option;
      segment.append(text);
      el.append(segment);
    });
    return;
  }
  el.setAttribute('role', 'button');
  el.setAttribute('aria-haspopup', 'menu');
  el.setAttribute('aria-expanded', 'false');
  el.tabIndex = 0;
  const labelEl = span('sb-picker-label');
  labelEl.textContent = label;
  const value = span('sb-picker-value');
  const valueText = span('sb-picker-value-text');
  valueText.textContent = options[selected] ?? '';
  value.append(valueText);
  const chevron = renderSymbol('chevron.up.chevron.down');
  if (chevron) value.append(chevron);
  el.append(labelEl, value);
  el.setAttribute('aria-label', `${label}, ${options[selected] ?? ''}`);
}

/** Move a segmented picker's pill or an inline picker's checkmark (optimistically, before Swift confirms). */
function setPickerSelection(picker: HTMLElement, index: number): void {
  picker.dataset['sbSelected'] = String(index);
  picker.querySelectorAll<HTMLElement>(':scope > .sb-segment, :scope > .sb-picker-option').forEach((segment, i) => {
    segment.setAttribute('aria-checked', String(i === index));
  });
}

const FONT_WEIGHT: Record<FontWeight, string> = {
  ultraLight: '100',
  thin: '200',
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  heavy: '800',
  black: '900',
};

const FONT_FAMILY_MONO =
  'ui-monospace, "SF Mono", Menlo, Monaco, "Cascadia Mono", "Roboto Mono", Consolas, monospace';
const FONT_FAMILY_SERIF = 'ui-serif, "New York", Georgia, "Times New Roman", serif';
const FONT_FAMILY_ROUNDED =
  'ui-rounded, "SF Pro Rounded", -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif';

/**
 * Visual styling of a `styled` box. Layout fields (`padding`, `frame`,
 * `layoutPriority`, `fixedSize`, `lineLimit`, `multilineTextAlignment`) are
 * read by the layout engine from the node tree; they only leave data
 * attributes here. Font and color settings are set as inline CSS so children
 * inherit them (the engine resolves text fonts itself).
 */
export function applyStyle(el: HTMLElement, style: Style): void {
  const s = el.style;

  if (style.padding) {
    const { top = 0, leading = 0, bottom = 0, trailing = 0 } = style.padding;
    el.dataset['sbPadding'] = `${top} ${trailing} ${bottom} ${leading}`;
  }

  if (style.font) {
    const { size, weight, design, textStyle, relativeTo, monospacedDigit } = style.font;
    if (size != null) {
      // A fixed size; `relativeTo` scales it like that text style (the
      // scale factor is a CSS variable the page sets per Dynamic Type size).
      s.fontSize = relativeTo ? `calc(${size}px * var(--sb-ts-${relativeTo}-scale, 1))` : `${size}px`;
      // Do not inherit a text style's px line height from an ancestor.
      s.lineHeight = '1.3';
      if (relativeTo) el.dataset['sbRelativeTo'] = relativeTo;
    } else if (textStyle) {
      // Text styles follow Dynamic Type through CSS variables (see
      // typography.ts); the fallbacks are the Large sizes.
      const large = resolveTextStyle(textStyle, DEFAULT_TYPE_SIZE);
      s.fontSize = `var(${textStyleVar(textStyle, 'size')}, ${large.fontSize}px)`;
      s.lineHeight = `var(${textStyleVar(textStyle, 'lh')}, ${Math.round(large.lineHeight * 100) / 100}px)`;
      el.dataset['sbTextStyle'] = textStyle;
      const implied = TEXT_STYLE_WEIGHT[textStyle];
      if (!weight && implied) s.fontWeight = FONT_WEIGHT[implied];
    }
    if (weight) s.fontWeight = FONT_WEIGHT[weight] ?? '400';
    if (design === 'monospaced') s.fontFamily = FONT_FAMILY_MONO;
    else if (design === 'rounded') s.fontFamily = FONT_FAMILY_ROUNDED;
    else if (design === 'serif') s.fontFamily = FONT_FAMILY_SERIF;
    else if (design === 'default') s.fontFamily = 'var(--sb-font-family)';
    // Inherited by the DOM inside; the engine's text fonts carry it too (setFont).
    if (monospacedDigit !== undefined) s.fontVariantNumeric = monospacedDigit ? 'tabular-nums' : 'normal';
  }

  if (style.foreground) {
    const paint = styleToCSS(style.foreground, 'text');
    // Buttons default to the accent color; a foreground set by an ancestor
    // styled box wins, as it does in SwiftUI.
    s.color = paint.solid;
    s.setProperty('--sb-button-color', paint.solid);
    // `.secondary` and the other hierarchical levels below are levels of this
    // foreground (styles.ts); a hierarchical foreground keeps the ones above.
    if (!('hierarchical' in style.foreground)) {
      for (const level of [1, 2, 3, 4] as const) {
        const opacity = HIERARCHY_OPACITY[level];
        s.setProperty(`--sb-hierarchy-${level}`, opacity === 1 ? paint.solid : `color-mix(in srgb, ${paint.solid} ${opacity * 100}%, transparent)`);
      }
    }
    // `.foregroundStyle(.white, .indigo)`: the levels after the first. A
    // hierarchical one (`.foregroundStyle(.red, .secondary)`) stays the level
    // of the primary set above (it would refer to itself).
    style.foregroundLevels?.forEach((level, index) => {
      if (level && typeof level === 'object' && 'hierarchical' in level) return;
      s.setProperty(`--sb-hierarchy-${index + 2}`, styleToCSS(level, 'text').solid);
    });
    if (paint.kind === 'gradient') {
      // Text paints the gradient through its own background clipped to the
      // glyphs (see `.sb-text` in styles.css); the custom properties inherit
      // down to every text in the subtree. `currentColor` consumers
      // (symbols, strokes) fall back to the first stop set above.
      s.setProperty('--sb-text-gradient', paint.value);
      s.setProperty('--sb-text-clip', 'text');
      s.setProperty('--sb-text-color', 'transparent');
      s.setProperty('--sb-foreground-paint', paint.value);
      el.dataset['sbGradientText'] = '';
    } else {
      // A flat foreground ends a gradient inherited from an ancestor.
      s.setProperty('--sb-text-gradient', 'initial');
      s.setProperty('--sb-text-clip', 'initial');
      s.setProperty('--sb-text-color', 'initial');
      s.setProperty('--sb-foreground-paint', 'initial');
    }
  }

  // View `.underline()` / `.strikethrough()` / `.italic()`: inherited by the
  // texts below (`.sb-text` reads the decoration properties; `initial` turns
  // an ancestor's off).
  if (style.underline != null) s.setProperty('--sb-text-underline', style.underline ? 'underline' : 'initial');
  if (style.strikethrough != null) {
    s.setProperty('--sb-text-strikethrough', style.strikethrough ? 'line-through' : 'initial');
  }
  if (style.italic != null) s.fontStyle = style.italic ? 'italic' : 'normal';

  if (style.background) {
    applyPaint(el, styleToCSS(style.background, 'paint'));
    // A painted background is a view of its own: hittable (styles.css).
    el.dataset['sbBackground'] = '';
  } else {
    delete el.dataset['sbBackground'];
  }

  if (style.frame) {
    const { width, height, minWidth, maxWidth, minHeight, maxHeight, alignment } = style.frame;
    const parts: string[] = [];
    if (width != null) parts.push(`w${width}`);
    if (height != null) parts.push(`h${height}`);
    if (minWidth != null) parts.push(`minw${minWidth}`);
    if (maxWidth != null) parts.push(`maxw${maxWidth}`);
    if (minHeight != null) parts.push(`minh${minHeight}`);
    if (maxHeight != null) parts.push(`maxh${maxHeight}`);
    el.dataset['sbFrame'] = parts.join(' ');
    el.dataset['sbFrameAlignment'] = alignment ?? 'center';
  }

  if (style.cornerRadius != null) {
    s.borderRadius = `${style.cornerRadius}px`;
    s.overflow = 'hidden';
  } else if (style.backgroundCornerRadius != null) {
    // The background in its container's shape, without clipping the content.
    s.borderRadius = `${style.backgroundCornerRadius}px`;
  }

  // Phase 6 visual effects: CSS only, the layout engine never sees them.
  // A shadow is not the box's own filter: as SwiftUI's `.shadow` on a container, it falls on each view the box draws
  // (castShadows). A box that clips, masks or composites what it holds draws it as one, so the shadows around it are
  // cast by it too (`--sb-shadow-filter`, which castShadows sets).
  const effects = effectsCSS(style, { shadow: false });
  if (effects.transform) s.transform = effects.transform;
  if (effects.transformOrigin) s.transformOrigin = effects.transformOrigin;
  const multiply = style.colorMultiply ? colorMultiplyFilter(style.colorMultiply, el) : '';
  const asOne = style.compositingGroup === true || style.cornerRadius != null || style.clipped === true || (style.mask != null && typeof style.mask === 'object');
  const filter = [multiply, effects.filter ?? '', asOne ? 'var(--sb-shadow-filter, )' : ''].filter(Boolean).join(' ');
  if (filter) s.filter = filter;
  if (asOne) el.dataset['sbAsOne'] = '';
  else delete el.dataset['sbAsOne'];
  if (style.shadow) {
    el.dataset['sbShadow'] = `drop-shadow(${shadowCSS(style.shadow)})`;
    el.dataset['sbShadowBox'] = shadowCSS(style.shadow);
  } else if (el.dataset['sbShadow'] !== undefined) {
    delete el.dataset['sbShadow'];
    delete el.dataset['sbShadowBox'];
    s.removeProperty('--sb-shadow-filter');
    s.removeProperty('--sb-shadow-box');
  }
  if (style.compositingGroup) el.dataset['sbComposite'] = '';
  else delete el.dataset['sbComposite'];
  // Phase 7: `.hidden()` keeps the box (the engine lays it out) but draws
  // nothing and takes no input; `.mask { }` clips the box to a style's alpha.
  if (effects.hidden) {
    s.visibility = 'hidden';
    s.pointerEvents = 'none';
    el.dataset['sbHidden'] = '';
  }
  if (effects.maskImage) {
    for (const prefix of ['', '-webkit-']) {
      s.setProperty(`${prefix}mask-image`, effects.maskImage);
      s.setProperty(`${prefix}mask-size`, '100% 100%');
      s.setProperty(`${prefix}mask-repeat`, 'no-repeat');
    }
  }
  if (style.clipped) {
    s.overflow = 'hidden';
    el.dataset['sbClipped'] = '';
  }
  // Phase 8: `.allowsHitTesting(false)` lets input through the subtree.
  if (style.hitTesting === false) {
    s.pointerEvents = 'none';
    el.dataset['sbHitTesting'] = 'false';
  }
  // `.contentShape(Rectangle())`: the box takes input across its area, which a box already does; marked for the stylesheet and tests.
  if (style.contentShape) el.dataset['sbContentShape'] = style.contentShape;
  else delete el.dataset['sbContentShape'];
  // Hover, tooltips, cursors, symbol effects and matched geometry ids (pointerEffects.ts).
  applyPointerAndEffects(el, style);

  let opacity = style.opacity;
  if (style.disabled) {
    // `.disabled(true)`: dim the subtree and block input.
    // The undimmed opacity stays available to styles.css, which draws a
    // disabled bordered button gray instead of faded.
    s.setProperty('--sb-undimmed-opacity', String(opacity ?? 1));
    opacity = (opacity ?? 1) * 0.4;
    s.pointerEvents = 'none';
    el.dataset['sbDisabled'] = '';
    el.setAttribute('aria-disabled', 'true');
  } else {
    s.removeProperty('--sb-undimmed-opacity');
    el.removeAttribute('aria-disabled');
  }
  if (opacity != null) {
    s.opacity = String(opacity);
  }
  // `.blendMode`: blends the box with what is behind it.
  if (style.blendMode) s.mixBlendMode = style.blendMode;
  else if (s.mixBlendMode) s.mixBlendMode = '';
  // `.zIndex`: in front of siblings with a lower index (boxes are positioned).
  // CSS takes integers; ×1000 keeps fractional indices (0.5) apart. The
  // stack it is in stacks its children on its own (styles.css,
  // `[data-sb-z]`), so a negative index orders the box among its siblings
  // only, as SwiftUI's does, and never sends it behind the views around.
  if (style.zIndex != null && Number.isFinite(style.zIndex)) {
    s.zIndex = String(Math.round(style.zIndex * 1000));
    el.dataset['sbZ'] = '';
  } else if (s.zIndex) {
    s.zIndex = '';
    delete el.dataset['sbZ'];
  }

  // Phase 3 text and layout hints (the engine reads them from the node tree).
  if (style.layoutPriority != null && Number.isFinite(style.layoutPriority)) {
    el.dataset['sbLayoutPriority'] = String(style.layoutPriority);
  }
  if (style.lineLimit != null && style.lineLimit > 0) {
    el.dataset['sbLineLimit'] = String(Math.floor(style.lineLimit));
  }
  if (style.multilineTextAlignment) el.dataset['sbTextAlign'] = style.multilineTextAlignment;
  if (style.fixedSize) {
    const { horizontal, vertical } = style.fixedSize;
    el.dataset['sbFixedSize'] = `${horizontal ? 'h' : ''}${vertical ? 'v' : ''}`;
  }
  // Phase 5: `.aspectRatio` is pure layout (the engine sizes the child); the
  // box reports the child's size either way, so `fill` has nothing to clip
  // here (SwiftUI needs `.clipped()` for that too).
  if (style.aspectRatio) {
    const { ratio, contentMode } = style.aspectRatio;
    el.dataset['sbAspectRatio'] = `${ratio ?? 'auto'} ${contentMode ?? 'fit'}`;
  }
  if (style.animationToken != null) el.dataset[ANIMATION_TOKEN] = String(style.animationToken);
  // A `Label`'s icon: tinted in list rows (styles.css).
  if (style.labelIcon) el.dataset['sbLabelIcon'] = '';
  // `.textSelection(.enabled)`: the text below can be selected (styles.css).
  el.toggleAttribute('data-sb-text-selection', style.textSelection === true);
  if (style.animation) ANIMATIONS.set(el, style.animation);
  if (style.transition) setTransition(el, style.transition);

  // Phase 6 (docs/phase6/layout-containers.md).
  if (style.tint) {
    // `.tint`: gauges, switches and prominent buttons below read `--sb-tint`;
    // a bordered button fills with the tint, faintly (`--sb-tint-fill`).
    const tint = colorToCSS(style.tint);
    s.setProperty('--sb-tint', tint);
    s.setProperty('--sb-tint-fill', `color-mix(in srgb, ${tint} var(--sb-tint-fill-amount), transparent)`);
  }
  if (style.position) {
    const { x, y } = style.position;
    el.dataset['sbPosition'] = `${x} ${y}`;
  }
  if (style.gridCellColumns != null && Number.isFinite(style.gridCellColumns)) {
    el.dataset['sbGridCellColumns'] = String(Math.max(1, Math.floor(style.gridCellColumns)));
  }
  if (style.gridCellAnchor) {
    const { x, y } = style.gridCellAnchor;
    el.dataset['sbGridCellAnchor'] = `${x} ${y}`;
  }
  // `.scrollTransition`: drawn over the effects above as the box scrolls (scrollTransition.ts).
  setScrollTransition(el, style);
}

// Colors and ShapeStyles resolve in src/styles.ts; re-exported for callers of the renderer.
export { colorToCSS, styleToCSS } from './styles';

/**
 * The `<style>` text declaring every catalog color as a custom property per
 * theme (the device sets `data-theme`, see styles.css). A colorset without a
 * dark variant keeps its light value in dark mode, as on iOS. Empty string
 * for a catalog without colors.
 */
export function assetColorsCSS(catalog: AssetCatalog): string {
  const names = Object.keys(catalog.colors).sort();
  if (names.length === 0) return '';
  const accent = catalog.colors[ACCENT_COLOR_NAME];
  const declarations = (scheme: 'light' | 'dark'): string =>
    names
      .map((name) => {
        const color = catalog.colors[name];
        if (!color) return '';
        const value = scheme === 'dark' ? (color.dark ?? color.light) : color.light;
        return `  ${assetColorVar(name)}: ${sanitizeColor(value)};\n`;
      })
      .join('') +
    // The app's accent color, as Xcode's global accent setting (`AccentColor`
    // in its templates) makes it on iOS: buttons, links, toggles, selection.
    (accent ? `  --sb-color-accent: var(${assetColorVar(ACCENT_COLOR_NAME)});\n` : '');
  return (
    `.device[data-theme="light"] {\n${declarations('light')}}\n` +
    `.device[data-theme="dark"] {\n${declarations('dark')}}\n`
  );
}

/** The catalog color that is the app's accent (Xcode's `ASSETCATALOG_COMPILER_GLOBAL_ACCENT_COLOR_NAME` default). */
const ACCENT_COLOR_NAME = 'AccentColor';

/** A manifest color value is injected into a stylesheet: keep only CSS color syntax characters. */
function sanitizeColor(value: string): string {
  return /^[A-Za-z0-9#(),.%\s/-]+$/.test(value) ? value : 'magenta';
}

// Re-exported so callers can build typed fixtures without importing protocol.ts.
export type { Op, ElementKind, ElementPropsByKind, RendererEvent };

/**
 * Whether a `lazyspacer` is within one screen of the visible area of the
 * scroll view (or list) it is in, along its axis; the page's viewport when it
 * is in none. Spacers in hidden tabs, menus or candidates are not.
 */
function lazySpacerIsNear(el: HTMLElement): boolean {
  if (el.closest('[data-sb-tab-hidden], [hidden]')) return false;
  const rect = el.getBoundingClientRect();
  const scroller = el.closest<HTMLElement>('.sb-scrollview, .sb-list');
  const view = scroller ? scroller.getBoundingClientRect() : new DOMRect(0, 0, window.innerWidth, window.innerHeight);
  if (view.width <= 0 || view.height <= 0) return false;
  if (el.dataset['axis'] === 'horizontal') {
    return rect.left < view.right + view.width && rect.right > view.left - view.width;
  }
  return rect.top < view.bottom + view.height && rect.bottom > view.top - view.height;
}

/**
 * Which way a `.numericText` change rolls: the direction it names, or else
 * down when the new text reads as a smaller number than the old one (as
 * SwiftUI's `numericText(value:)` decides from the value).
 */
export function countsDown(named: string | undefined, before: string, after: string): boolean {
  if (named !== undefined) return named === 'true';
  const number = (text: string): number => Number.parseFloat(text.replace(/[^\d.+-]/g, ''));
  const from = number(before);
  const to = number(after);
  return Number.isFinite(from) && Number.isFinite(to) && to < from;
}
